// SCHEDULE_MODEL_PHASE10_20260911
import {
  useState,
  useEffect } from 'react';
// TRADITIONAL_CORE_WEIGHTS_PATCH_20260813
// TRADITIONAL_GRADING_PATCH_20260813
import RichTextEditor from '../../../components/RichTextEditor';
import SubmissionFileSettingsEditor from './SubmissionFileSettingsEditor';
import PeerReviewManager from './PeerReviewManager';
import StructuredFormScheduleEditor from './StructuredFormScheduleEditor';
import StructuredFormBuilder from '../StructuredFormBuilder';
import { resolveAssignmentInstanceLabel } from '../../../utils/assignmentLabels';
import CourseDatePicker from './CourseDatePicker';
import {
  fetchAssignmentGroupLabelsForCourse,
  fetchCoursePlacementLabels,
  getAssignmentGroupDisplayName,
  getCourseGroupDisplayName,
  getStreamDisplayName,
  renameInstructorAssignmentGroup,
  fetchAssignmentGroupDescriptionsForCourse,
  saveInstructorAssignmentGroupDescription
} from '../../../utils/groupLabels';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function apiHeaders(extra = {}) {
  return {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY,
    ...extra
  };
}

async function readJson(
  response,
  label
) {
  const text =
    await response.text();

  if (!response.ok) {
    let message = '';

    if (text) {
      try {
        const parsed =
          JSON.parse(text);

        message =
          parsed?.message ||
          parsed?.error ||
          '';
      } catch {
        message =
          text.trim();
      }
    }

    throw new Error(
      message ||
      `${label} failed (${response.status}).`
    );
  }

  return text
    ? JSON.parse(text)
    : null;
}


async function fetchAssignmentCalendarHolidays(instructorId) {
  if (!instructorId) return [];

  const instructorResponse = await fetch(
    SUPABASE_URL +
      '/rest/v1/instructors?id=eq.' +
      encodeURIComponent(instructorId) +
      '&select=institution_id&limit=1',
    { headers: apiHeaders() }
  );

  const instructorRows = await readJson(
    instructorResponse,
    'Loading instructor institution'
  );

  const institutionId =
    instructorRows?.[0]?.institution_id;

  if (!institutionId) return [];

  const response = await fetch(
    SUPABASE_URL +
      '/rest/v1/holidays?institution_id=eq.' +
      encodeURIComponent(institutionId) +
      '&or=(is_personal.eq.false,instructor_id.eq.' +
      encodeURIComponent(instructorId) +
      ')&select=*&order=date.asc',
    { headers: apiHeaders() }
  );

  const rows = await readJson(
    response,
    'Loading assignment calendar holidays'
  );

  return Array.isArray(rows)
    ? rows
    : [];
}


function addDaysToScheduleDate(dateStr, days) {
  if (!dateStr) return null;

  const parts = String(dateStr).slice(0, 10).split('-').map(Number);

  if (parts.length !== 3 || !parts.every(Number.isFinite)) {
    return dateStr;
  }

  const date = new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
  date.setDate(date.getDate() + Number(days || 0));

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-');
}

function formatScheduleDate(dateStr) {
  if (!dateStr) return '—';

  const parts = String(dateStr).slice(0, 10).split('-').map(Number);
  if (parts.length !== 3 || !parts.every(Number.isFinite)) return dateStr;

  return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

function scheduleTimingDraftFromSettings(settings) {
  const openOffset = settings?.schedule_open_offset_days;
  const dueOffset = settings?.schedule_due_offset_days;

  return {
    openDays:
      openOffset === null || openOffset === undefined
        ? ''
        : String(Math.abs(Number(openOffset))),
    openDirection: Number(openOffset) < 0 ? 'before' : 'after',
    dueDays:
      dueOffset === null || dueOffset === undefined
        ? ''
        : String(Math.abs(Number(dueOffset))),
    dueDirection: Number(dueOffset) < 0 ? 'before' : 'after'
  };
}

function scheduleChoiceDraftFromSettings(settings) {
  return {
    openDate: settings?.schedule_choice_open_date || '',
    closeDate: settings?.schedule_choice_close_date || '',
    requiredCount: String(
      Math.max(
        1,
        Number(settings?.schedule_choice_required_count) || 1
      )
    )
  };
}

async function fetchWeeks(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&select=*&order=week_number.asc`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading weeks');
}

async function updateWeek(id, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: apiHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(`Updating week failed (${response.status}): ${detail}`);
  }
}

async function fetchAssignmentTypes(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_types?or=(is_course_copy.eq.false,course_id.eq.${courseId})&select=*&order=name.asc,course_copy_index.asc`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading assignment types');
}

async function fetchCourseAssignments(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignments?course_id=eq.${courseId}&select=*,assignment_types(id,name,requires_submission,instructions,allow_groups,is_core,assignment_kind,is_course_copy,source_assignment_type_id,course_copy_index)&order=instance_number.asc`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading course assignments');
}


async function fetchScheduledCourseReadings(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_week_reading_assignments?select=id,course_week_id,display_order,course_readings(id,title,author),course_weeks!inner(id,course_id,week_number,class_date)&course_weeks.course_id=eq.${courseId}`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(
    response,
    'Loading scheduled course readings'
  );

  return (Array.isArray(rows) ? rows : []).sort((a, b) => {
    const weekCompare =
      Number(a.course_weeks?.week_number || 0) -
      Number(b.course_weeks?.week_number || 0);

    if (weekCompare !== 0) return weekCompare;

    const orderCompare =
      Number(a.display_order || 0) -
      Number(b.display_order || 0);

    if (orderCompare !== 0) return orderCompare;

    return String(a.course_readings?.title || '').localeCompare(
      String(b.course_readings?.title || '')
    );
  });
}

async function fetchCourseEventsForAssignmentSchedule(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_events?select=id,course_week_id,title,event_date,event_time,location,course_weeks!inner(id,course_id,week_number,class_date)&course_weeks.course_id=eq.${courseId}`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(
    response,
    'Loading course events'
  );

  return (Array.isArray(rows) ? rows : []).sort((a, b) => {
    const weekCompare =
      Number(a.course_weeks?.week_number || 0) -
      Number(b.course_weeks?.week_number || 0);

    if (weekCompare !== 0) return weekCompare;

    return String(a.title || '').localeCompare(
      String(b.title || '')
    );
  });
}

async function fetchAssignmentScheduleItems(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_schedule_items?select=*,course_assignments!inner(course_id)&course_assignments.course_id=eq.${courseId}&order=display_order.asc,created_at.asc`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading assignment schedule items'
  );
}

async function createAssignmentScheduleItem(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_schedule_items`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }),
      body: JSON.stringify(data)
    }
  );

  return readJson(
    response,
    'Adding assignment schedule item'
  );
}

async function updateAssignmentScheduleItem(id, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_schedule_items?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }),
      body: JSON.stringify(data)
    }
  );

  return readJson(
    response,
    'Updating assignment schedule item'
  );
}

async function deleteAssignmentScheduleItem(id) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_schedule_items?id=eq.${id}`,
    {
      method: 'DELETE',
      headers: apiHeaders()
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Removing assignment schedule item failed (${response.status}): ${detail}`
    );
  }
}

async function fetchAssignmentScheduleSelections(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_schedule_selections?select=*,course_assignments!inner(course_id)&course_assignments.course_id=eq.${courseId}&order=created_at.asc`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading assignment schedule choices'
  );
}

async function createAssignmentScheduleSelection(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_schedule_selections`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }),
      body: JSON.stringify(data)
    }
  );

  return readJson(
    response,
    'Adding assignment schedule choice'
  );
}

async function deleteAssignmentScheduleSelection(id) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_schedule_selections?id=eq.${id}`,
    {
      method: 'DELETE',
      headers: apiHeaders()
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Removing assignment schedule choice failed (${response.status}): ${detail}`
    );
  }
}

async function deleteAssignmentScheduleSelectionsForAssignment(
  courseAssignmentId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_schedule_selections?course_assignment_id=eq.${courseAssignmentId}`,
    {
      method: 'DELETE',
      headers: apiHeaders()
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Removing assignment schedule choices failed (${response.status}): ${detail}`
    );
  }
}

async function fetchAssignmentScheduleDuplicates(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_schedule_duplicate_details?course_id=eq.${courseId}&select=*`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading duplicated assignment Schedules'
  );
}

async function setAssignmentScheduleDuplicate(
  courseAssignmentId,
  sourceCourseAssignmentId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/set_assignment_schedule_duplicate`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        p_course_assignment_id: courseAssignmentId,
        p_source_course_assignment_id: sourceCourseAssignmentId || null
      })
    }
  );

  return readJson(response, 'Updating Duplicate Schedule');
}

async function setAssignmentScheduleSharedMode(
  courseId,
  assignmentTypeId,
  enabled
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/set_assignment_schedule_shared_mode`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        p_course_id: courseId,
        p_assignment_type_id: assignmentTypeId,
        p_enabled: !!enabled
      })
    }
  );

  return readJson(response, 'Updating shared Schedule mode');
}

async function fetchAssignmentSequences(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_sequences?course_id=eq.${courseId}&select=*&order=name.asc`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading assignment sequences');
}

async function createAssignmentSequence(courseId, name) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_sequences`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }),
      body: JSON.stringify({
        course_id: courseId,
        name
      })
    }
  );

  return readJson(response, 'Creating assignment sequence');
}

async function deleteAssignmentSequence(id) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_sequences?id=eq.${id}`,
    {
      method: 'DELETE',
      headers: apiHeaders()
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Removing assignment sequence failed (${response.status}): ${detail}`
    );
  }
}

async function fetchTypeDescriptions(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_descriptions?course_id=eq.${courseId}&select=*`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading assignment descriptions');
}

async function fetchGroupDeadlines(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_group_deadlines?select=*,course_assignments!inner(course_id)&course_assignments.course_id=eq.${courseId}`,
    { headers: apiHeaders() }
  );

  return readJson(response, 'Loading group deadlines');
}

async function upsertTypeSettings(
  courseId,
  assignmentTypeId,
  data
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?on_conflict=course_id,assignment_type_id`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates'
      }),
      body: JSON.stringify({
        course_id: courseId,
        assignment_type_id: assignmentTypeId,
        ...data
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Saving assignment-type setting failed (${response.status}): ${detail}`
    );
  }
}

async function upsertGroupDeadline(
  courseAssignmentId,
  groupNumber,
  data
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_group_deadlines?on_conflict=course_assignment_id,group_number`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates,return=representation'
      }),
      body: JSON.stringify({
        course_assignment_id: courseAssignmentId,
        group_number: groupNumber,
        ...data
      })
    }
  );

  return readJson(
    response,
    'Saving group deadline'
  );
}


async function fetchAiDisclosureSettings(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?course_id=eq.${courseId}&select=*`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading AI Disclosure settings'
  );
}

async function upsertAiDisclosureSetting(
  courseId,
  assignmentTypeId,
  enabled
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?on_conflict=course_id,assignment_type_id`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates,return=representation'
      }),
      body: JSON.stringify({
        course_id: courseId,
        assignment_type_id: assignmentTypeId,
        ai_disclosure_enabled: !!enabled,
        updated_at: new Date().toISOString()
      })
    }
  );

  return readJson(
    response,
    'Saving AI Disclosure setting'
  );
}

async function fetchAssignmentWeights(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_weights?course_id=eq.${courseId}&select=*`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading assignment weights'
  );
}

async function saveAssignmentWeight(
  courseId,
  assignmentTypeId,
  weightPercent
) {
  const existingResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_weights?course_id=eq.${courseId}&assignment_type_id=eq.${assignmentTypeId}&select=*&limit=1`,
    { headers: apiHeaders() }
  );

  const existing = await readJson(
    existingResponse,
    'Checking assignment weight'
  );

  if (existing.length > 0) {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/course_assignment_type_weights?course_id=eq.${courseId}&assignment_type_id=eq.${assignmentTypeId}`,
      {
        method: 'PATCH',
        headers: apiHeaders({
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        }),
        body: JSON.stringify({
          weight_percent: weightPercent
        })
      }
    );

    return readJson(
      response,
      'Saving assignment weight'
    );
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_weights`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }),
      body: JSON.stringify({
        course_id: courseId,
        assignment_type_id: assignmentTypeId,
        weight_percent: weightPercent
      })
    }
  );

  return readJson(
    response,
    'Saving assignment weight'
  );
}

async function deleteAssignmentWeight(
  courseId,
  assignmentTypeId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_weights?course_id=eq.${courseId}&assignment_type_id=eq.${assignmentTypeId}`,
    {
      method: 'DELETE',
      headers: apiHeaders()
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Removing assignment weight failed (${response.status}): ${detail}`
    );
  }
}

async function assignmentHasStudentRecords(
  courseAssignmentId
) {
  const [submissionResponse, completionResponse] =
    await Promise.all([
      fetch(
        `${SUPABASE_URL}/rest/v1/assignment_submissions?course_assignment_id=eq.${courseAssignmentId}&select=id&limit=1`,
        { headers: apiHeaders() }
      ),
      fetch(
        `${SUPABASE_URL}/rest/v1/assignment_completions?course_assignment_id=eq.${courseAssignmentId}&select=id&limit=1`,
        { headers: apiHeaders() }
      )
    ]);

  const submissions = await readJson(
    submissionResponse,
    'Checking assignment submissions'
  );

  const completions = await readJson(
    completionResponse,
    'Checking assignment completions'
  );

  return (
    submissions.length > 0 ||
    completions.length > 0
  );
}

async function deleteRows(
  path,
  label
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/${path}`,
    {
      method: 'DELETE',
      headers: apiHeaders()
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `${label} failed (${response.status}): ${detail}`
    );
  }
}

async function deleteAssignmentConfiguration(
  courseAssignmentId
) {
  await deleteAssignmentScheduleSelectionsForAssignment(
    courseAssignmentId
  );

  await deleteRows(
    `assignment_group_memberships?course_assignment_id=eq.${courseAssignmentId}`,
    'Removing assignment groups'
  );

  await deleteRows(
    `course_assignment_group_deadlines?course_assignment_id=eq.${courseAssignmentId}`,
    'Removing group deadlines'
  );

  await deleteRows(
    `course_assignment_schedule_items?course_assignment_id=eq.${courseAssignmentId}`,
    'Removing Schedule links'
  );
}

async function deleteCourseAssignmentTypeData(
  courseId,
  assignmentTypeId
) {
  await deleteRows(
    `course_assignment_type_settings?course_id=eq.${courseId}&assignment_type_id=eq.${assignmentTypeId}`,
    'Removing assignment settings'
  );

  await deleteRows(
    `course_assignment_type_descriptions?course_id=eq.${courseId}&assignment_type_id=eq.${assignmentTypeId}`,
    'Removing course-specific assignment description'
  );

  await deleteAssignmentWeight(
    courseId,
    assignmentTypeId
  );
}

async function clearFeedbackReferencesToAssignments(
  assignmentIds
) {
  const ids = (assignmentIds || [])
    .filter(Boolean);

  if (ids.length === 0) return;

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignments?feedback_available_in_assignment_id=in.(${ids.join(',')})`,
    {
      method: 'PATCH',
      headers: apiHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        feedback_available_in_assignment_id: null
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Clearing feedback links failed (${response.status}): ${detail}`
    );
  }
}

async function fetchCourseEnrolments(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&active=eq.true&select=id,student_id,stream,group_number,students(id,student_code,english_name,chinese_name,is_master)`,
    { headers: apiHeaders() }
  );

  const rows = await readJson(response, 'Loading students');

  return rows
    .filter(row => row.students?.is_master !== true)
    .sort((a, b) => {
    const aCode = String(a.students?.student_code || '');
    const bCode = String(b.students?.student_code || '');

    return aCode.localeCompare(
      bCode,
      undefined,
      { numeric: true, sensitivity: 'base' }
    );
  });
}


async function fetchGroupMemberships(courseAssignmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_group_memberships?course_assignment_id=eq.${courseAssignmentId}&select=*&order=group_number.asc`,
    { headers: apiHeaders() }
  );
  return readJson(response, 'Loading assignment groups');
}

async function fetchAllGroupMemberships(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_group_memberships?select=*,course_assignments!inner(course_id)&course_assignments.course_id=eq.${courseId}`,
    { headers: apiHeaders() }
  );

  return readJson(
    response,
    'Loading assignment group memberships'
  );
}


async function upsertGroupMemberships(
  courseAssignmentId,
  enrolmentIds,
  groupNumber
) {
  if (!enrolmentIds.length) return [];

  const rows = enrolmentIds.map(enrolmentId => ({
    course_assignment_id: courseAssignmentId,
    enrolment_id: enrolmentId,
    group_number: groupNumber
  }));

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_group_memberships?on_conflict=course_assignment_id,enrolment_id`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates,return=representation'
      }),
      body: JSON.stringify(rows)
    }
  );

  return readJson(response, 'Adding students to group');
}

async function removeGroupMembership(
  courseAssignmentId,
  enrolmentId
) {
  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/assignment_group_memberships?course_assignment_id=eq.${courseAssignmentId}&enrolment_id=eq.${enrolmentId}`,
      {
        method: 'DELETE',
        headers:
          apiHeaders()
      }
    );

  await readJson(
    response,
    'Removing student from group'
  );
}

async function upsertTypeDescription(
  courseId,
  assignmentTypeId,
  description
) {
  const lookupResponse = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_descriptions?course_id=eq.${encodeURIComponent(courseId)}&assignment_type_id=eq.${encodeURIComponent(assignmentTypeId)}&select=id`,
    { headers: apiHeaders() }
  );

  const existingRows = await readJson(
    lookupResponse,
    'Checking existing assignment description'
  );

  const payload = {
    description,
    updated_at: new Date().toISOString()
  };

  if (
    Array.isArray(existingRows) &&
    existingRows.length > 0
  ) {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/course_assignment_type_descriptions?course_id=eq.${encodeURIComponent(courseId)}&assignment_type_id=eq.${encodeURIComponent(assignmentTypeId)}`,
      {
        method: 'PATCH',
        headers: apiHeaders({
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        }),
        body: JSON.stringify(payload)
      }
    );

    return readJson(
      response,
      'Saving description'
    );
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_descriptions`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }),
      body: JSON.stringify({
        course_id: courseId,
        assignment_type_id: assignmentTypeId,
        ...payload
      })
    }
  );

  return readJson(
    response,
    'Saving description'
  );
}

async function saveCourseAssignment(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignments`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }),
      body: JSON.stringify(data)
    }
  );

  return readJson(response, 'Adding assignment');
}


async function createCourseAssignmentConfiguration(
  cmsToken,
  courseId,
  sourceAssignmentTypeId,
  instanceCount
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/create_course_assignment_configuration`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        p_cms_token: cmsToken,
        p_course_id: courseId,
        p_source_assignment_type_id:
          sourceAssignmentTypeId,
        p_instance_count: instanceCount
      })
    }
  );

  return readJson(
    response,
    'Adding configured assignment'
  );
}

async function updateCourseAssignment(id, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignments?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      }),
      body: JSON.stringify(data)
    }
  );

  return readJson(response, 'Updating assignment');
}

async function deleteCourseAssignment(id) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignments?id=eq.${id}`,
    {
      method: 'DELETE',
      headers: apiHeaders()
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Removing assignment failed (${response.status}): ${detail}`
    );
  }
}

const CORE_NAMES = [
  'Attendance',
  'Critical Questions',
  'Philosophical Diary'
];

const WEEK_FIELD = {
  // Attendance is configured in the dedicated Attendance tab.
  'Critical Questions': 'cq_enabled',
  'Philosophical Diary': 'pd_enabled'
};

const COURSE_FIELD = {
  'Attendance': 'attendance_enabled',
  'Critical Questions': 'cq_enabled',
  'Philosophical Diary': 'pd_enabled'
};

function TypeNameEditor({
  courseId,
  assignmentTypeId,
  libraryName,
  initialName,
  onSaved
}) {
  const [name, setName] =
    useState(initialName || libraryName || '');
  const [saving, setSaving] =
    useState(false);
  const [message, setMessage] =
    useState('');

  useEffect(() => {
    setName(initialName || libraryName || '');
    setMessage('');
  }, [initialName, libraryName]);

  async function save() {
    const trimmed = name.trim();

    if (!trimmed) {
      setMessage('The assignment name cannot be blank.');
      return;
    }

    const override =
      trimmed === String(libraryName || '').trim()
        ? null
        : trimmed;

    setSaving(true);
    setMessage('');

    try {
      await upsertTypeSettings(
        courseId,
        assignmentTypeId,
        {
          display_name: override
        }
      );

      if (onSaved) {
        onSaved(override);
      }

      setMessage('Assignment name saved.');
    } catch (err) {
      console.error(
        'Saving assignment name failed:',
        err
      );
      setMessage(
        err?.message ||
        'Could not save the assignment name.'
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        marginBottom: '18px',
        paddingBottom: '18px',
        borderBottom: '1px solid #e6ebef'
      }}
    >
      <div
        style={{
          fontWeight: 700,
          color: '#334e68',
          marginBottom: '6px'
        }}
      >
        Assignment Name
      </div>

      <div
        style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center'
        }}
      >
        <input
          type="text"
          value={name}
          onChange={event =>
            setName(event.target.value)
          }
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              save();
            }
          }}
          style={{
            flex: 1,
            minWidth: 0,
            padding: '9px 10px',
            border: '1px solid #bcccdc',
            borderRadius: '6px',
            font: 'inherit'
          }}
        />

        <button
          type="button"
          style={styles.smallBtn}
          disabled={saving}
          onClick={save}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      <div
        style={{
          marginTop: '6px',
          color: '#829ab1',
          fontSize: '12px'
        }}
      >
        This changes the name only in this course. The Assignment Library name remains “{libraryName}”.
      </div>

      {message && (
        <div
          style={{
            marginTop: '7px',
            color: message === 'Assignment name saved.'
              ? '#276738'
              : '#a61b1b',
            fontSize: '13px'
          }}
        >
          {message}
        </div>
      )}
    </div>
  );
}

function AssignmentInstanceNameEditor({
  assignment,
  label,
  automaticLabel,
  onSave
}) {
  const [editing, setEditing] =
    useState(false);

  const [draft, setDraft] =
    useState(
      assignment?.instance_name ||
      ''
    );

  const [saving, setSaving] =
    useState(false);

  useEffect(() => {
    if (!editing) {
      setDraft(
        assignment?.instance_name ||
        ''
      );
    }
  }, [
    assignment?.instance_name,
    editing
  ]);

  if (!editing) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexWrap: 'wrap'
        }}
      >
        <span>{label}</span>

        <button
          type="button"
          onClick={() =>
            setEditing(true)
          }
          style={{
            border: 0,
            background: 'transparent',
            padding: '1px 3px',
            color: '#52758c',
            fontSize: '10px',
            fontWeight: '700',
            cursor: 'pointer'
          }}
        >
          Edit name
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexWrap: 'wrap'
      }}
    >
      <input
        type="text"
        autoFocus
        value={draft}
        placeholder={automaticLabel}
        onChange={event =>
          setDraft(
            event.target.value
          )
        }
        onKeyDown={event => {
          if (event.key === 'Escape') {
            setDraft(
              assignment?.instance_name ||
              ''
            );
            setEditing(false);
          }
        }}
        style={{
          minWidth: '210px',
          flex: '1 1 210px',
          border: '1px solid #cbd5df',
          borderRadius: '6px',
          padding: '6px 8px',
          fontSize: '13px'
        }}
      />

      <button
        type="button"
        disabled={saving}
        style={styles.smallMoveBtn}
        onClick={async () => {
          setSaving(true);

          try {
            const clean =
              draft.trim();

            await onSave(
              clean || null
            );

            setEditing(false);
          } finally {
            setSaving(false);
          }
        }}
      >
        {saving
          ? 'Saving...'
          : 'Save'}
      </button>

      <button
        type="button"
        disabled={saving}
        style={styles.smallCancelBtn}
        onClick={() => {
          setDraft(
            assignment?.instance_name ||
            ''
          );
          setEditing(false);
        }}
      >
        Cancel
      </button>
    </div>
  );
}

function TypeDescriptionEditor({
  courseId,
  assignmentTypeId,
  initialDescription,
  onSaved
}) {
  const [description, setDescription] = useState(
    initialDescription || ''
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDescription(initialDescription || '');
  }, [initialDescription]);

  async function handleSave() {
    setSaving(true);

    try {
      await upsertTypeDescription(
        courseId,
        assignmentTypeId,
        description
      );
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);

      if (onSaved) {
        onSaved(description);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <details
      style={{
        ...styles.descriptionWrap,
        border: '1px solid #d7dee5',
        borderRadius: '8px',
        background: '#f8fafc',
        overflow: 'hidden'
      }}
    >
      <summary
        style={{
          padding: '11px 14px',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: '700',
          color: '#1f2933',
          userSelect: 'none'
        }}
      >
        Course-specific description
      </summary>

      <div
        style={{
          padding: '0 14px 14px'
        }}
      >
        <div style={styles.fieldHint}>
          This description is shared by all instances of this configured
          assignment and overrides the Assignment Library default.
        </div>

        <RichTextEditor
          value={description}
          onChange={setDescription}
        />

        <button
          type="button"
          style={styles.secondaryBtn}
          disabled={saving}
          onClick={handleSave}
        >
          {saving
            ? 'Saving...'
            : saved
              ? '✓ Saved'
              : 'Save Description'}
        </button>
      </div>
    </details>
  );
}

function studentLabel(enrolment) {
  const student = enrolment?.students;

  return [
    student?.student_code || '—',
    student?.english_name || student?.chinese_name || ''
  ]
    .filter(Boolean)
    .join(' — ');
}

function GroupManagerModal({
  assignment,
  label,
  enrolments,
  memberships,
  loading,
  saving,
  selectedIds,
  setSelectedIds,
  onAdd,
  onRemove,
  copySources,
  onCopySource,
  groupNameFor,
  groupDescriptionFor,
  onRenameGroup,
  onEditDescription,
  onClose
}) {
  const [copyTargetGroupNumber, setCopyTargetGroupNumber] =
    useState(null);

  if (!assignment) return null;

  const membershipByEnrolment = new Map(
    memberships.map(row => [row.enrolment_id, row])
  );

  const unassigned = enrolments.filter(
    enrolment => !membershipByEnrolment.has(enrolment.id)
  );

  function toggleSelected(enrolmentId) {
    setSelectedIds(prev =>
      prev.includes(enrolmentId)
        ? prev.filter(id => id !== enrolmentId)
        : [...prev, enrolmentId]
    );
  }

  const groupNumbers = Array.from(
    { length: Math.max(0, Number(assignment.group_count) || 0) },
    (_, index) => index + 1
  );

  return (
    <div
      style={styles.modalBackdrop}
      onMouseDown={onClose}
    >
      <div
        style={styles.modal}
        onMouseDown={event => event.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <div>
            <h3 style={styles.modalTitle}>
              Manage Groups — {label}
            </h3>
            <div style={styles.modalHint}>
              Select students on the left and add them manually, or copy
              exact membership from an existing stream or assignment group.
            </div>
          </div>

          <button
            type="button"
            style={styles.closeBtn}
            onClick={onClose}
            aria-label="Close group manager"
          >
            ×
          </button>
        </div>

        {loading ? (
          <div style={styles.modalLoading}>Loading groups...</div>
        ) : (
          <div style={styles.groupManagerBody}>
            <div style={styles.unassignedPanel}>
              <div style={styles.groupPanelTitle}>
                Unassigned Students
              </div>

              {unassigned.length === 0 ? (
                <div style={styles.emptyGroupText}>
                  All active students are assigned.
                </div>
              ) : (
                <div style={styles.studentPicker}>
                  {unassigned.map(enrolment => (
                    <label
                      key={enrolment.id}
                      style={styles.studentPickerRow}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(enrolment.id)}
                        onChange={() => toggleSelected(enrolment.id)}
                      />
                      <span>{studentLabel(enrolment)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.groupsScroll}>
              <div
                style={{
                  ...styles.groupsGrid,
                  gridTemplateColumns:
                    `repeat(${Math.max(1, groupNumbers.length)}, minmax(190px, 1fr))`
                }}
              >
                {groupNumbers.map(groupNumber => {
                  const memberIds = memberships
                    .filter(row => row.group_number === groupNumber)
                    .map(row => row.enrolment_id);

                  const members = enrolments.filter(enrolment =>
                    memberIds.includes(enrolment.id)
                  );

                  return (
                    <div
                      key={groupNumber}
                      style={styles.groupColumn}
                    >
                      <div style={styles.groupColumnHeader}>
                        <div style={styles.groupColumnTitle}>
                          {groupNameFor(groupNumber)}
                        </div>

                        {groupDescriptionFor?.(groupNumber) && (
                          <div
                            style={{
                              fontSize: '12px',
                              lineHeight: '1.45',
                              color: '#52616d',
                              marginBottom: '8px',
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            {groupDescriptionFor(groupNumber)}
                          </div>
                        )}

                        <div style={styles.groupColumnActions}>
                          <button
                            type="button"
                            style={styles.smallBtn}
                            disabled={saving}
                            onClick={() => onRenameGroup(groupNumber)}
                          >
                            Rename
                          </button>

                          <button
                            type="button"
                            style={styles.smallBtn}
                            disabled={saving}
                            onClick={() =>
                              onEditDescription(groupNumber)
                            }
                          >
                            {groupDescriptionFor?.(groupNumber)
                              ? 'Edit Description'
                              : 'Add Description'}
                          </button>

                          <button
                            type="button"
                            style={{
                              ...styles.smallBtn,
                              ...(
                                selectedIds.length === 0 || saving
                                  ? styles.disabledBtn
                                  : {}
                              )
                            }}
                            disabled={
                              selectedIds.length === 0 || saving
                            }
                            onClick={() => onAdd(groupNumber)}
                          >
                            Add Students
                          </button>

                          <button
                            type="button"
                            style={styles.copyGroupBtn}
                            disabled={saving}
                            onClick={() =>
                              setCopyTargetGroupNumber(current =>
                                current === groupNumber
                                  ? null
                                  : groupNumber
                              )
                            }
                          >
                            Copy from Existing Group
                          </button>
                        </div>
                      </div>

                      {copyTargetGroupNumber === groupNumber && (
                        <div style={styles.copySourcePanel}>
                          <div style={styles.copySourceHeading}>
                            Replace {groupNameFor(groupNumber)} with:
                          </div>

                          {['Streams', 'Assignment Groups'].map(category => {
                            const categorySources = (copySources || [])
                              .filter(source => source.category === category);

                            return (
                              <div
                                key={category}
                                style={styles.copySourceCategory}
                              >
                                <div style={styles.copySourceCategoryTitle}>
                                  {category}
                                </div>

                                {categorySources.length === 0 ? (
                                  <div style={styles.emptyGroupText}>
                                    No {category.toLowerCase()} available.
                                  </div>
                                ) : (
                                  <div style={styles.copySourceButtons}>
                                    {categorySources.map(source => (
                                      <button
                                        key={source.key}
                                        type="button"
                                        style={styles.copySourceBtn}
                                        disabled={saving}
                                        onClick={() =>
                                          onCopySource(groupNumber, source)
                                            .then(() =>
                                              setCopyTargetGroupNumber(null)
                                            )
                                        }
                                      >
                                        <span>{source.label}</span>
                                        <span style={styles.copySourceCount}>
                                          {source.enrolmentIds.length}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          <button
                            type="button"
                            style={styles.copySourceCancel}
                            disabled={saving}
                            onClick={() => setCopyTargetGroupNumber(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      )}

                      <div style={styles.groupMemberList}>
                        {members.length === 0 ? (
                          <div style={styles.emptyGroupText}>
                            No students yet.
                          </div>
                        ) : (
                          members.map(enrolment => (
                            <div
                              key={enrolment.id}
                              style={styles.groupMember}
                            >
                              <span>
                                {studentLabel(enrolment)}
                              </span>

                              <button
                                type="button"
                                style={styles.removeMemberBtn}
                                disabled={saving}
                                onClick={() =>
                                  onRemove(enrolment.id)
                                }
                              >
                                Remove
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div style={styles.modalFooter}>
          <button
            type="button"
            style={styles.btn}
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CourseAssignments({ course, session }) {
  const [weeks, setWeeks] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [assignmentTypes, setAssignmentTypes] = useState([]);
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [typeDescriptions, setTypeDescriptions] = useState({});
  const [enrolments, setEnrolments] = useState([]);
  const [assignmentWeights, setAssignmentWeights] =
    useState([]);
  const [weightDrafts, setWeightDrafts] =
    useState({});
  const [weightSavingTypeId, setWeightSavingTypeId] =
    useState(null);
  const [aiDisclosureSettings, setAiDisclosureSettings] =
    useState({});
  const [aiDisclosureSavingTypeId, setAiDisclosureSavingTypeId] =
    useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [addingTypeId, setAddingTypeId] = useState('');
  const [addingCount, setAddingCount] = useState(1);
  const [saving, setSaving] = useState(false);
  const [
    instanceCountDrafts,
    setInstanceCountDrafts
  ] = useState({});
  const [
    instanceCountSavingTypeId,
    setInstanceCountSavingTypeId
  ] = useState(null);
  const [
    removingTypeId,
    setRemovingTypeId
  ] = useState(null);

  const [expandedTypeId, setExpandedTypeId] = useState(null);
  const [typeTabs, setTypeTabs] = useState({});

  const [groupModalAssignmentId, setGroupModalAssignmentId] =
    useState(null);
  const [groupMemberships, setGroupMemberships] = useState([]);
  const [selectedGroupStudents, setSelectedGroupStudents] =
    useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupSaving, setGroupSaving] = useState(false);
  const [allGroupMemberships, setAllGroupMemberships] = useState([]);
  const [assignmentGroupLabels, setAssignmentGroupLabels] =
    useState({});
  const [
    assignmentGroupDescriptions,
    setAssignmentGroupDescriptions
  ] = useState({});
  const [coursePlacementLabels, setCoursePlacementLabels] =
    useState({ streams: {}, groups: {} });
  const [applyingGroupSetupId, setApplyingGroupSetupId] =
    useState(null);

  const [typeSettings, setTypeSettings] = useState({});
  const [groupDeadlines, setGroupDeadlines] = useState({});
  const [assignmentSequences, setAssignmentSequences] = useState([]);
  const [newSequenceName, setNewSequenceName] = useState('');
  const [sequenceSaving, setSequenceSaving] = useState(false);
  const [menuOrderSaving, setMenuOrderSaving] = useState(false);

  const [scheduleReadings, setScheduleReadings] = useState([]);
  const [scheduleEvents, setScheduleEvents] = useState([]);
  const [assignmentScheduleItems, setAssignmentScheduleItems] =
    useState([]);
  const [scheduleSelections, setScheduleSelections] = useState({});
  const [scheduleSavingId, setScheduleSavingId] = useState(null);
  const [scheduleModeSavingTypeId, setScheduleModeSavingTypeId] = useState(null);
  const [scheduleTimingDrafts, setScheduleTimingDrafts] = useState({});
  const [scheduleTimingSavingTypeId, setScheduleTimingSavingTypeId] = useState(null);
  const [scheduleChoiceDrafts, setScheduleChoiceDrafts] = useState({});
  const [scheduleChoiceSavingTypeId, setScheduleChoiceSavingTypeId] = useState(null);
  const [scheduleSharingSavingTypeId, setScheduleSharingSavingTypeId] = useState(null);
  const [assignmentScheduleSelections, setAssignmentScheduleSelections] =
    useState([]);
  const [assignmentScheduleDuplicates, setAssignmentScheduleDuplicates] =
    useState([]);
  const [duplicateModeDrafts, setDuplicateModeDrafts] = useState({});
  const [duplicateSavingAssignmentId, setDuplicateSavingAssignmentId] =
    useState(null);
  const [choiceUnitByAssignment, setChoiceUnitByAssignment] = useState({});
  const [choiceItemByAssignment, setChoiceItemByAssignment] = useState({});
  const [choiceTargetAssignmentByType, setChoiceTargetAssignmentByType] =
    useState({});
  const [choiceSavingAssignmentId, setChoiceSavingAssignmentId] =
    useState(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    setExpandedTypeId(null);
    setGroupModalAssignmentId(null);

    Promise.all([
      fetchWeeks(course.id),
      fetchAssignmentCalendarHolidays(course.instructor_id),
      fetchAssignmentTypes(course.id),
      fetchCourseAssignments(course.id),
      fetchTypeDescriptions(course.id),
      fetchCourseEnrolments(course.id),
      fetchAssignmentWeights(course.id),
      fetchAiDisclosureSettings(course.id),
      fetchGroupDeadlines(course.id),
      fetchAllGroupMemberships(course.id),
      fetchAssignmentSequences(course.id),
      fetchScheduledCourseReadings(course.id),
      fetchCourseEventsForAssignmentSchedule(course.id),
      fetchAssignmentScheduleItems(course.id),
      fetchAssignmentScheduleSelections(course.id),
      fetchAssignmentScheduleDuplicates(course.id)
    ])
      .then(([
        w,
        h,
        t,
        a,
        td,
        e,
        aw,
        aiSettings,
        gd,
        allGroups,
        sequences,
        scheduledReadings,
        courseEvents,
        scheduleItems,
        scheduleChoices,
        scheduleDuplicates
      ]) => {
        setWeeks(w);
        setHolidays(
          Array.isArray(h)
            ? h
            : []
        );
        setAssignmentTypes(t);
        setCourseAssignments(a);
        setCourseAssignments(a);
        setEnrolments(e);
        setAssignmentWeights(
          Array.isArray(aw) ? aw : []
        );

        const weightMap = {};
        (Array.isArray(aw) ? aw : []).forEach(row => {
          weightMap[row.assignment_type_id] =
            row.weight_percent ?? '';
        });
        setWeightDrafts(weightMap);

        const aiDisclosureMap = {};
        const settingsMap = {};

        (Array.isArray(aiSettings) ? aiSettings : []).forEach(row => {
          aiDisclosureMap[row.assignment_type_id] =
            !!row.ai_disclosure_enabled;

          settingsMap[row.assignment_type_id] = row;
        });

        setAiDisclosureSettings(aiDisclosureMap);
        setTypeSettings(settingsMap);

        const timingDraftMap = {};
        Object.entries(settingsMap).forEach(([assignmentTypeId, settings]) => {
          timingDraftMap[assignmentTypeId] =
            scheduleTimingDraftFromSettings(settings);
        });
        setScheduleTimingDrafts(timingDraftMap);

        const choiceDraftMap = {};
        Object.entries(settingsMap).forEach(([assignmentTypeId, settings]) => {
          choiceDraftMap[assignmentTypeId] =
            scheduleChoiceDraftFromSettings(settings);
        });
        setScheduleChoiceDrafts(choiceDraftMap);

        const deadlineMap = {};

        (Array.isArray(gd) ? gd : []).forEach(row => {
          if (!deadlineMap[row.course_assignment_id]) {
            deadlineMap[row.course_assignment_id] = {};
          }

          deadlineMap[row.course_assignment_id][row.group_number] = row;
        });

        setGroupDeadlines(deadlineMap);
        setAllGroupMemberships(
          Array.isArray(allGroups) ? allGroups : []
        );
        setAssignmentSequences(
          Array.isArray(sequences) ? sequences : []
        );
        setScheduleReadings(
          Array.isArray(scheduledReadings)
            ? scheduledReadings
            : []
        );
        setScheduleEvents(
          Array.isArray(courseEvents)
            ? courseEvents
            : []
        );
        setAssignmentScheduleItems(
          Array.isArray(scheduleItems)
            ? scheduleItems
            : []
        );
        setAssignmentScheduleSelections(
          Array.isArray(scheduleChoices)
            ? scheduleChoices
            : []
        );
        setAssignmentScheduleDuplicates(
          Array.isArray(scheduleDuplicates)
            ? scheduleDuplicates
            : []
        );
        setDuplicateModeDrafts({});

        const descMap = {};
        td.forEach(row => {
          descMap[row.assignment_type_id] = row.description;
        });
        setTypeDescriptions(descMap);
      })
      .catch(err => {
        console.error('Assignment settings load failed:', err);
        setError(err?.message || 'Failed to load assignments.');
      })
      .finally(() => setLoading(false));
  }, [course.id, course.instructor_id]);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      fetchAssignmentGroupLabelsForCourse(course.id),
      fetchCoursePlacementLabels(course.id)
    ])
      .then(([assignmentLabels, placementLabels]) => {
        if (cancelled) return;
        setAssignmentGroupLabels(assignmentLabels || {});
        setCoursePlacementLabels(
          placementLabels || { streams: {}, groups: {} }
        );
      })
      .catch(err => {
        if (!cancelled) {
          console.error(
            'Loading Group display names failed:',
            err
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [course.id]);

  useEffect(() => {
    let cancelled = false;

    fetchAssignmentGroupDescriptionsForCourse(
      course.id
    )
      .then(descriptions => {
        if (cancelled) return;

        setAssignmentGroupDescriptions(
          descriptions || {}
        );
      })
      .catch(err => {
        console.error(
          'Loading Assignment Group descriptions failed:',
          err
        );
      });

    return () => {
      cancelled = true;
    };
  }, [course.id]);

  function assignmentGroupDescription(
    courseAssignmentId,
    groupNumber
  ) {
    return (
      assignmentGroupDescriptions[
        `${courseAssignmentId}|${groupNumber}`
      ] || ''
    );
  }


  function assignmentGroupName(
    courseAssignmentId,
    groupNumber
  ) {
    return getAssignmentGroupDisplayName(
      assignmentGroupLabels,
      courseAssignmentId,
      groupNumber
    );
  }

  function streamName(streamNumber) {
    return getStreamDisplayName(
      coursePlacementLabels,
      streamNumber
    );
  }

  function courseGroupName(
    streamNumber,
    groupNumber
  ) {
    return getCourseGroupDisplayName(
      coursePlacementLabels,
      streamNumber,
      groupNumber
    );
  }

  async function handleEditAssignmentGroupDescription(
    courseAssignmentId,
    groupNumber
  ) {
    const current =
      assignmentGroupDescription(
        courseAssignmentId,
        groupNumber
      );

    const next = window.prompt(
      'Group description. Leave blank to remove the description.',
      current
    );

    if (next === null) return;

    setGroupSaving(true);
    setError('');

    try {
      await saveInstructorAssignmentGroupDescription({
        cmsToken: session?.cmsToken,
        courseAssignmentId,
        groupNumber,
        description: next
      });

      const refreshedDescriptions =
        await fetchAssignmentGroupDescriptionsForCourse(
          course.id
        );

      setAssignmentGroupDescriptions(
        refreshedDescriptions || {}
      );
    } catch (err) {
      setError(
        err?.message ||
        'Could not save the Assignment Group description.'
      );
    } finally {
      setGroupSaving(false);
    }
  }


  async function handleRenameAssignmentGroup(
    courseAssignmentId,
    groupNumber
  ) {
    const current =
      assignmentGroupLabels[
        `${courseAssignmentId}|${groupNumber}`
      ] || '';

    const next = window.prompt(
      `Group name. Leave blank to restore "Group ${groupNumber}".`,
      current
    );

    if (next === null) return;

    setGroupSaving(true);
    setError('');

    try {
      await renameInstructorAssignmentGroup({
        cmsToken: session?.cmsToken,
        courseAssignmentId,
        groupNumber,
        name: next
      });

      const refreshedLabels =
        await fetchAssignmentGroupLabelsForCourse(
          course.id
        );

      setAssignmentGroupLabels(
        refreshedLabels || {}
      );
    } catch (err) {
      setError(
        err?.message ||
        'Could not rename the Assignment Group.'
      );
    } finally {
      setGroupSaving(false);
    }
  }

  function getScheduleItemChronology(
    item
  ) {
    const source =
      getScheduleSourceInfo(
        item
      );

    if (!source?.anchorDate) {
      return null;
    }


    let time =
      '00:00:00';


    if (
      item.item_type ===
      'event'
    ) {
      const event =
        scheduleEvents.find(
          row =>
            String(row.id) ===
            String(
              item.course_event_id
            )
        );

      if (event?.event_time) {
        time =
          String(
            event.event_time
          ).slice(
            0,
            8
          );
      }
    }


    const value =
      new Date(
        source.anchorDate +
        'T' +
        time
      ).getTime();


    return Number.isFinite(
      value
    )
      ? value
      : null;
  }


  function compareScheduleItemsByChronology(
    a,
    b
  ) {
    const aTime =
      getScheduleItemChronology(a);

    const bTime =
      getScheduleItemChronology(b);


    if (
      aTime !== null &&
      bTime !== null &&
      aTime !== bTime
    ) {
      return aTime - bTime;
    }


    if (
      aTime !== null &&
      bTime === null
    ) {
      return -1;
    }


    if (
      aTime === null &&
      bTime !== null
    ) {
      return 1;
    }


    const orderCompare =
      Number(
        a.display_order || 0
      ) -
      Number(
        b.display_order || 0
      );


    if (orderCompare !== 0) {
      return orderCompare;
    }


    return String(
      a.created_at || a.id || ''
    ).localeCompare(
      String(
        b.created_at || b.id || ''
      )
    );
  }


  function getAssignmentScheduleItems(
    courseAssignmentId,
    configurationMode = null
  ) {
    return assignmentScheduleItems
      .filter(item =>
        item.course_assignment_id === courseAssignmentId &&
        (
          !configurationMode ||
          item.configuration_mode === configurationMode
        )
      )
      .sort(
        compareScheduleItemsByChronology
      );
  }

  function getAssignmentScheduleDuplicate(courseAssignmentId) {
    return assignmentScheduleDuplicates.find(
      link =>
        String(link.course_assignment_id) ===
        String(courseAssignmentId)
    ) || null;
  }

  function getSharedPoolItemIds(item) {
    if (!item?.id) return [];

    const directLink = getAssignmentScheduleDuplicate(
      item.course_assignment_id
    );

    const poolItemId =
      directLink?.sync_kind === 'shared_instance' &&
      item.duplicate_source_schedule_item_id
        ? item.duplicate_source_schedule_item_id
        : item.id;

    const ids = new Set([String(poolItemId)]);

    assignmentScheduleItems.forEach(candidate => {
      const candidateLink = getAssignmentScheduleDuplicate(
        candidate.course_assignment_id
      );

      if (
        candidateLink?.sync_kind === 'shared_instance' &&
        String(candidate.duplicate_source_schedule_item_id || '') ===
          String(poolItemId)
      ) {
        ids.add(String(candidate.id));
      }
    });

    return [...ids];
  }

  function getScheduleItemChoices(item) {
    const ids = new Set(getSharedPoolItemIds(item));

    return assignmentScheduleSelections.filter(choice =>
      ids.has(String(choice.course_assignment_schedule_item_id))
    );
  }

  function getSharedScheduleSourceAssignment(
    assignmentTypeId,
    assignmentsForType = null
  ) {
    const candidates = (
      assignmentsForType ||
      courseAssignments.filter(
        assignment =>
          String(assignment.assignment_type_id) ===
          String(assignmentTypeId)
      )
    )
      .slice()
      .sort(
        (a, b) =>
          (Number(a.instance_number) || 0) -
            (Number(b.instance_number) || 0) ||
          String(a.id).localeCompare(String(b.id))
      );

    return candidates.find(assignment => {
      const link = getAssignmentScheduleDuplicate(assignment.id);
      return link?.sync_kind !== 'shared_instance';
    }) || candidates[0] || null;
  }

  function getEffectiveDuplicateChoiceEnabled(courseAssignmentId) {
    const byFollower = new Map(
      assignmentScheduleDuplicates.map(link => [
        String(link.course_assignment_id),
        link
      ])
    );

    let current = byFollower.get(String(courseAssignmentId)) || null;
    if (!current) return false;

    const seen = new Set([String(courseAssignmentId)]);
    let effective = current.source_schedule_choice_enabled === true;

    while (current) {
      const sourceId = String(current.source_course_assignment_id || '');
      if (!sourceId || seen.has(sourceId)) break;
      seen.add(sourceId);
      effective = current.source_schedule_choice_enabled === true;
      current = byFollower.get(sourceId) || null;
    }

    return effective;
  }

  // CALENDAR_CARRY_FORWARD_INSTANCES_V1

  function getPreviousAssignmentInstance(
    assignment
  ) {
    if (!assignment) {
      return null;
    }

    const currentNumber =
      Number(
        assignment.instance_number
      ) || 0;

    if (currentNumber <= 1) {
      return null;
    }

    return (
      courseAssignments
        .filter(
          candidate =>
            String(
              candidate
                .assignment_type_id
            ) ===
              String(
                assignment
                  .assignment_type_id
              ) &&
            (
              Number(
                candidate
                  .instance_number
              ) || 0
            ) <
              currentNumber
        )
        .sort(
          (a, b) =>
            (
              Number(
                b.instance_number
              ) || 0
            ) -
            (
              Number(
                a.instance_number
              ) || 0
            )
        )[0] ||
      null
    );
  }


  function getPreviousAssignmentClosingDate(
    assignment
  ) {
    const previous =
      getPreviousAssignmentInstance(
        assignment
      );

    return (
      previous?.due_date ||
      previous?.open_date ||
      ''
    );
  }


  function getPreviousGroupClosingDate(
    assignment,
    groupNumber
  ) {
    const previous =
      getPreviousAssignmentInstance(
        assignment
      );

    if (!previous) {
      return '';
    }

    const previousDeadline =
      groupDeadlines[
        previous.id
      ]?.[
        groupNumber
      ] || null;

    return (
      previousDeadline?.due_date ||
      previous?.due_date ||
      previousDeadline?.open_date ||
      previous?.open_date ||
      ''
    );
  }


  function assignmentDisplayLabel(assignment) {
    if (!assignment) return 'Assignment';

    const type = assignmentTypes.find(
      row => String(row.id) === String(assignment.assignment_type_id)
    );
    const typeName = type?.name || 'Assignment';
    const sameTypeCount = courseAssignments.filter(
      row =>
        String(row.assignment_type_id) ===
        String(assignment.assignment_type_id)
    ).length;

    return typeName + (
      sameTypeCount > 1
        ? ` ${assignment.instance_number}`
        : ''
    );
  }

  function assignmentInstanceLabel(assignment) {
    return getAssignmentLabel(
      assignment
    );
  }

  function duplicateWouldCreateCycle(assignmentId, sourceAssignmentId) {
    const byFollower = new Map(
      assignmentScheduleDuplicates.map(link => [
        String(link.course_assignment_id),
        String(link.source_course_assignment_id)
      ])
    );

    let currentId = String(sourceAssignmentId || '');
    const targetId = String(assignmentId || '');
    const seen = new Set();

    while (currentId && !seen.has(currentId)) {
      if (currentId === targetId) return true;
      seen.add(currentId);
      currentId = byFollower.get(currentId) || '';
    }

    return false;
  }

  function getDuplicateCandidates(assignment) {
    const sequenceId =
      typeSettings[assignment.assignment_type_id]?.sequence_id || null;

    if (!sequenceId) return [];

    return courseAssignments
      .filter(candidate => {
        if (String(candidate.id) === String(assignment.id)) return false;

        const candidateSettings =
          typeSettings[candidate.assignment_type_id];

        if (candidateSettings?.sequence_id !== sequenceId) return false;
        if (candidateSettings?.schedule_enabled !== true) return false;

        const candidateLink =
          getAssignmentScheduleDuplicate(candidate.id);

        if (candidateLink?.sync_kind === 'shared_instance') {
          return false;
        }

        return !duplicateWouldCreateCycle(
          assignment.id,
          candidate.id
        );
      })
      .sort((a, b) =>
        assignmentInstanceLabel(a).localeCompare(
          assignmentInstanceLabel(b),
          undefined,
          { numeric: true, sensitivity: 'base' }
        )
      );
  }

  async function refreshDuplicateScheduleData() {
    const refreshedAssignmentGroupLabels =
      await fetchAssignmentGroupLabelsForCourse(course.id);

    setAssignmentGroupLabels(
      refreshedAssignmentGroupLabels || {}
    );

    const [
      duplicates,
      items,
      choices,
      assignments,
      allGroups
    ] = await Promise.all([
      fetchAssignmentScheduleDuplicates(course.id),
      fetchAssignmentScheduleItems(course.id),
      fetchAssignmentScheduleSelections(course.id),
      fetchCourseAssignments(course.id),
      fetchAllGroupMemberships(course.id)
    ]);

    setAssignmentScheduleDuplicates(
      Array.isArray(duplicates) ? duplicates : []
    );
    setAssignmentScheduleItems(
      Array.isArray(items) ? items : []
    );
    setAssignmentScheduleSelections(
      Array.isArray(choices) ? choices : []
    );
    setCourseAssignments(
      Array.isArray(assignments) ? assignments : []
    );
    setAllGroupMemberships(
      Array.isArray(allGroups) ? allGroups : []
    );

    if (groupModalAssignmentId) {
      setGroupMemberships(
        (Array.isArray(allGroups) ? allGroups : []).filter(
          row =>
            String(row.course_assignment_id) ===
            String(groupModalAssignmentId)
        )
      );
    }
  }

  async function handleSelectDuplicateSource(assignment, sourceId) {
    if (!sourceId) return;

    const existing = getAssignmentScheduleDuplicate(assignment.id);
    const hasCurrentConfiguration =
      getAssignmentScheduleItems(assignment.id).length > 0 ||
      assignmentScheduleSelections.some(
        choice =>
          String(choice.course_assignment_id) === String(assignment.id)
      );

    if (
      (!existing ||
        String(existing.source_course_assignment_id) !== String(sourceId)) &&
      hasCurrentConfiguration
    ) {
      const confirmed = window.confirm(
        "Duplicate will replace this assignment instance's current linked " +
        'Schedule items and choices with a live sync from the selected ' +
        'assignment. Continue?'
      );

      if (!confirmed) return;
    }

    setDuplicateSavingAssignmentId(assignment.id);
    setError('');
    setSuccess('');

    try {
      await setAssignmentScheduleDuplicate(assignment.id, sourceId);
      await refreshDuplicateScheduleData();
      setDuplicateModeDrafts(prev => ({
        ...prev,
        [assignment.id]: false
      }));
      setScheduleSelections(prev => ({
        ...prev,
        [assignment.id]: ''
      }));
      setSuccess(
        `${assignmentDisplayLabel(assignment)} is now live-synced to ` +
        `${assignmentInstanceLabel(
          courseAssignments.find(row => String(row.id) === String(sourceId))
        )}.`
      );
    } catch (err) {
      console.error('Duplicate Schedule setup failed:', err);
      setError(err?.message || 'Failed to set Duplicate Schedule.');
    } finally {
      setDuplicateSavingAssignmentId(null);
    }
  }

  async function handleStopDuplicate(assignment) {
    const confirmed = window.confirm(
      'Stop duplicating this Schedule? The current linked items and choices ' +
      'will be kept, but future changes will no longer sync.'
    );

    if (!confirmed) return;

    setDuplicateSavingAssignmentId(assignment.id);
    setError('');
    setSuccess('');

    try {
      await setAssignmentScheduleDuplicate(assignment.id, null);
      await refreshDuplicateScheduleData();
      setDuplicateModeDrafts(prev => ({
        ...prev,
        [assignment.id]: false
      }));
      setSuccess('Duplicate Schedule stopped. Current configuration was kept.');
    } catch (err) {
      console.error('Stopping Duplicate Schedule failed:', err);
      setError(err?.message || 'Failed to stop Duplicate Schedule.');
    } finally {
      setDuplicateSavingAssignmentId(null);
    }
  }

  function handleToggleDuplicate(assignment) {
    const existing = getAssignmentScheduleDuplicate(assignment.id);

    if (existing) {
      handleStopDuplicate(assignment);
      return;
    }

    setDuplicateModeDrafts(prev => ({
      ...prev,
      [assignment.id]: !prev[assignment.id]
    }));
  }

  function getScheduleSourceInfo(item) {
    if (item.item_type === 'reading') {
      const occurrence = scheduleReadings.find(
        row =>
          row.id ===
          item.week_reading_assignment_id
      );

      return {
        typeLabel: 'Reading',
        title:
          occurrence?.course_readings?.title ||
          'Reading',
        anchorDate: occurrence?.course_weeks?.class_date || null,
        meta: [
          occurrence?.course_weeks?.week_number
            ? `Week ${occurrence.course_weeks.week_number}`
            : null,
          occurrence?.course_readings?.author
            ? `by ${occurrence.course_readings.author}`
            : null
        ].filter(Boolean).join(' · ')
      };
    }

    const courseEvent = scheduleEvents.find(
      row => row.id === item.course_event_id
    );

    return {
      typeLabel: 'Event',
      title: courseEvent?.title || 'Event',
      anchorDate:
        courseEvent?.event_date ||
        courseEvent?.course_weeks?.class_date ||
        null,
      meta:
        courseEvent?.course_weeks?.week_number
          ? `Week ${courseEvent.course_weeks.week_number}`
          : ''
    };
  }

  // SCHEDULE_EVENT_CHRONOLOGICAL_INSERT_20260919
  function getScheduleEventChronology(
    courseEventId
  ) {
    const courseEvent =
      scheduleEvents.find(
        row =>
          String(row.id) ===
          String(courseEventId)
      );

    const anchorDate =
      courseEvent?.event_date ||
      courseEvent
        ?.course_weeks
        ?.class_date ||
      null;

    if (!anchorDate) {
      return null;
    }

    const eventTime =
      courseEvent?.event_time
        ? String(
            courseEvent.event_time
          ).slice(0, 8)
        : '00:00:00';

    const value =
      new Date(
        `${anchorDate}T${eventTime}`
      ).getTime();

    return Number.isFinite(value)
      ? value
      : null;
  }


  function getEventInsertionIndex(
    existingItems,
    newCourseEventId
  ) {
    const newChronology =
      getScheduleEventChronology(
        newCourseEventId
      );

    if (newChronology === null) {
      return existingItems.length;
    }

    const datedEvents =
      existingItems
        .map(
          (item, index) => ({
            item,
            index,

            chronology:
              item.item_type ===
              'event'
                ? getScheduleEventChronology(
                    item.course_event_id
                  )
                : null
          })
        )
        .filter(
          row =>
            row.chronology !==
            null
        );


    if (
      datedEvents.length === 0
    ) {
      return existingItems.length;
    }


    const nextLater =
      datedEvents
        .filter(
          row =>
            row.chronology >
            newChronology
        )
        .sort(
          (a, b) =>
            a.chronology -
              b.chronology ||
            a.index -
              b.index
        )[0];


    if (nextLater) {
      return nextLater.index;
    }


    const previous =
      datedEvents
        .filter(
          row =>
            row.chronology <=
            newChronology
        )
        .sort(
          (a, b) =>
            b.chronology -
              a.chronology ||
            b.index -
              a.index
        )[0];


    if (previous) {
      return (
        previous.index + 1
      );
    }


    return existingItems.length;
  }


  async function handleAddScheduleItem(
    courseAssignmentId,
    configurationMode
  ) {
    const selection =
      scheduleSelections[courseAssignmentId] || '';

    if (!selection) return;

    const mode =
      configurationMode === 'choice'
        ? 'choice'
        : 'single';

    const separatorIndex = selection.indexOf(':');

    if (separatorIndex < 0) {
      setError('Could not identify that schedule item.');
      return;
    }

    const itemType = selection.slice(0, separatorIndex);
    const sourceId = selection.slice(separatorIndex + 1);

    if (
      !['reading', 'event'].includes(itemType) ||
      !sourceId
    ) {
      setError('Could not identify that schedule item.');
      return;
    }

    const existing =
      getAssignmentScheduleItems(
        courseAssignmentId,
        mode
      );

    if (mode === 'single' && existing.length >= 1) {
      setError(
        'Choice is off, so this assignment instance can have only one linked reading/event. Remove the current linked item before adding another.'
      );
      return;
    }

    const nextDisplayOrder =
      existing.reduce(
        (max, item) =>
          Math.max(
            max,
            Number(item.display_order) || 0
          ),
        0
      ) + 1;

    setScheduleSavingId(courseAssignmentId);
    setError('');
    setSuccess('');

    try {
      const payload = {
        course_assignment_id: courseAssignmentId,
        item_type: itemType,
        week_reading_assignment_id:
          itemType === 'reading'
            ? sourceId
            : null,
        course_event_id:
          itemType === 'event'
            ? sourceId
            : null,
        display_order: nextDisplayOrder,
        configuration_mode: mode
      };

      const result =
        await createAssignmentScheduleItem(payload);

      const created = result?.[0];

      if (!created?.id) {
        throw new Error(
          'The linked schedule item was not returned.'
        );
      }


      if (
        itemType === 'event' &&
        existing.length > 0
      ) {
        const insertionIndex =
          getEventInsertionIndex(
            existing,
            sourceId
          );


        /*
         * The event is initially created at the end.
         *
         * Only move it if its natural chronological
         * position is earlier in the current list.
         *
         * Existing items retain their relative order,
         * including any previous manual reordering.
         */
        if (
          insertionIndex <
          existing.length
        ) {
          const reordered = [
            ...existing
          ];

          reordered.splice(
            insertionIndex,
            0,
            {
              ...payload,
              ...created
            }
          );


          await Promise.all(
            reordered.map(
              (scheduleItem, index) => {
                const displayOrder =
                  index + 1;

                if (
                  Number(
                    scheduleItem
                      .display_order
                  ) ===
                    displayOrder
                ) {
                  return Promise.resolve();
                }

                return updateAssignmentScheduleItem(
                  scheduleItem.id,
                  {
                    display_order:
                      displayOrder
                  }
                );
              }
            )
          );
        }
      }


      await refreshDuplicateScheduleData();

      setScheduleSelections(prev => ({
        ...prev,
        [courseAssignmentId]: ''
      }));

      setSuccess(
        mode === 'choice'
          ? 'Choice option linked.'
          : 'Assignment anchor linked.'
      );
    } catch (err) {
      console.error(
        'Assignment schedule item add failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to link schedule item.'
      );
    } finally {
      setScheduleSavingId(null);
    }
  }

  async function handleRemoveScheduleItem(item) {
    const dependentChoices = getScheduleItemChoices(item);

    if (dependentChoices.length > 0) {
      setError(
        `This linked item already has ${dependentChoices.length} choice${dependentChoices.length === 1 ? '' : 's'}. Remove those choices first.`
      );
      return;
    }

    setScheduleSavingId(item.course_assignment_id);
    setError('');
    setSuccess('');

    try {
      await deleteAssignmentScheduleItem(item.id);

      await refreshDuplicateScheduleData();

      setSuccess('Schedule item removed.');
    } catch (err) {
      console.error(
        'Assignment schedule item removal failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to remove schedule item.'
      );
    } finally {
      setScheduleSavingId(null);
    }
  }

  async function handleMoveScheduleItem(
    courseAssignmentId,
    itemId,
    direction
  ) {
    const items =
      getAssignmentScheduleItems(courseAssignmentId);

    const index = items.findIndex(
      item => item.id === itemId
    );

    const targetIndex = index + direction;

    if (
      index < 0 ||
      targetIndex < 0 ||
      targetIndex >= items.length
    ) {
      return;
    }

    const current = items[index];
    const target = items[targetIndex];

    setScheduleSavingId(courseAssignmentId);
    setError('');
    setSuccess('');

    try {
      await Promise.all([
        updateAssignmentScheduleItem(
          current.id,
          {
            display_order:
              Number(target.display_order) ||
              targetIndex + 1
          }
        ),
        updateAssignmentScheduleItem(
          target.id,
          {
            display_order:
              Number(current.display_order) ||
              index + 1
          }
        )
      ]);

      await refreshDuplicateScheduleData();
    } catch (err) {
      console.error(
        'Assignment schedule item reorder failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to reorder schedule items.'
      );
    } finally {
      setScheduleSavingId(null);
    }
  }

  async function handleToggleScheduleSharing(
    assignmentTypeId,
    enabled
  ) {
    const typeAssignments = courseAssignments
      .filter(
        assignment =>
          String(assignment.assignment_type_id) ===
          String(assignmentTypeId)
      )
      .sort(
        (a, b) =>
          (Number(a.instance_number) || 0) -
            (Number(b.instance_number) || 0) ||
          String(a.id).localeCompare(String(b.id))
      );

    if (enabled && typeAssignments.length > 1) {
      const source = typeAssignments[0];
      const sourceLabel = assignmentInstanceLabel(source);

      if (
        !window.confirm(
          `Use one Schedule for all instances? ${sourceLabel} will become ` +
          'the shared Schedule. Linked items, capacities and group structure ' +
          'will be live-synced across instances. Student/group choices remain ' +
          'separate for each assignment instance. Existing student work can still block unsafe consolidation.'
        )
      ) {
        return;
      }
    }

    if (
      !enabled &&
      typeAssignments.length > 1 &&
      !window.confirm(
        'Stop sharing the Schedule across instances? Each instance will keep ' +
        'its current readings/events and groups as an independent snapshot. ' +
        'Student/group choices remain with their own assignment instance.'
      )
    ) {
      return;
    }

    setScheduleSharingSavingTypeId(assignmentTypeId);
    setError('');
    setSuccess('');

    try {
      await setAssignmentScheduleSharedMode(
        course.id,
        assignmentTypeId,
        enabled
      );

      setTypeSettings(prev => ({
        ...prev,
        [assignmentTypeId]: {
          ...prev[assignmentTypeId],
          course_id: course.id,
          assignment_type_id: assignmentTypeId,
          schedule_shared_across_instances: !!enabled
        }
      }));

      await refreshDuplicateScheduleData();

      setSuccess(
        enabled
          ? 'One Schedule is now shared across all assignment instances.'
          : 'Each assignment instance now has its own Schedule.'
      );
    } catch (err) {
      console.error('Shared Schedule setting save failed:', err);
      setError(
        err?.message ||
        'Failed to update shared Schedule mode.'
      );
    } finally {
      setScheduleSharingSavingTypeId(null);
    }
  }

  async function handleSaveScheduleTiming(assignmentTypeId) {
    const draft = scheduleTimingDrafts[assignmentTypeId] ||
      scheduleTimingDraftFromSettings(typeSettings[assignmentTypeId]);

    function toOffset(daysValue, direction) {
      if (daysValue === '' || daysValue === null || daysValue === undefined) {
        return null;
      }

      const magnitude = Math.abs(Number(daysValue));
      if (!Number.isFinite(magnitude)) return null;

      return direction === 'before' ? -magnitude : magnitude;
    }

    const openOffset = toOffset(draft.openDays, draft.openDirection);
    const dueOffset = toOffset(draft.dueDays, draft.dueDirection);

    if (
      openOffset !== null &&
      dueOffset !== null &&
      openOffset > dueOffset
    ) {
      setError('The relative open date cannot be after the relative due date.');
      return;
    }

    setScheduleTimingSavingTypeId(assignmentTypeId);
    setError('');
    setSuccess('');

    try {
      await upsertTypeSettings(
        course.id,
        assignmentTypeId,
        {
          schedule_open_offset_days: openOffset,
          schedule_due_offset_days: dueOffset
        }
      );

      setTypeSettings(prev => ({
        ...prev,
        [assignmentTypeId]: {
          ...prev[assignmentTypeId],
          course_id: course.id,
          assignment_type_id: assignmentTypeId,
          schedule_open_offset_days: openOffset,
          schedule_due_offset_days: dueOffset
        }
      }));

      setSuccess('Relative schedule dates saved.');
    } catch (err) {
      console.error('Relative schedule dates save failed:', err);
      setError(err?.message || 'Failed to save relative schedule dates.');
    } finally {
      setScheduleTimingSavingTypeId(null);
    }
  }


  function getChoiceUnits(assignment) {
    const usesGroups =
      assignment?.groups_enabled === true &&
      Number(assignment?.group_count) > 0;

    if (usesGroups) {
      return Array.from(
        {
          length:
            Math.max(
              0,
              Number(assignment.group_count) || 0
            )
        },
        (_, index) => index + 1
      ).map(groupNumber => {
        const memberIds = allGroupMemberships
          .filter(
            row =>
              row.course_assignment_id === assignment.id &&
              row.group_number === groupNumber
          )
          .map(row => row.enrolment_id);

        const memberNames = enrolments
          .filter(enrolment => memberIds.includes(enrolment.id))
          .map(enrolment =>
            enrolment.students?.english_name ||
            enrolment.students?.student_code ||
            'Student'
          );

        return {
          key: `group:${groupNumber}`,
          kind: 'group',
          groupNumber,
          label: assignmentGroupName(assignment.id, groupNumber),
          detail: memberNames.join(', ')
        };
      });
    }

    return enrolments.map(enrolment => ({
      key: `enrolment:${enrolment.id}`,
      kind: 'enrolment',
      enrolmentId: enrolment.id,
      label: studentLabel(enrolment),
      detail: ''
    }));
  }

  function choiceMatchesUnit(choice, unit) {
    if (!choice || !unit) return false;

    if (unit.kind === 'group') {
      return (
        Number(choice.group_number) === Number(unit.groupNumber) &&
        !choice.enrolment_id
      );
    }

    return String(choice.enrolment_id || '') ===
      String(unit.enrolmentId || '');
  }

  function getChoicesForAssignment(courseAssignmentId) {
    return assignmentScheduleSelections.filter(
      choice =>
        String(choice.course_assignment_id) ===
        String(courseAssignmentId)
    );
  }

  function getChoiceCountForUnit(assignment, unit) {
    return getChoicesForAssignment(assignment.id)
      .filter(choice => choiceMatchesUnit(choice, unit))
      .length;
  }

  function getChoiceOccupantLabel(choice) {
    if (choice.group_number) {
      return assignmentGroupName(
        choice.course_assignment_id,
        choice.group_number
      );
    }

    const enrolment = enrolments.find(
      item => item.id === choice.enrolment_id
    );

    return enrolment
      ? studentLabel(enrolment)
      : 'Student';
  }

  async function handleToggleScheduleChoiceMode(
    assignmentTypeId,
    enabled
  ) {
    const typeAssignments = courseAssignments
      .filter(
        assignment =>
          String(assignment.assignment_type_id) ===
          String(assignmentTypeId)
      )
      .sort(
        (a, b) =>
          (Number(a.instance_number) || 0) -
            (Number(b.instance_number) || 0) ||
          String(a.id).localeCompare(String(b.id))
      );

    const shouldShare =
      !!enabled && typeAssignments.length > 1;

    setScheduleChoiceSavingTypeId(assignmentTypeId);
    setError('');
    setSuccess('');

    try {
      if (
        !enabled &&
        typeSettings[assignmentTypeId]
          ?.schedule_shared_across_instances === true
      ) {
        await setAssignmentScheduleSharedMode(
          course.id,
          assignmentTypeId,
          false
        );
      }

      await upsertTypeSettings(
        course.id,
        assignmentTypeId,
        {
          schedule_choice_enabled: !!enabled,
          schedule_choice_required_count: 1
        }
      );

      if (shouldShare) {
        try {
          await setAssignmentScheduleSharedMode(
            course.id,
            assignmentTypeId,
            true
          );
        } catch (err) {
          await upsertTypeSettings(
            course.id,
            assignmentTypeId,
            {
              schedule_choice_enabled: false,
              schedule_choice_required_count: 1
            }
          );
          throw err;
        }
      } else if (
        typeSettings[assignmentTypeId]
          ?.schedule_shared_across_instances === true
      ) {
        await setAssignmentScheduleSharedMode(
          course.id,
          assignmentTypeId,
          false
        );
      }

      setTypeSettings(prev => ({
        ...prev,
        [assignmentTypeId]: {
          ...prev[assignmentTypeId],
          course_id: course.id,
          assignment_type_id: assignmentTypeId,
          schedule_choice_enabled: !!enabled,
          schedule_choice_required_count: 1,
          schedule_shared_across_instances: shouldShare
        }
      }));

      setScheduleChoiceDrafts(prev => ({
        ...prev,
        [assignmentTypeId]: {
          ...(prev[assignmentTypeId] ||
            scheduleChoiceDraftFromSettings(
              typeSettings[assignmentTypeId]
            )),
          requiredCount: '1'
        }
      }));

      await refreshDuplicateScheduleData();

      setSuccess(
        enabled
          ? shouldShare
            ? 'Choice enabled. All instances now draw from the same remembered pool; each instance still has its own choice.'
            : 'Choice enabled. The remembered choice pool is active.'
          : 'Choice disabled. The choice pool and saved choices are retained in the background; each instance now uses its remembered single linked item.'
      );
    } catch (err) {
      console.error(
        'Schedule choice mode save failed:',
        err
      );
      setError(
        err?.message ||
        'Failed to save choice mode.'
      );
    } finally {
      setScheduleChoiceSavingTypeId(null);
    }
  }

  async function handleSaveScheduleChoiceSettings(
    assignmentTypeId
  ) {
    const draft =
      scheduleChoiceDrafts[assignmentTypeId] ||
      scheduleChoiceDraftFromSettings(
        typeSettings[assignmentTypeId]
      );

    const requiredCount = 1;

    if (
      draft.openDate &&
      draft.closeDate &&
      draft.closeDate < draft.openDate
    ) {
      setError(
        'Choice close date cannot be before the open date.'
      );
      return;
    }

    setScheduleChoiceSavingTypeId(assignmentTypeId);
    setError('');
    setSuccess('');

    try {
      await upsertTypeSettings(
        course.id,
        assignmentTypeId,
        {
          schedule_choice_open_date:
            draft.openDate || null,
          schedule_choice_close_date:
            draft.closeDate || null,
          schedule_choice_required_count:
            requiredCount
        }
      );

      setTypeSettings(prev => ({
        ...prev,
        [assignmentTypeId]: {
          ...prev[assignmentTypeId],
          course_id: course.id,
          assignment_type_id: assignmentTypeId,
          schedule_choice_open_date:
            draft.openDate || null,
          schedule_choice_close_date:
            draft.closeDate || null,
          schedule_choice_required_count:
            requiredCount
        }
      }));

      setScheduleChoiceDrafts(prev => ({
        ...prev,
        [assignmentTypeId]: {
          ...draft,
          requiredCount: String(requiredCount)
        }
      }));

      setSuccess('Choice settings saved.');
    } catch (err) {
      console.error(
        'Schedule choice settings save failed:',
        err
      );
      setError(
        err?.message ||
        'Failed to save choice settings.'
      );
    } finally {
      setScheduleChoiceSavingTypeId(null);
    }
  }

  async function handleSaveScheduleItemCapacity(
    item,
    rawValue
  ) {
    const nextCapacity =
      rawValue === ''
        ? null
        : Math.max(1, Number(rawValue) || 1);

    const occupancy = getScheduleItemChoices(item).length;

    if (
      nextCapacity !== null &&
      nextCapacity < occupancy
    ) {
      setError(
        `Capacity cannot be lower than the ${occupancy} existing choice${occupancy === 1 ? '' : 's'}.`
      );
      return;
    }

    setScheduleSavingId(item.course_assignment_id);
    setError('');
    setSuccess('');

    try {
      await updateAssignmentScheduleItem(
        item.id,
        { capacity: nextCapacity }
      );

      await refreshDuplicateScheduleData();

      setSuccess('Capacity saved.');
    } catch (err) {
      console.error('Capacity save failed:', err);
      setError(
        err?.message ||
        'Failed to save capacity.'
      );
    } finally {
      setScheduleSavingId(null);
    }
  }

  async function handleToggleManualScheduleChoice(
    assignment,
    unit,
    item
  ) {
    if (!assignment || !unit || !item) return;

    const existing =
      assignmentScheduleSelections.find(
        choice =>
          choice.course_assignment_schedule_item_id === item.id &&
          choiceMatchesUnit(choice, unit)
      );

    setChoiceSavingAssignmentId(assignment.id);
    setError('');
    setSuccess('');

    try {
      if (existing) {
        await deleteAssignmentScheduleSelection(existing.id);

        await refreshDuplicateScheduleData();

        setSuccess('Choice removed.');
        return;
      }

      const settings =
        typeSettings[assignment.assignment_type_id] || {};
      const requiredCount =
        Math.max(
          1,
          Number(settings.schedule_choice_required_count) || 1
        );
      const currentUnitCount =
        getChoiceCountForUnit(assignment, unit);

      if (currentUnitCount >= requiredCount) {
        throw new Error(
          `${unit.label} already has the required ${requiredCount} choice${requiredCount === 1 ? '' : 's'}.`
        );
      }

      const occupancy = getScheduleItemChoices(item).length;
      const capacity =
        item.capacity === null ||
        item.capacity === undefined
          ? null
          : Number(item.capacity);

      if (
        capacity !== null &&
        occupancy >= capacity
      ) {
        throw new Error(
          'That item is already at capacity.'
        );
      }

      const payload = {
        course_assignment_id: assignment.id,
        course_assignment_schedule_item_id: item.id,
        enrolment_id:
          unit.kind === 'enrolment'
            ? unit.enrolmentId
            : null,
        group_number:
          unit.kind === 'group'
            ? unit.groupNumber
            : null,
        selected_via: 'instructor',
        selected_by_enrolment_id: null
      };

      const result =
        await createAssignmentScheduleSelection(payload);
      const created = result?.[0];

      if (!created?.id) {
        throw new Error(
          'The choice was not returned after saving.'
        );
      }

      await refreshDuplicateScheduleData();

      setSuccess('Choice assigned.');
    } catch (err) {
      console.error(
        'Manual schedule choice save failed:',
        err
      );
      setError(
        err?.message ||
        'Failed to update choice.'
      );
    } finally {
      setChoiceSavingAssignmentId(null);
    }
  }

  async function handleAssignManualScheduleChoice(
    assignment,
    unit,
    item
  ) {
    if (!assignment || !unit || !item) return;

    const currentChoices =
      getChoicesForAssignment(assignment.id)
        .filter(choice =>
          choiceMatchesUnit(choice, unit)
        );

    const sameChoice = currentChoices.find(
      choice =>
        String(choice.course_assignment_schedule_item_id) ===
        String(item.id)
    );

    if (sameChoice && currentChoices.length === 1) {
      setSuccess(
        `${unit.label} is already assigned to that option.`
      );
      return;
    }

    const occupancy = getScheduleItemChoices(item).length;
    const capacity =
      item.capacity === null ||
      item.capacity === undefined
        ? null
        : Number(item.capacity);

    if (
      !sameChoice &&
      capacity !== null &&
      occupancy >= capacity
    ) {
      setError('That option is already at capacity.');
      return;
    }

    setChoiceSavingAssignmentId(assignment.id);
    setError('');
    setSuccess('');

    try {
      let created = sameChoice;

      if (!sameChoice) {
        const result =
          await createAssignmentScheduleSelection({
            course_assignment_id: assignment.id,
            course_assignment_schedule_item_id: item.id,
            enrolment_id:
              unit.kind === 'enrolment'
                ? unit.enrolmentId
                : null,
            group_number:
              unit.kind === 'group'
                ? unit.groupNumber
                : null,
            selected_via: 'instructor',
            selected_by_enrolment_id: null
          });

        created = result?.[0];

        if (!created?.id) {
          throw new Error(
            'The assignment was not returned after saving.'
          );
        }
      }

      const removals = currentChoices.filter(
        choice =>
          String(choice.id) !== String(created.id)
      );

      await Promise.all(
        removals.map(choice =>
          deleteAssignmentScheduleSelection(choice.id)
        )
      );

      setChoiceItemByAssignment({});

      await refreshDuplicateScheduleData();
      setSuccess(
        `${unit.label} assigned to ${
          getScheduleSourceInfo(item).title
        }.`
      );
    } catch (err) {
      console.error(
        'Manual schedule assignment failed:',
        err
      );
      setError(
        err?.message ||
        'Failed to assign that option.'
      );
    } finally {
      setChoiceSavingAssignmentId(null);
    }
  }

  async function handleRemoveManualScheduleChoice(
    assignment,
    unit
  ) {
    if (!assignment || !unit) return;

    const currentChoices =
      getChoicesForAssignment(assignment.id)
        .filter(choice =>
          choiceMatchesUnit(choice, unit)
        );

    if (currentChoices.length === 0) return;

    setChoiceSavingAssignmentId(assignment.id);
    setError('');
    setSuccess('');

    try {
      await Promise.all(
        currentChoices.map(choice =>
          deleteAssignmentScheduleSelection(choice.id)
        )
      );
      await refreshDuplicateScheduleData();
      setSuccess(`${unit.label}'s assignment removed.`);
    } catch (err) {
      console.error(
        'Removing manual schedule assignment failed:',
        err
      );
      setError(
        err?.message ||
        'Failed to remove that assignment.'
      );
    } finally {
      setChoiceSavingAssignmentId(null);
    }
  }

  async function handleToggleScheduleMode(
    assignmentTypeId,
    enabled
  ) {
    setScheduleModeSavingTypeId(assignmentTypeId);
    setError('');
    setSuccess('');

    const scheduleTargetType =
      assignmentTypes.find(
        type =>
          String(type.id) ===
          String(assignmentTypeId)
      );

    const structuredSchedule =
      scheduleTargetType?.assignment_kind ===
      'structured_form';

    if (structuredSchedule) {
      try {
        await upsertTypeSettings(
          course.id,
          assignmentTypeId,
          {
            schedule_enabled: !!enabled
          }
        );

        setTypeSettings(prev => ({
          ...prev,
          [assignmentTypeId]: {
            ...prev[assignmentTypeId],
            course_id: course.id,
            assignment_type_id:
              assignmentTypeId,
            schedule_enabled: !!enabled
          }
        }));

        setTypeTabs(prev => ({
          ...prev,
          [assignmentTypeId]: 'dates'
        }));

        setSuccess(
          `Schedule ${enabled ? 'enabled' : 'disabled'}.`
        );
      } catch (err) {
        console.error(
          'Structured Form Schedule setting save failed:',
          err
        );

        setError(
          err?.message ||
          'Failed to save Schedule setting.'
        );
      } finally {
        setScheduleModeSavingTypeId(null);
      }

      return;
    }

    const typeAssignments = courseAssignments.filter(
      assignment =>
        String(assignment.assignment_type_id) ===
        String(assignmentTypeId)
    );

    const choiceEnabled =
      typeSettings[assignmentTypeId]
        ?.schedule_choice_enabled === true;

    try {
      if (
        (
          !enabled ||
          !choiceEnabled ||
          typeAssignments.length <= 1
        ) &&
        typeSettings[assignmentTypeId]
          ?.schedule_shared_across_instances === true
      ) {
        await setAssignmentScheduleSharedMode(
          course.id,
          assignmentTypeId,
          false
        );
      }

      await upsertTypeSettings(
        course.id,
        assignmentTypeId,
        {
          schedule_enabled: !!enabled
        }
      );

      const shouldShare =
        !!enabled &&
        choiceEnabled &&
        typeAssignments.length > 1;

      if (shouldShare) {
        await setAssignmentScheduleSharedMode(
          course.id,
          assignmentTypeId,
          true
        );
      }

      setTypeSettings(prev => ({
        ...prev,
        [assignmentTypeId]: {
          ...prev[assignmentTypeId],
          course_id: course.id,
          assignment_type_id: assignmentTypeId,
          schedule_enabled: !!enabled,
          schedule_shared_across_instances:
            shouldShare
        }
      }));

      if (!enabled || shouldShare) {
        await refreshDuplicateScheduleData();
      }

      setTypeTabs(prev => ({
        ...prev,
        [assignmentTypeId]: enabled ? 'schedule' : 'dates'
      }));

      setSuccess(
        `Schedule ${enabled ? 'enabled' : 'disabled'}.`
      );
    } catch (err) {
      console.error(
        'Schedule setting save failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to save Schedule setting.'
      );
    } finally {
      setScheduleModeSavingTypeId(null);
    }
  }

  async function handleToggleGroupDeadlines(
    assignmentTypeId,
    enabled
  ) {
    setError('');
    setSuccess('');

    try {
      await upsertTypeSettings(
        course.id,
        assignmentTypeId,
        {
          group_deadlines_enabled: !!enabled
        }
      );

      setTypeSettings(prev => ({
        ...prev,
        [assignmentTypeId]: {
          ...prev[assignmentTypeId],
          group_deadlines_enabled: !!enabled
        }
      }));

      setSuccess(
        `Group deadlines ${enabled ? 'enabled' : 'disabled'}.`
      );
    } catch (err) {
      console.error(
        'Group-deadline setting save failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to save group-deadline setting.'
      );
    }
  }

  async function handleGroupDeadlineDateChange(
    assignmentId,
    groupNumber,
    field,
    value
  ) {
    const deadline =
      groupDeadlines[
        assignmentId
      ]?.[
        groupNumber
      ] || {};

    const normalized =
      value || null;

    try {
      const result =
        await upsertGroupDeadline(
          assignmentId,
          groupNumber,
          {
            [field]:
              normalized
          }
        );

      const saved =
        result?.[0] || {
          ...deadline,
          course_assignment_id:
            assignmentId,
          group_number:
            groupNumber,
          [field]:
            normalized
        };

      setGroupDeadlines(
        prev => ({
          ...prev,

          [assignmentId]: {
            ...prev[
              assignmentId
            ],

            [groupNumber]:
              saved
          }
        })
      );

    } catch (err) {
      console.error(
        'Group deadline date save failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to save group deadline.'
      );
    }
  }


  async function handleToggleAiDisclosure(
    assignmentTypeId,
    enabled
  ) {
    setAiDisclosureSavingTypeId(assignmentTypeId);
    setError('');
    setSuccess('');

    try {
      await upsertAiDisclosureSetting(
        course.id,
        assignmentTypeId,
        enabled
      );

      setAiDisclosureSettings(prev => ({
        ...prev,
        [assignmentTypeId]: !!enabled
      }));

      setSuccess(
        `AI Disclosure ${enabled ? 'enabled' : 'disabled'}.`
      );
    } catch (err) {
      console.error('AI Disclosure setting save failed:', err);
      setError(
        err?.message ||
        'Failed to save AI Disclosure setting.'
      );
    } finally {
      setAiDisclosureSavingTypeId(null);
    }
  }

  function activeWeightedTypeIds() {
    const ids = new Set();

    assignmentTypes.forEach(type => {
      if (
        type.is_core &&
        CORE_NAMES.includes(type.name)
      ) {
        const courseField = COURSE_FIELD[type.name];

        if (course?.[courseField] !== false) {
          ids.add(type.id);
        }
      }
    });

    courseAssignments.forEach(assignment => {
      const type = assignmentTypes.find(
        item =>
          item.id === assignment.assignment_type_id
      );

      if (
        type &&
        !type.is_core &&
        !CORE_NAMES.includes(type.name) &&
        type.requires_submission !== false
      ) {
        ids.add(assignment.assignment_type_id);
      }
    });

    return ids;
  }

  function currentWeightTotal() {
    const activeIds = activeWeightedTypeIds();

    return assignmentWeights
      .filter(row =>
        activeIds.has(row.assignment_type_id)
      )
      .reduce(
        (sum, row) =>
          sum +
          (Number(row.weight_percent) || 0),
        0
      );
  }

  async function handleSaveWeight(typeId) {
    const raw = weightDrafts[typeId];
    const nextValue =
      raw === '' || raw === null
        ? 0
        : Number(raw);

    if (
      !Number.isFinite(nextValue) ||
      nextValue < 0 ||
      nextValue > 100
    ) {
      setError(
        'Assignment weight must be between 0 and 100.'
      );
      return;
    }

    const currentRow = assignmentWeights.find(
      row => row.assignment_type_id === typeId
    );

    const currentValue =
      Number(currentRow?.weight_percent) || 0;

    const nextTotal =
      currentWeightTotal() -
      currentValue +
      nextValue;

    if (nextTotal > 100.000001) {
      setError(
        `Assignment weights cannot exceed 100%. The new total would be ${nextTotal}%.`
      );
      return;
    }

    setWeightSavingTypeId(typeId);
    setError('');
    setSuccess('');

    try {
      if (nextValue === 0) {
        await deleteAssignmentWeight(
          course.id,
          typeId
        );

        setAssignmentWeights(prev =>
          prev.filter(
            row =>
              row.assignment_type_id !==
              typeId
          )
        );

        setWeightDrafts(prev => ({
          ...prev,
          [typeId]: ''
        }));
      } else {
        const result =
          await saveAssignmentWeight(
            course.id,
            typeId,
            nextValue
          );

        const saved = result?.[0] || {
          course_id: course.id,
          assignment_type_id: typeId,
          weight_percent: nextValue
        };

        setAssignmentWeights(prev => [
          ...prev.filter(
            row =>
              row.assignment_type_id !==
              typeId
          ),
          saved
        ]);

        setWeightDrafts(prev => ({
          ...prev,
          [typeId]:
            saved.weight_percent ?? nextValue
        }));
      }

      setSuccess('Assignment weight saved.');
    } catch (err) {
      console.error(
        'Assignment weight save failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to save assignment weight.'
      );
    } finally {
      setWeightSavingTypeId(null);
    }
  }

  async function handleToggleWeek(
    weekId,
    field,
    currentValue
  ) {
    try {
      await updateWeek(weekId, { [field]: !currentValue });

      setWeeks(prev => prev.map(week =>
        week.id === weekId
          ? { ...week, [field]: !currentValue }
          : week
      ));
    } catch (err) {
      console.error('Week update failed:', err);
      setError(err?.message || 'Failed to update week.');
    }
  }

  async function handleAddAssignment(event) {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!addingTypeId) {
      setError('Select an assignment type.');
      return;
    }

    if (
      !Number.isInteger(Number(addingCount)) ||
      Number(addingCount) < 1 ||
      Number(addingCount) > 10
    ) {
      setError(
        'Number of instances must be between 1 and 10.'
      );
      return;
    }

    const type = assignmentTypes.find(
      item => item.id === addingTypeId
    );

    if (!type || type.is_course_copy === true) {
      setError(
        'Select an Assignment Library type.'
      );
      return;
    }

    if (!session?.cmsToken) {
      setError(
        'Your instructor session is invalid or has expired.'
      );
      return;
    }

    setSaving(true);

    try {
      const created =
        await createCourseAssignmentConfiguration(
          session.cmsToken,
          course.id,
          addingTypeId,
          Number(addingCount)
        );

      const [
        refreshedTypes,
        refreshedAssignments
      ] = await Promise.all([
        fetchAssignmentTypes(course.id),
        fetchCourseAssignments(course.id)
      ]);

      setAssignmentTypes(
        Array.isArray(refreshedTypes)
          ? refreshedTypes
          : []
      );

      setCourseAssignments(
        Array.isArray(refreshedAssignments)
          ? refreshedAssignments
          : []
      );

      const createdTypeId =
        created?.assignment_type?.id || null;

      if (createdTypeId) {
        setExpandedTypeId(createdTypeId);

        setTypeTabs(prev => ({
          ...prev,
          [createdTypeId]: 'description'
        }));
      }

      setAddingTypeId('');
      setAddingCount(1);

      setSuccess(
        `${created?.assignment_type?.name || type.name} added.`
      );
    } catch (err) {
      console.error(
        'Add configured assignment failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to add assignment.'
      );
    } finally {
      setSaving(false);
    }
  }


  async function handleSaveInstanceCount(
    assignmentTypeId,
    rawCount
  ) {
    const targetCount =
      Number(rawCount);

    if (
      !Number.isInteger(targetCount) ||
      targetCount < 1 ||
      targetCount > 10
    ) {
      setError(
        'Number of instances must be between 1 and 10.'
      );
      return;
    }

    const currentAssignments =
      courseAssignments
        .filter(
          assignment =>
            String(
              assignment.assignment_type_id
            ) ===
            String(assignmentTypeId)
        )
        .slice()
        .sort(
          (a, b) =>
            (
              Number(a.instance_number) ||
              0
            ) -
              (
                Number(b.instance_number) ||
                0
              ) ||
            String(a.id).localeCompare(
              String(b.id)
            )
        );

    const currentCount =
      currentAssignments.length;

    if (targetCount === currentCount) {
      setInstanceCountDrafts(prev => ({
        ...prev,
        [assignmentTypeId]:
          String(targetCount)
      }));
      setSuccess(
        'Number of instances is unchanged.'
      );
      return;
    }

    const type =
      assignmentTypes.find(
        item =>
          String(item.id) ===
          String(assignmentTypeId)
      );

    if (!type) {
      setError(
        'Assignment type not found.'
      );
      return;
    }

    setInstanceCountSavingTypeId(
      assignmentTypeId
    );
    setError('');
    setSuccess('');

    try {
      if (targetCount > currentCount) {
        const highestInstance =
          currentAssignments.reduce(
            (highest, assignment) =>
              Math.max(
                highest,
                Number(
                  assignment.instance_number
                ) || 0
              ),
            0
          );

        const createdIds = [];

        try {
          for (
            let index = 0;
            index <
              targetCount - currentCount;
            index += 1
          ) {
            const result =
              await saveCourseAssignment({
                course_id: course.id,
                assignment_type_id:
                  assignmentTypeId,
                instance_number:
                  highestInstance +
                  index +
                  1,
                open_date: null,
                due_date: null,
                occurs_on: null,
                word_limit: null,
                groups_enabled: false,
                group_count: 0
              });

            const created =
              result?.[0];

            if (!created?.id) {
              throw new Error(
                'A new assignment instance was not returned after saving.'
              );
            }

            createdIds.push(created.id);
          }
        } catch (err) {
          for (
            const createdId of
            createdIds.reverse()
          ) {
            try {
              await deleteCourseAssignment(
                createdId
              );
            } catch (
              rollbackError
            ) {
              console.error(
                'Instance-count rollback failed:',
                rollbackError
              );
            }
          }

          throw err;
        }
      } else {
        const toRemove =
          currentAssignments
            .slice(targetCount)
            .reverse();

        const blocked = [];

        for (
          const assignment of
          toRemove
        ) {
          if (
            await assignmentHasStudentRecords(
              assignment.id
            )
          ) {
            blocked.push(
              assignmentInstanceLabel(
                assignment
              )
            );
          }
        }

        if (blocked.length > 0) {
          throw new Error(
            'Cannot reduce the number of instances because student work already exists for: ' +
            blocked.join(', ') +
            '.'
          );
        }

        const removedLabels =
          toRemove.map(
            assignment =>
              assignmentInstanceLabel(
                assignment
              )
          );

        const confirmed =
          window.confirm(
            'Reduce this assignment from ' +
            currentCount +
            ' to ' +
            targetCount +
            ' instance' +
            (
              targetCount === 1
                ? ''
                : 's'
            ) +
            '? This will remove ' +
            removedLabels.join(', ') +
            ' and their dates, Schedule links, groups, and other instance-specific settings.'
          );

        if (!confirmed) {
          return;
        }

        for (
          const assignment of
          toRemove
        ) {
          await deleteAssignmentConfiguration(
            assignment.id
          );

          await deleteCourseAssignment(
            assignment.id
          );
        }

        if (
          toRemove.some(
            assignment =>
              String(
                assignment.id
              ) ===
              String(
                groupModalAssignmentId
              )
          )
        ) {
          setGroupModalAssignmentId(
            null
          );
          setGroupMemberships([]);
          setSelectedGroupStudents([]);
        }
      }

      const settings =
        typeSettings[
          assignmentTypeId
        ] || {};

      const shouldShare =
        settings.schedule_enabled ===
          true &&
        settings.schedule_choice_enabled ===
          true &&
        settings
          .schedule_shared_across_instances ===
          true &&
        targetCount > 1;

      if (shouldShare) {
        await setAssignmentScheduleSharedMode(
          course.id,
          assignmentTypeId,
          true
        );
      } else if (
        settings
          .schedule_shared_across_instances ===
        true
      ) {
        await setAssignmentScheduleSharedMode(
          course.id,
          assignmentTypeId,
          false
        );

        setTypeSettings(prev => ({
          ...prev,
          [assignmentTypeId]: {
            ...prev[assignmentTypeId],
            schedule_shared_across_instances:
              false
          }
        }));
      }

      await refreshDuplicateScheduleData();

      setInstanceCountDrafts(prev => ({
        ...prev,
        [assignmentTypeId]:
          String(targetCount)
      }));

      setSuccess(
        type.name +
        ' now has ' +
        targetCount +
        ' instance' +
        (
          targetCount === 1
            ? ''
            : 's'
        ) +
        '.'
      );
    } catch (err) {
      console.error(
        'Instance-count update failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to update the number of assignment instances.'
      );
    } finally {
      setInstanceCountSavingTypeId(
        null
      );
    }
  }

  async function handleUpdateAssignment(id, data) {
    setError('');
    setSuccess('');

    try {
      const result = await updateCourseAssignment(id, data);
      const returned = result[0] || {};

      setCourseAssignments(prev => prev.map(assignment =>
        assignment.id === id
          ? {
              ...assignment,
              ...returned,
              ...data,
              assignment_types: assignment.assignment_types
            }
          : assignment
      ));

      if (
        Object.prototype.hasOwnProperty.call(data, 'groups_enabled') ||
        Object.prototype.hasOwnProperty.call(data, 'group_count')
      ) {
        await refreshDuplicateScheduleData();
      }

      return {
        ...data,
        ...returned
      };
    } catch (err) {
      console.error('Assignment update failed:', err);
      setError(err?.message || 'Failed to update assignment.');
      throw err;
    }
  }

  async function handleRemoveAssignmentTypeFromCourse(
    assignmentTypeId
  ) {
    const assignments =
      courseAssignments
        .filter(
          assignment =>
            String(
              assignment.assignment_type_id
            ) ===
            String(assignmentTypeId)
        )
        .slice()
        .sort(
          (a, b) =>
            (
              Number(a.instance_number) ||
              0
            ) -
            (
              Number(b.instance_number) ||
              0
            )
        );

    if (assignments.length === 0) {
      setError(
        'No course assignments were found to remove.'
      );
      return;
    }

    const type =
      assignmentTypes.find(
        item =>
          String(item.id) ===
          String(assignmentTypeId)
      );

    const typeName =
      type?.name ||
      assignments[0]?.assignment_types?.name ||
      'Assignment';

    const blocked = [];

    setRemovingTypeId(
      assignmentTypeId
    );
    setError('');
    setSuccess('');

    try {
      for (
        const assignment of
        assignments
      ) {
        if (
          await assignmentHasStudentRecords(
            assignment.id
          )
        ) {
          blocked.push(
            assignmentInstanceLabel(
              assignment
            )
          );
        }
      }

      if (blocked.length > 0) {
        throw new Error(
          'This assignment cannot be removed because student submission or completion records already exist for: ' +
          blocked.join(', ') +
          '.'
        );
      }

      const confirmed =
        window.confirm(
          assignments.length === 1
            ? `Remove "${typeName}" from this course? Course-specific dates, Schedule links, groups, settings and description will also be removed.`
            : `Remove "${typeName}" and all ${assignments.length} instances from this course? Course-specific dates, Schedule links, groups, settings and description will also be removed.`
        );

      if (!confirmed) {
        return;
      }

      const ids =
        assignments.map(
          assignment =>
            String(assignment.id)
        );

      const idSet =
        new Set(ids);

      /*
       * Stop shared-instance mode first so follower snapshots are
       * detached cleanly before any source assignment disappears.
       */
      if (
        typeSettings[assignmentTypeId]
          ?.schedule_shared_across_instances ===
        true
      ) {
        await setAssignmentScheduleSharedMode(
          course.id,
          assignmentTypeId,
          false
        );
      }

      /*
       * Break Duplicate Schedule links both where an assignment
       * being removed is a follower and where it is the source for
       * an assignment that is staying in the course.
       */
      for (
        const link of
        assignmentScheduleDuplicates
      ) {
        const followerId =
          String(
            link.course_assignment_id ||
            ''
          );
        const sourceId =
          String(
            link.source_course_assignment_id ||
            ''
          );

        if (
          idSet.has(followerId)
        ) {
          await setAssignmentScheduleDuplicate(
            followerId,
            null
          );
        } else if (
          idSet.has(sourceId)
        ) {
          await setAssignmentScheduleDuplicate(
            followerId,
            null
          );
        }
      }

      await clearFeedbackReferencesToAssignments(
        ids
      );

      /*
       * Remove highest-numbered instances first. That keeps the
       * operation predictable if the database has ordering-related
       * constraints.
       */
      for (
        const assignment of
        [...assignments].reverse()
      ) {
        await deleteAssignmentConfiguration(
          assignment.id
        );

        await deleteCourseAssignment(
          assignment.id
        );
      }

      await deleteCourseAssignmentTypeData(
        course.id,
        assignmentTypeId
      );

      setCourseAssignments(prev =>
        prev.filter(
          assignment =>
            String(
              assignment.assignment_type_id
            ) !==
            String(assignmentTypeId)
        )
      );

      setAssignmentWeights(prev =>
        prev.filter(
          row =>
            String(
              row.assignment_type_id
            ) !==
            String(assignmentTypeId)
        )
      );

      setWeightDrafts(prev => {
        const next = { ...prev };
        delete next[assignmentTypeId];
        return next;
      });

      setTypeSettings(prev => {
        const next = { ...prev };
        delete next[assignmentTypeId];
        return next;
      });

      setTypeDescriptions(prev => {
        const next = { ...prev };
        delete next[assignmentTypeId];
        return next;
      });

      setInstanceCountDrafts(prev => {
        const next = { ...prev };
        delete next[assignmentTypeId];
        return next;
      });

      setExpandedTypeId(current =>
        String(current) ===
        String(assignmentTypeId)
          ? null
          : current
      );

      if (
        ids.includes(
          String(
            groupModalAssignmentId
          )
        )
      ) {
        setGroupModalAssignmentId(
          null
        );
        setGroupMemberships([]);
        setSelectedGroupStudents([]);
      }

      await refreshDuplicateScheduleData();

      setSuccess(
        `${typeName} removed from this course.`
      );
    } catch (err) {
      console.error(
        'Assignment type removal failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to remove assignment from the course.'
      );
    } finally {
      setRemovingTypeId(
        null
      );
    }
  }

  async function handleDeleteAssignment(id) {
    const assignment = courseAssignments.find(
      item => item.id === id
    );

    if (!assignment) {
      setError('Assignment not found.');
      return;
    }

    if (
      !window.confirm(
        'Remove this assignment from the course?'
      )
    ) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const hasStudentRecords =
        await assignmentHasStudentRecords(id);

      if (hasStudentRecords) {
        throw new Error(
          'This assignment cannot be removed because student submission or completion records already exist.'
        );
      }

      await deleteAssignmentConfiguration(id);
      await deleteCourseAssignment(id);

      const sameTypeRemaining =
        courseAssignments.filter(
          item =>
            item.id !== id &&
            item.assignment_type_id ===
              assignment.assignment_type_id
        );

      if (sameTypeRemaining.length === 0) {
        await deleteAssignmentWeight(
          course.id,
          assignment.assignment_type_id
        );

        setAssignmentWeights(prev =>
          prev.filter(
            row =>
              row.assignment_type_id !==
              assignment.assignment_type_id
          )
        );

        setWeightDrafts(prev => {
          const next = { ...prev };
          delete next[
            assignment.assignment_type_id
          ];
          return next;
        });
      }

      setCourseAssignments(prev =>
        prev.filter(item => item.id !== id)
      );

      if (groupModalAssignmentId === id) {
        setGroupModalAssignmentId(null);
        setGroupMemberships([]);
        setSelectedGroupStudents([]);
      }

      setSuccess('Assignment removed.');
    } catch (err) {
      console.error(
        'Assignment removal failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to remove assignment.'
      );
    }
  }

  function toggleTypeExpanded(typeId) {
    setExpandedTypeId(current =>
      current === typeId ? null : typeId
    );

    setTypeTabs(prev => ({
      ...prev,
      [typeId]: prev[typeId] || 'description'
    }));

    setError('');
    setSuccess('');
  }

  function setTypeTab(typeId, tab) {
    setTypeTabs(prev => ({
      ...prev,
      [typeId]: tab
    }));
  }

  async function handleGradePublicationModeChange(
    assignment,
    nextMode
  ) {
    const currentMode =
      assignment
        ?.grade_publication_mode ||
      'immediate';

    if (
      currentMode === 'manual' &&
      nextMode === 'immediate'
    ) {
      const checkResponse =
        await fetch(
          SUPABASE_URL +
            '/rest/v1/rpc/course_assignment_has_unpublished_grades',
          {
            method: 'POST',
            headers: apiHeaders({
              'Content-Type':
                'application/json'
            }),
            body: JSON.stringify({
              p_course_assignment_id:
                assignment.id
            })
          }
        );

      const hasUnpublished =
        await readJson(
          checkResponse,
          'Checking unpublished grades'
        );

      if (
        hasUnpublished === true &&
        !window.confirm(
          'Changing to Immediate Grade Publication will publish all current draft grades. Are you sure?'
        )
      ) {
        return;
      }
    }

    const response =
      await fetch(
        SUPABASE_URL +
          '/rest/v1/rpc/set_course_assignment_grade_publication_mode',
        {
          method: 'POST',
          headers: apiHeaders({
            'Content-Type':
              'application/json'
          }),
          body: JSON.stringify({
            p_course_assignment_id:
              assignment.id,
            p_mode:
              nextMode
          })
        }
      );

    await readJson(
      response,
      'Updating grade publication'
    );

    setCourseAssignments(
      current =>
        current.map(
          row =>
            String(row.id) ===
              String(assignment.id)
              ? {
                  ...row,
                  grade_publication_mode:
                    nextMode
                }
              : row
        )
    );
  }


  async function handleToggleGroups(assignment, enabled) {
    if (!enabled && assignment.groups_enabled) {
      const confirmed = window.confirm(
        'Turn groups off for this assignment? Existing group memberships will be cleared.'
      );

      if (!confirmed) return;
    }

    const nextGroupCount = enabled
      ? Math.max(2, Number(assignment.group_count) || 0)
      : 0;

    await handleUpdateAssignment(
      assignment.id,
      {
        groups_enabled: enabled,
        group_count: nextGroupCount
      }
    );

    if (!enabled && groupModalAssignmentId === assignment.id) {
      setGroupModalAssignmentId(null);
      setGroupMemberships([]);
      setSelectedGroupStudents([]);
    }
  }

  async function handleChangeGroupCount(
    assignment,
    nextGroupCount
  ) {
    const current = Number(assignment.group_count) || 0;

    if (
      nextGroupCount < current &&
      !window.confirm(
        'Reducing the number of groups will make students in removed groups unassigned. Continue?'
      )
    ) {
      return;
    }

    await handleUpdateAssignment(
      assignment.id,
      { group_count: nextGroupCount }
    );

    if (groupModalAssignmentId === assignment.id) {
      const refreshed = await fetchGroupMemberships(
        assignment.id
      );
      setGroupMemberships(refreshed);
      setSelectedGroupStudents([]);
    }
  }

  async function openGroupManager(assignmentId) {
    setGroupModalAssignmentId(assignmentId);
    setSelectedGroupStudents([]);
    setGroupLoading(true);
    setError('');

    try {
      const [rows, allRows] = await Promise.all([
        fetchGroupMemberships(assignmentId),
        fetchAllGroupMemberships(course.id)
      ]);

      setGroupMemberships(rows);
      setAllGroupMemberships(
        Array.isArray(allRows) ? allRows : []
      );
    } catch (err) {
      console.error('Loading groups failed:', err);
      setError(err?.message || 'Failed to load groups.');
      setGroupModalAssignmentId(null);
    } finally {
      setGroupLoading(false);
    }
  }

  async function addSelectedStudentsToGroup(groupNumber) {
    if (
      !groupModalAssignmentId ||
      selectedGroupStudents.length === 0
    ) {
      return;
    }

    setGroupSaving(true);
    setError('');

    try {
      await upsertGroupMemberships(
        groupModalAssignmentId,
        selectedGroupStudents,
        groupNumber
      );

      await refreshDuplicateScheduleData();
      setSelectedGroupStudents([]);
    } catch (err) {
      console.error('Adding students to group failed:', err);
      setError(err?.message || 'Failed to add students to group.');
    } finally {
      setGroupSaving(false);
    }
  }

  async function removeStudentFromGroup(enrolmentId) {
    if (!groupModalAssignmentId) return;

    setGroupSaving(true);
    setError('');

    try {
      await removeGroupMembership(
        groupModalAssignmentId,
        enrolmentId
      );

      await refreshDuplicateScheduleData();
    } catch (err) {
      console.error('Removing student from group failed:', err);
      setError(err?.message || 'Failed to remove student from group.');
    } finally {
      setGroupSaving(false);
    }
  }

  function getAssignmentLabel(assignment) {
    if (!assignment) {
      return 'Assignment';
    }

    const type =
      assignmentTypes.find(
        item =>
          String(item.id) ===
          String(
            assignment.assignment_type_id
          )
      );

    const sameType =
      courseAssignments.filter(
        item =>
          String(
            item.assignment_type_id
          ) ===
          String(
            assignment.assignment_type_id
          )
      );

    return resolveAssignmentInstanceLabel(
      assignment,
      {
        baseName:
          type?.name ||
          'Assignment',
        sameTypeCount:
          sameType.length
      }
    );
  }

  function buildGroupCopySources(targetAssignmentId) {
    const sources = [];

    const streams = [
      ...new Set(
        enrolments
          .map(enrolment => enrolment.stream)
          .filter(
            value =>
              value !== null &&
              value !== undefined
          )
      )
    ].sort((a, b) => Number(a) - Number(b));

    streams.forEach(stream => {
      const streamMembers = enrolments.filter(
        enrolment =>
          Number(enrolment.stream) === Number(stream)
      );

      sources.push({
        key: `stream-${stream}`,
        category: 'Streams',
        label: `${streamName(stream)} — all students`,
        enrolmentIds: streamMembers.map(
          enrolment => enrolment.id
        )
      });

      const streamGroups = [
        ...new Set(
          streamMembers
            .map(enrolment => enrolment.group_number)
            .filter(
              value =>
                value !== null &&
                value !== undefined
            )
        )
      ].sort((a, b) => Number(a) - Number(b));

      streamGroups.forEach(groupNumber => {
        sources.push({
          key: `stream-${stream}-group-${groupNumber}`,
          category: 'Streams',
          label:
            `${streamName(stream)} — ${courseGroupName(stream, groupNumber)}`,
          enrolmentIds: streamMembers
            .filter(
              enrolment =>
                Number(enrolment.group_number) ===
                Number(groupNumber)
            )
            .map(enrolment => enrolment.id)
        });
      });
    });

    const activeEnrolmentIds = new Set(
      enrolments.map(enrolment => enrolment.id)
    );

    courseAssignments
      .filter(
        assignment =>
          assignment.id !== targetAssignmentId &&
          assignment.groups_enabled &&
          Number(assignment.group_count) > 0
      )
      .forEach(assignment => {
        for (
          let groupNumber = 1;
          groupNumber <= Number(assignment.group_count);
          groupNumber += 1
        ) {
          const ids = allGroupMemberships
            .filter(
              row =>
                row.course_assignment_id === assignment.id &&
                Number(row.group_number) === groupNumber &&
                activeEnrolmentIds.has(row.enrolment_id)
            )
            .map(row => row.enrolment_id);

          sources.push({
            key:
              `assignment-${assignment.id}-group-${groupNumber}`,
            category: 'Assignment Groups',
            label:
              `${getAssignmentLabel(assignment)} — ${assignmentGroupName(
                assignment.id,
                groupNumber
              )}`,
            enrolmentIds: ids
          });
        }
      });

    return sources;
  }

  async function copySourceIntoAssignmentGroup(
    groupNumber,
    source
  ) {
    if (!groupModalAssignmentId) return;

    const sourceIds = [
      ...new Set(source.enrolmentIds || [])
    ];
    const sourceIdSet = new Set(sourceIds);

    const existingTargetIds = groupMemberships
      .filter(
        row =>
          Number(row.group_number) === Number(groupNumber)
      )
      .map(row => row.enrolment_id);

    const movedFromOtherGroups = sourceIds.filter(
      enrolmentId => {
        const existing = groupMemberships.find(
          row => row.enrolment_id === enrolmentId
        );

        return (
          existing &&
          Number(existing.group_number) !== Number(groupNumber)
        );
      }
    ).length;

    const confirmed = window.confirm(
      `Replace ${assignmentGroupName(
        groupModalAssignmentId,
        groupNumber
      )}'s current membership ` +
      `(${existingTargetIds.length}) with ${source.label} ` +
      `(${sourceIds.length})?` +
      (
        movedFromOtherGroups
          ? ` ${movedFromOtherGroups} student${
              movedFromOtherGroups === 1 ? '' : 's'
            } will be moved from another group in this assignment.`
          : ''
      )
    );

    if (!confirmed) return;

    setGroupSaving(true);
    setError('');
    setSuccess('');

    try {
      if (sourceIds.length > 0) {
        await upsertGroupMemberships(
          groupModalAssignmentId,
          sourceIds,
          groupNumber
        );
      }

      const removals = existingTargetIds.filter(
        enrolmentId => !sourceIdSet.has(enrolmentId)
      );

      await Promise.all(
        removals.map(enrolmentId =>
          removeGroupMembership(
            groupModalAssignmentId,
            enrolmentId
          )
        )
      );

      await refreshDuplicateScheduleData();
      setSelectedGroupStudents([]);
      setSuccess(
        `${assignmentGroupName(
          groupModalAssignmentId,
          groupNumber
        )} copied from ${source.label}.`
      );
    } catch (err) {
      console.error(
        'Copying assignment group failed:',
        err
      );
      setError(
        err?.message ||
        'Failed to copy assignment group membership.'
      );
      throw err;
    } finally {
      setGroupSaving(false);
    }
  }

  async function applyGroupSetupToAllInstances(
    sourceAssignment
  ) {
    if (
      !sourceAssignment?.groups_enabled ||
      Number(sourceAssignment.group_count) < 1
    ) {
      return;
    }

    const targets = courseAssignments.filter(
      assignment =>
        assignment.assignment_type_id ===
          sourceAssignment.assignment_type_id &&
        assignment.id !== sourceAssignment.id &&
        !getAssignmentScheduleDuplicate(assignment.id)
    );

    if (targets.length === 0) return;

    setApplyingGroupSetupId(sourceAssignment.id);
    setError('');
    setSuccess('');

    try {
      const sourceMemberships =
        await fetchGroupMemberships(sourceAssignment.id);

      const sourceLabel =
        getAssignmentLabel(sourceAssignment);

      const confirmed = window.confirm(
        `Apply the group setup from ${sourceLabel} to ` +
        `${targets.length} other assignment instance${
          targets.length === 1 ? '' : 's'
        }? This will turn groups on for those instances, set each to ` +
        `${sourceAssignment.group_count} group${
          Number(sourceAssignment.group_count) === 1 ? '' : 's'
        }, and replace their existing group memberships.`
      );

      if (!confirmed) return;

      const membershipsByGroup = new Map();

      sourceMemberships.forEach(row => {
        const groupNumber = Number(row.group_number);

        if (!membershipsByGroup.has(groupNumber)) {
          membershipsByGroup.set(groupNumber, []);
        }

        membershipsByGroup
          .get(groupNumber)
          .push(row.enrolment_id);
      });

      for (const target of targets) {
        await updateCourseAssignment(
          target.id,
          {
            groups_enabled: true,
            group_count:
              Number(sourceAssignment.group_count)
          }
        );

        await deleteAssignmentConfiguration(
          target.id
        );

        for (const [
          groupNumber,
          enrolmentIds
        ] of membershipsByGroup.entries()) {
          if (enrolmentIds.length > 0) {
            await upsertGroupMemberships(
              target.id,
              enrolmentIds,
              groupNumber
            );
          }
        }
      }

      setCourseAssignments(prev =>
        prev.map(assignment => {
          if (
            assignment.assignment_type_id !==
              sourceAssignment.assignment_type_id ||
            assignment.id === sourceAssignment.id
          ) {
            return assignment;
          }

          return {
            ...assignment,
            groups_enabled: true,
            group_count:
              Number(sourceAssignment.group_count)
          };
        })
      );

      await refreshDuplicateScheduleData();

      setSuccess(
        `Group setup from ${sourceLabel} applied to all instances.`
      );
    } catch (err) {
      console.error(
        'Applying group setup to all instances failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to apply group setup to all instances.'
      );
    } finally {
      setApplyingGroupSetupId(null);
    }
  }

  async function handleCreateSequence(event) {
    event.preventDefault();

    const name = newSequenceName.trim();

    if (!name) {
      setError('Enter a sequence name.');
      return;
    }

    if (
      assignmentSequences.some(
        sequence =>
          String(sequence.name || '')
            .trim()
            .toLowerCase() === name.toLowerCase()
      )
    ) {
      setError('A sequence with that name already exists.');
      return;
    }

    setSequenceSaving(true);
    setError('');
    setSuccess('');

    try {
      const result = await createAssignmentSequence(
        course.id,
        name
      );

      const created = result?.[0];

      if (created) {
        setAssignmentSequences(prev =>
          [...prev, created].sort((a, b) =>
            String(a.name || '').localeCompare(
              String(b.name || ''),
              undefined,
              { sensitivity: 'base' }
            )
          )
        );
      }

      setNewSequenceName('');
      setSuccess(`Sequence "${name}" created.`);
    } catch (err) {
      console.error('Creating assignment sequence failed:', err);
      setError(
        err?.message ||
        'Failed to create assignment sequence.'
      );
    } finally {
      setSequenceSaving(false);
    }
  }

  async function handleDeleteSequence(sequence) {
    if (!sequence?.id) return;

    const assignedCount = Object.values(typeSettings).filter(
      setting => setting?.sequence_id === sequence.id
    ).length;

    const confirmed = window.confirm(
      `Delete the sequence "${sequence.name}"?` +
      (
        assignedCount
          ? ` ${assignedCount} assignment type${assignedCount === 1 ? '' : 's'} will become independent.`
          : ''
      ) +
      ' No assignments will be deleted.'
    );

    if (!confirmed) return;

    setSequenceSaving(true);
    setError('');
    setSuccess('');

    try {
      await deleteAssignmentSequence(sequence.id);

      setAssignmentSequences(prev =>
        prev.filter(item => item.id !== sequence.id)
      );

      setTypeSettings(prev => {
        const next = { ...prev };

        Object.keys(next).forEach(typeId => {
          if (next[typeId]?.sequence_id === sequence.id) {
            next[typeId] = {
              ...next[typeId],
              sequence_id: null
            };
          }
        });

        return next;
      });

      setSuccess('Assignment sequence removed.');
    } catch (err) {
      console.error('Removing assignment sequence failed:', err);
      setError(
        err?.message ||
        'Failed to remove assignment sequence.'
      );
    } finally {
      setSequenceSaving(false);
    }
  }

  async function handleSetAssignmentTypeSequence(
    typeId,
    sequenceId
  ) {
    setSequenceSaving(true);
    setError('');
    setSuccess('');

    try {
      const nextSequenceId = sequenceId || null;

      await upsertTypeSettings(
        course.id,
        typeId,
        { sequence_id: nextSequenceId }
      );

      setTypeSettings(prev => ({
        ...prev,
        [typeId]: {
          ...prev[typeId],
          course_id: course.id,
          assignment_type_id: typeId,
          sequence_id: nextSequenceId
        }
      }));

      setSuccess('Assignment sequence updated.');
    } catch (err) {
      console.error('Saving assignment sequence failed:', err);
      setError(
        err?.message ||
        'Failed to save assignment sequence.'
      );
    } finally {
      setSequenceSaving(false);
    }
  }


  const nonCoreTypes = assignmentTypes.filter(
    type =>
      !type.is_core &&
      type.is_course_copy !== true
  );

  const coreTypes = assignmentTypes.filter(type => {
  if (!type.is_core || !CORE_NAMES.includes(type.name)) {
    return false;
  }

  const courseField = COURSE_FIELD[type.name];

  return course?.[courseField] !== false;
});

  const assignmentsByType = {};

  courseAssignments.forEach(assignment => {
    const assignmentType = assignmentTypes.find(
      type =>
        type.id === assignment.assignment_type_id
    );

    if (
      assignmentType?.is_core ||
      CORE_NAMES.includes(assignmentType?.name)
    ) {
      return;
    }

    if (!assignmentsByType[assignment.assignment_type_id]) {
      assignmentsByType[assignment.assignment_type_id] = [];
    }

    assignmentsByType[assignment.assignment_type_id].push(
      assignment
    );
  });

  Object.values(assignmentsByType).forEach(assignments => {
    assignments.sort(
      (a, b) =>
        (Number(a.instance_number) || 0) -
        (Number(b.instance_number) || 0)
    );
  });

  const orderedAssignmentTypeEntries =
    Object.entries(assignmentsByType)
      .map((entry, originalIndex) => {
        const rawOrder =
          typeSettings[entry[0]]?.menu_order;

        const numericOrder =
          rawOrder === null ||
          rawOrder === undefined ||
          rawOrder === ''
            ? null
            : Number(rawOrder);

        return {
          entry,
          originalIndex,
          menuOrder:
            Number.isFinite(numericOrder)
              ? numericOrder
              : null
        };
      })
      .sort((a, b) => {
        if (
          a.menuOrder !== null &&
          b.menuOrder !== null
        ) {
          return (
            a.menuOrder - b.menuOrder ||
            a.originalIndex - b.originalIndex
          );
        }

        if (a.menuOrder !== null) return -1;
        if (b.menuOrder !== null) return 1;

        return a.originalIndex - b.originalIndex;
      })
      .map(item => item.entry);

  async function handleMoveAssignmentType(
    typeId,
    direction
  ) {
    const currentIndex =
      orderedAssignmentTypeEntries.findIndex(
        ([id]) => id === typeId
      );

    const targetIndex = currentIndex + direction;

    if (
      currentIndex < 0 ||
      targetIndex < 0 ||
      targetIndex >= orderedAssignmentTypeEntries.length
    ) {
      return;
    }

    const reordered = [
      ...orderedAssignmentTypeEntries
    ];

    [
      reordered[currentIndex],
      reordered[targetIndex]
    ] = [
      reordered[targetIndex],
      reordered[currentIndex]
    ];

    setMenuOrderSaving(true);
    setError('');
    setSuccess('');

    try {
      await Promise.all(
        reordered.map(([id], index) =>
          upsertTypeSettings(
            course.id,
            id,
            {
              menu_order: (index + 1) * 10
            }
          )
        )
      );

      setTypeSettings(prev => {
        const next = { ...prev };

        reordered.forEach(([id], index) => {
          next[id] = {
            ...next[id],
            menu_order: (index + 1) * 10
          };
        });

        return next;
      });

      setSuccess('Assignment menu order updated.');
    } catch (err) {
      console.error(
        'Assignment menu-order save failed:',
        err
      );

      setError(
        err?.message ||
        'Failed to save assignment menu order.'
      );
    } finally {
      setMenuOrderSaving(false);
    }
  }

  const weightTotal = currentWeightTotal();

  const modalAssignment = courseAssignments.find(
    assignment => assignment.id === groupModalAssignmentId
  );

  const modalAssignmentLabel =
    modalAssignment
      ? getAssignmentLabel(modalAssignment)
      : '';

  const modalCopySources =
    modalAssignment
      ? buildGroupCopySources(modalAssignment.id)
      : [];

  if (loading) {
    return <div style={styles.muted}>Loading...</div>;
  }

  return (
    <div>
      <h2 style={styles.subheading}>Assignments</h2>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {success && (
        <div style={styles.success}>{success}</div>
      )}

      {coreTypes.map(type => {
        const field = WEEK_FIELD[type.name];

        if (!field) return null;

const displayedWeeks =
  type.name === 'Philosophical Diary'
    ? Object.values(
        weeks.reduce((result, week) => {
          const key = String(
            week.week_number
          );

          const current =
            result[key];

          if (
            !current ||
            (
              week.pd_enabled &&
              !current.pd_enabled
            ) ||
            (
              week.pd_enabled ===
                current.pd_enabled &&
              String(week.class_date) <
                String(current.class_date)
            )
          ) {
            result[key] = week;
          }

          return result;
        }, {})
      ).sort(
        (a, b) =>
          Number(a.week_number) -
          Number(b.week_number)
      )
    : weeks;

        const enabledCount = displayedWeeks.filter(
          week => week[field]
        ).length;

        return (
          <div key={type.id} style={styles.section}>
            <div style={styles.sectionHeader}>
              <h3 style={styles.sectionHeading}>
                {type.name}
              </h3>

              <span style={styles.countPill}>
                {enabledCount} of {displayedWeeks.length} weeks enabled
              </span>

              {type.name === 'Philosophical Diary' && (
                <label style={styles.aiDisclosureHeaderToggle}>
                  <span style={styles.aiDisclosureHeaderLabel}>
                    AI Disclosure
                  </span>
                  <input
                    type="checkbox"
                    checked={!!aiDisclosureSettings[type.id]}
                    disabled={aiDisclosureSavingTypeId === type.id}
                    onChange={event =>
                      handleToggleAiDisclosure(
                        type.id,
                        event.target.checked
                      )
                    }
                  />
                  <span style={styles.aiDisclosureHeaderState}>
                    {aiDisclosureSettings[type.id] ? 'On' : 'Off'}
                  </span>
                </label>
              )}

              {course?.grading_mode === 'traditional' && (
                <div style={styles.weightControl}>
                  <label style={styles.weightLabel}>
                    Weight
                  </label>

                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    style={styles.weightInput}
                    value={weightDrafts[type.id] ?? ''}
                    onChange={event =>
                      setWeightDrafts(prev => ({
                        ...prev,
                        [type.id]: event.target.value
                      }))
                    }
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleSaveWeight(type.id);
                      }
                    }}
                    placeholder="0"
                  />

                  <span style={styles.weightPercent}>%</span>

                  <button
                    type="button"
                    style={styles.smallBtn}
                    disabled={weightSavingTypeId === type.id}
                    onClick={() => handleSaveWeight(type.id)}
                  >
                    {weightSavingTypeId === type.id
                      ? 'Saving...'
                      : 'Save'}
                  </button>
                </div>
              )}
            </div>

            <div style={styles.weekGrid}>
  {displayedWeeks.map(week => (
    <div key={week.id} style={styles.weekControl}>
            <button
        type="button"
        style={{
          ...styles.weekBtn,
          ...(
            week[field]
              ? styles.weekBtnOn
              : styles.weekBtnOff
          )
        }}
        onClick={() =>
          handleToggleWeek(
            week.id,
            field,
            week[field]
          )
        }
        title={week.class_date}
      >
        <div style={styles.weekNum}>
          W{week.week_number}
        </div>

        <div style={styles.weekDate}>
          {week.class_date.slice(5)}
        </div>
      </button>
    </div>
  ))}
</div>

          </div>
        );
      })}

      <div style={styles.section}>
        <h3 style={styles.sectionHeading}>
          Other Assignments
        </h3>

        <div style={styles.sequencePanel}>
          <div style={styles.sequencePanelHeader}>
            <div>
              <div style={styles.sequencePanelTitle}>
                Assignment Sequences
              </div>
              <div style={styles.fieldHint}>
                Use a sequence when several assignment types are stages of
                the same larger task. Student upcoming views will show only
                the next stage in each sequence.
              </div>
            </div>
          </div>

          <form
            onSubmit={handleCreateSequence}
            style={styles.sequenceCreateRow}
          >
            <input
              type="text"
              style={styles.input}
              value={newSequenceName}
              onChange={event =>
                setNewSequenceName(event.target.value)
              }
              placeholder="e.g. Research Project"
              maxLength={120}
            />

            <button
              type="submit"
              style={styles.smallBtn}
              disabled={sequenceSaving}
            >
              {sequenceSaving ? 'Saving...' : 'Add Sequence'}
            </button>
          </form>

          {assignmentSequences.length > 0 && (
            <div style={styles.sequenceList}>
              {assignmentSequences.map(sequence => {
                const count = Object.values(typeSettings).filter(
                  setting =>
                    setting?.sequence_id === sequence.id
                ).length;

                return (
                  <div
                    key={sequence.id}
                    style={styles.sequenceItem}
                  >
                    <span style={styles.sequenceName}>
                      {sequence.name}
                    </span>
                    <span style={styles.sequenceCount}>
                      {count} assignment type{count === 1 ? '' : 's'}
                    </span>
                    <button
                      type="button"
                      style={styles.sequenceDeleteBtn}
                      disabled={sequenceSaving}
                      onClick={() =>
                        handleDeleteSequence(sequence)
                      }
                    >
                      Delete
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {course?.grading_mode === 'traditional' && (
          <div style={styles.weightSummary}>
            Configured grading weights:{' '}
            <strong>
              {weightTotal}%
            </strong>
            {' / 100%'}
          </div>
        )}

        {Object.keys(assignmentsByType).length === 0 && (
          <div style={styles.muted}>
            No other assignments added yet.
          </div>
        )}

        {orderedAssignmentTypeEntries.map(
          ([typeId, assignments], typeIndex) => {
            const type = assignmentTypes.find(
              item => item.id === typeId
            );

            const libraryTypeName =
              type?.name || 'Assignment';
            const typeName =
              String(
                typeSettings[typeId]?.display_name ||
                ''
              ).trim() ||
              libraryTypeName;
            const requiresSubmission =
              type?.requires_submission ?? true;
            const isPeerReview =
              type?.assignment_kind === 'peer_review';

            const isStructuredForm =
              type?.assignment_kind ===
              'structured_form';

            const allowGroups =
              !isPeerReview &&
              !!type?.allow_groups;

            const typeDesc = typeDescriptions[typeId];
            const defaultDesc = type?.instructions || '';

            const expanded = expandedTypeId === typeId;
            const scheduleEnabled =
              !isPeerReview &&
              requiresSubmission &&
              !!typeSettings[typeId]?.schedule_enabled;
            const choiceEnabled =
              scheduleEnabled &&
              !!typeSettings[typeId]?.schedule_choice_enabled;
            const sharedScheduleEnabled =
              scheduleEnabled &&
              choiceEnabled &&
              assignments.length > 1 &&
              typeSettings[typeId]?.schedule_shared_across_instances === true;
            const sharedScheduleSource =
              sharedScheduleEnabled
                ? getSharedScheduleSourceAssignment(typeId, assignments)
                : null;
            const displayedScheduleAssignments =
              sharedScheduleEnabled
                ? (sharedScheduleSource ? [sharedScheduleSource] : [])
                : assignments;

            const scheduleChoiceCapacityWarning = (() => {
              if (!choiceEnabled) return null;

              if (
                assignments.length > 1 &&
                !sharedScheduleEnabled
              ) {
                return null;
              }

              const usesGroups = assignments.some(
                assignment =>
                  assignment.groups_enabled === true &&
                  Number(assignment.group_count) > 0
              );

              const requiredPlaces = assignments.reduce(
                (total, assignment) =>
                  total + getChoiceUnits(assignment).length,
                0
              );

              const poolAssignment =
                sharedScheduleEnabled
                  ? sharedScheduleSource
                  : assignments[0] || null;

              const poolItems = poolAssignment
                ? getAssignmentScheduleItems(
                    poolAssignment.id,
                    'choice'
                  )
                : [];

              if (
                poolItems.some(
                  item =>
                    item.capacity === null ||
                    item.capacity === undefined
                )
              ) {
                return null;
              }

              const availablePlaces = poolItems.reduce(
                (total, item) =>
                  total +
                  Math.max(
                    0,
                    Number(item.capacity) || 0
                  ),
                0
              );

              if (availablePlaces >= requiredPlaces) {
                return null;
              }

              return {
                noun: usesGroups ? 'group' : 'student',
                requiredPlaces,
                availablePlaces
              };
            })();

            const requestedTab =
              typeTabs[typeId] ||
              (
                isStructuredForm
                  ? 'form'
                  : 'description'
              );

            const activeTab =
              scheduleEnabled && requestedTab === 'dates'
                ? 'schedule'
                : !scheduleEnabled && requestedTab === 'schedule'
                  ? 'dates'
                  : requestedTab;

            const dateTabLabel =
              isStructuredForm
                ? 'Form Schedule'
                : requiresSubmission
                  ? 'Due Dates'
                  : 'Occurrence Dates';

            return (
              <div
                key={typeId}
                style={styles.typeGroup}
              >
                <div
                  style={{
                    ...styles.typeGroupHeader,
                    ...styles.typeGroupHeaderClickable
                  }}
                  onClick={() => toggleTypeExpanded(typeId)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={event => {
                    if (
                      event.key === 'Enter' ||
                      event.key === ' '
                    ) {
                      event.preventDefault();
                      toggleTypeExpanded(typeId);
                    }
                  }}
                >
                  <div style={styles.typeGroupTitleBlock}>
                    <span style={styles.chevron}>
                      {expanded ? '▾' : '▸'}
                    </span>

                    <span style={styles.typeGroupName}>
                      {typeName}
                    </span>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        marginLeft: '4px'
                      }}
                      onClick={event =>
                        event.stopPropagation()
                      }
                    >
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          color: '#66788a'
                        }}
                      >
                        Instances
                      </span>

                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={
                          instanceCountDrafts[
                            typeId
                          ] ??
                          String(
                            assignments.length
                          )
                        }
                        onChange={event =>
                          setInstanceCountDrafts(
                            prev => ({
                              ...prev,
                              [typeId]:
                                event.target.value
                            })
                          )
                        }
                        onKeyDown={event => {
                          event.stopPropagation();

                          if (
                            event.key ===
                            'Enter'
                          ) {
                            event.preventDefault();

                            handleSaveInstanceCount(
                              typeId,
                              instanceCountDrafts[
                                typeId
                              ] ??
                                assignments.length
                            );
                          }
                        }}
                        style={{
                          width: '58px',
                          border:
                            '1px solid #cbd5df',
                          borderRadius: '6px',
                          padding: '4px 6px',
                          fontSize: '12px',
                          background: 'white',
                          boxSizing:
                            'border-box'
                        }}
                      />

                      <button
                        type="button"
                        style={{
                          ...styles.smallBtn,
                          padding: '4px 8px',
                          fontSize: '11px'
                        }}
                        disabled={
                          instanceCountSavingTypeId ===
                          typeId
                        }
                        onClick={event => {
                          event.stopPropagation();

                          handleSaveInstanceCount(
                            typeId,
                            instanceCountDrafts[
                              typeId
                            ] ??
                              assignments.length
                          );
                        }}
                      >
                        {
                          instanceCountSavingTypeId ===
                          typeId
                            ? 'Saving...'
                            : 'Save'
                        }
                      </button>
                    </div>

                    <span
                      style={
                        requiresSubmission
                          ? styles.pillBlue
                          : styles.pillGrey
                      }
                    >
                      {requiresSubmission
                        ? 'Submission'
                        : 'No submission'}
                    </span>
                    {requiresSubmission && !isPeerReview && (
                      <label
                        style={styles.aiDisclosureHeaderToggle}
                        onClick={event => event.stopPropagation()}
                      >
                        <span style={styles.aiDisclosureHeaderLabel}>
                          AI Disclosure
                        </span>
                        <input
                          type="checkbox"
                          checked={!!aiDisclosureSettings[typeId]}
                          disabled={aiDisclosureSavingTypeId === typeId}
                          onChange={event =>
                            handleToggleAiDisclosure(
                              typeId,
                              event.target.checked
                            )
                          }
                        />
                        <span style={styles.aiDisclosureHeaderState}>
                          {aiDisclosureSettings[typeId] ? 'On' : 'Off'}
                        </span>
                      </label>
                    )}

                                        {requiresSubmission &&
                      !isPeerReview && (
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '13px',
                          cursor: 'pointer',
                          marginLeft: '8px'
                        }}
                        onClick={event =>
                          event.stopPropagation()
                        }
                      >
                        <input
                          type="checkbox"
                          checked={scheduleEnabled}
                          disabled={
                            scheduleModeSavingTypeId === typeId
                          }
                          onChange={event =>
                            handleToggleScheduleMode(
                              typeId,
                              event.target.checked
                            )
                          }
                        />
                        Schedule
                      </label>
                    )}

                    {allowGroups && (
                      <span style={styles.pillPurple}>
                        Groups allowed
                      </span>
                    )}

                    {allowGroups && (
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '13px',
                          cursor: 'pointer',
                          marginLeft: '8px'
                        }}
                        onClick={event =>
                          event.stopPropagation()
                        }
                      >
                        <input
                          type="checkbox"
                          checked={
                            !!typeSettings[typeId]
                              ?.group_deadlines_enabled
                          }
                          onChange={event =>
                            handleToggleGroupDeadlines(
                              typeId,
                              event.target.checked
                            )
                          }
                        />
                        Group deadlines
                      </label>
                    )}
                    {allowGroups && (
  <label
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      cursor: 'pointer',
      marginLeft: '8px'
    }}
    onClick={event => event.stopPropagation()}
  >
    <input
      type="checkbox"
      checked={assignments.every(
        a => a.group_chat_enabled !== false
      )}
      onChange={async event => {
        for (const assignment of assignments) {
          await handleUpdateAssignment(
            assignment.id,
            { group_chat_enabled: event.target.checked }
          );
        }
      }}
    />
    Group Chat
  </label>
)}

                    <label
                      style={styles.sequenceHeaderField}
                      onClick={event =>
                        event.stopPropagation()
                      }
                    >
                      <span style={styles.sequenceHeaderLabel}>
                        Sequence
                      </span>
                      <select
                        style={styles.sequenceSelect}
                        value={typeSettings[typeId]?.sequence_id || ''}
                        disabled={sequenceSaving}
                        onChange={event =>
                          handleSetAssignmentTypeSequence(
                            typeId,
                            event.target.value
                          )
                        }
                      >
                        <option value="">None</option>
                        {assignmentSequences.map(sequence => (
                          <option
                            key={sequence.id}
                            value={sequence.id}
                          >
                            {sequence.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div
                    style={styles.orderControls}
                    onClick={event =>
                      event.stopPropagation()
                    }
                  >
                    <button
                      type="button"
                      style={{
                        ...styles.dangerBtn,
                        marginRight: '6px',
                        whiteSpace: 'nowrap'
                      }}
                      disabled={
                        removingTypeId ===
                        typeId
                      }
                      onClick={event => {
                        event.stopPropagation();

                        handleRemoveAssignmentTypeFromCourse(
                          typeId
                        );
                      }}
                    >
                      {
                        removingTypeId ===
                        typeId
                          ? 'Removing...'
                          : 'Remove from Course'
                      }
                    </button>

                    <button
                      type="button"
                      style={{
                        ...styles.orderBtn,
                        ...(
                          menuOrderSaving ||
                          typeIndex === 0
                            ? styles.disabledBtn
                            : {}
                        )
                      }}
                      disabled={
                        menuOrderSaving ||
                        typeIndex === 0
                      }
                      onClick={event => {
                        event.stopPropagation();
                        handleMoveAssignmentType(
                          typeId,
                          -1
                        );
                      }}
                      title="Move up"
                      aria-label={`Move ${typeName} up`}
                    >
                      ↑
                    </button>

                    <button
                      type="button"
                      style={{
                        ...styles.orderBtn,
                        ...(
                          menuOrderSaving ||
                          typeIndex ===
                            orderedAssignmentTypeEntries.length - 1
                            ? styles.disabledBtn
                            : {}
                        )
                      }}
                      disabled={
                        menuOrderSaving ||
                        typeIndex ===
                          orderedAssignmentTypeEntries.length - 1
                      }
                      onClick={event => {
                        event.stopPropagation();
                        handleMoveAssignmentType(
                          typeId,
                          1
                        );
                      }}
                      title="Move down"
                      aria-label={`Move ${typeName} down`}
                    >
                      ↓
                    </button>
                  </div>

                  {course?.grading_mode === 'traditional' &&
                    requiresSubmission && (
                    <div
                      style={styles.weightControl}
                      onClick={event =>
                        event.stopPropagation()
                      }
                    >
                      <label
                        style={styles.weightLabel}
                      >
                        Weight
                      </label>

                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        style={styles.weightInput}
                        value={
                          weightDrafts[typeId] ?? ''
                        }
                        onChange={event =>
                          setWeightDrafts(prev => ({
                            ...prev,
                            [typeId]:
                              event.target.value
                          }))
                        }
                        onKeyDown={event => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            handleSaveWeight(typeId);
                          }
                        }}
                        placeholder="0"
                      />

                      <span style={styles.weightPercent}>
                        %
                      </span>

                      <button
                        type="button"
                        style={styles.smallBtn}
                        disabled={
                          weightSavingTypeId === typeId
                        }
                        onClick={event => {
                          event.stopPropagation();
                          handleSaveWeight(typeId);
                        }}
                      >
                        {weightSavingTypeId === typeId
                          ? 'Saving...'
                          : 'Save'}
                      </button>
                    </div>
                  )}
                </div>

                {expanded && (
                  <div style={styles.typeExpanded}>
                    <div style={styles.innerTabs}>
                      <button
                        type="button"
                        style={{
                          ...styles.innerTab,
                          color:
                            activeTab === 'description'
                              ? '#215b7d'
                              : '#66788a',
                          borderBottom:
                            activeTab === 'description'
                              ? '2px solid #215b7d'
                              : '2px solid transparent'
                        }}
                        onClick={event => {
                          setTypeTab(typeId, 'description');
                          event.currentTarget.blur();
                        }}
                      >
                        {
                          isStructuredForm
                            ? 'General'
                            : 'Description'
                        }
                      </button>

                      {isStructuredForm && (
                        <button
                          type="button"
                          style={{
                            ...styles.innerTab,
                            color:
                              activeTab === 'form'
                                ? '#215b7d'
                                : '#66788a',
                            borderBottom:
                              activeTab === 'form'
                                ? '2px solid #215b7d'
                                : '2px solid transparent'
                          }}
                          onClick={event => {
                            setTypeTab(
                              typeId,
                              'form'
                            );
                            event.currentTarget.blur();
                          }}
                        >
                          Form
                        </button>
                      )}

                      {scheduleEnabled && !isStructuredForm ? (
                        <button
                          type="button"
                          style={{
                            ...styles.innerTab,
                            color:
                              activeTab === 'schedule'
                                ? '#215b7d'
                                : '#66788a',
                            borderBottom:
                              activeTab === 'schedule'
                                ? '2px solid #215b7d'
                                : '2px solid transparent'
                          }}
                          onClick={event => {
                            setTypeTab(typeId, 'schedule');
                            event.currentTarget.blur();
                          }}
                        >
                          Schedule
                        </button>
                      ) : (
                        <button
                          type="button"
                          style={{
                            ...styles.innerTab,
                            color:
                              activeTab === 'dates'
                                ? '#215b7d'
                                : '#66788a',
                            borderBottom:
                              activeTab === 'dates'
                                ? '2px solid #215b7d'
                                : '2px solid transparent'
                          }}
                          onClick={event => {
                            setTypeTab(typeId, 'dates');
                            event.currentTarget.blur();
                          }}
                        >
                          {dateTabLabel}
                        </button>
                      )}

                      {allowGroups && (
                        <button
                          type="button"
                          style={{
                            ...styles.innerTab,
                            color:
                              activeTab === 'groups'
                                ? '#215b7d'
                                : '#66788a',
                            borderBottom:
                              activeTab === 'groups'
                                ? '2px solid #215b7d'
                                : '2px solid transparent'
                          }}
                          onClick={event => {
                            setTypeTab(typeId, 'groups');
                            event.currentTarget.blur();
                          }}
                        >
                          Groups
                        </button>
                      )}
                    </div>

                    {requiresSubmission && !isPeerReview && activeTab === 'description' && (
                      <div
                        style={{
                          margin: '12px 0 0',
                          padding: '12px 14px',
                          border: '1px solid #d7dee5',
                          borderRadius: '8px',
                          background: '#f8fafc',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px'
                        }}
                      >
                        {assignments.map(assignment => {
                          const sourceLabel =
                            getAssignmentLabel(
                              assignment
                            );

                          return (
                            <div
                              key={`feedback-forward-${assignment.id}`}
                              style={{
                                display: 'grid',
                                gridTemplateColumns:
                                  'minmax(140px, 0.7fr) minmax(220px, 1.3fr)',
                                gap: '12px',
                                alignItems: 'end'
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '13px',
                                  fontWeight: '700',
                                  color: '#1f2933',
                                  paddingBottom: '9px'
                                }}
                              >
                                <AssignmentInstanceNameEditor
                                  assignment={assignment}
                                  label={sourceLabel}
                                  automaticLabel={
                                    typeName +
                                    (
                                      assignments.length > 1
                                        ? ` ${assignment.instance_number}`
                                        : ''
                                    )
                                  }
                                  onSave={instanceName =>
                                    handleUpdateAssignment(
                                      assignment.id,
                                      {
                                        instance_name:
                                          instanceName
                                      }
                                    )
                                  }
                                />
                              </div>

                              <label style={styles.instanceField}>
                                <span style={styles.compactLabel}>
                                  Make feedback available in
                                </span>

                                <select
                                  style={styles.input}
                                  value={
                                    assignment.feedback_available_in_assignment_id ||
                                    ''
                                  }
                                  onChange={event =>
                                    handleUpdateAssignment(
                                      assignment.id,
                                      {
                                        feedback_available_in_assignment_id:
                                          event.target.value || null
                                      }
                                    )
                                  }
                                >
                                  <option value="">None</option>

                                                                    {[...courseAssignments]
                                    .sort((a, b) => {
                                      const aTypeIndex =
                                        orderedAssignmentTypeEntries.findIndex(
                                          ([typeId]) =>
                                            String(typeId) ===
                                            String(a.assignment_type_id)
                                        );

                                      const bTypeIndex =
                                        orderedAssignmentTypeEntries.findIndex(
                                          ([typeId]) =>
                                            String(typeId) ===
                                            String(b.assignment_type_id)
                                        );

                                      if (aTypeIndex !== bTypeIndex) {
                                        return aTypeIndex - bTypeIndex;
                                      }

                                      return (
                                        Number(a.instance_number) -
                                        Number(b.instance_number)
                                      );
                                    })
                                    .filter(candidate => {
                                      if (
                                        String(candidate.id) ===
                                        String(assignment.id)
                                      ) {
                                        return false;
                                      }

                                      return (
                                        candidate.assignment_types
                                          ?.requires_submission ??
                                        candidate.requires_submission ??
                                        true
                                      ) !== false;
                                    })
                                    .map(candidate => {
                                      const candidateName =
                                        candidate.assignment_types?.name ||
                                        'Assignment';

                                      const sameTypeCount =
                                        courseAssignments.filter(
                                          item =>
                                            String(
                                              item.assignment_type_id
                                            ) ===
                                            String(
                                              candidate.assignment_type_id
                                            )
                                        ).length;

                                      const candidateLabel =
                                        resolveAssignmentInstanceLabel(
                                          candidate,
                                          {
                                            baseName:
                                              candidateName,
                                            sameTypeCount
                                          }
                                        );

                                      return (
                                        <option
                                          key={candidate.id}
                                          value={candidate.id}
                                        >
                                          {candidateLabel}
                                        </option>
                                      );
                                    })}
                                </select>
                              </label>

                              <label
                                style={{
                                  ...styles.instanceField,
                                  gridColumn: '1 / -1'
                                }}
                              >
                                <span style={styles.compactLabel}>
                                  Grade Publication
                                </span>

                                <select
                                  style={styles.input}
                                  value={
                                    assignment
                                      .grade_publication_mode ||
                                    'immediate'
                                  }
                                  onChange={event =>
                                    handleGradePublicationModeChange(
                                      assignment,
                                      event.target.value
                                    ).catch(err => {
                                      console.error(
                                        'Grade publication setting failed:',
                                        err
                                      );

                                      setError(
                                        err?.message ||
                                        'Could not update grade publication.'
                                      );
                                    })
                                  }
                                >
                                  <option value="immediate">
                                    Immediate Grade Publication
                                  </option>
                                  <option value="manual">
                                    Publish Grades Manually
                                  </option>
                                </select>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div style={styles.tabContent}>

                      {
                        activeTab === 'form' &&
                        isStructuredForm && (
                          <div
                            style={{
                              display: 'grid',
                              gap: '16px'
                            }}
                          >
                            {assignments.map(
                              assignment => {
                                const formLabel =
                                  getAssignmentLabel(
                                    assignment
                                  );

                                return (
                                  <StructuredFormBuilder
                                    key={assignment.id}
                                    mode="course"
                                    assignment={assignment}
                                    label={formLabel}
                                    session={session}
                                  />
                                );
                              }
                            )}
                          </div>
                        )
                      }

                      {activeTab === 'description' && (
                        <>
                          <TypeNameEditor
                            courseId={course.id}
                            assignmentTypeId={typeId}
                            libraryName={
                              type?.name ||
                              'Assignment'
                            }
                            initialName={
                              typeSettings[typeId]
                                ?.display_name ||
                              ''
                            }
                            onSaved={displayName =>
                              setTypeSettings(prev => ({
                                ...prev,
                                [typeId]: {
                                  ...(prev[typeId] || {}),
                                  course_id: course.id,
                                  assignment_type_id:
                                    typeId,
                                  display_name:
                                    displayName
                                }
                              }))
                            }
                          />
                          <TypeDescriptionEditor
                          courseId={course.id}
                          assignmentTypeId={typeId}
                          initialDescription={
                            typeDesc !== undefined
                              ? typeDesc
                              : defaultDesc
                          }
                          onSaved={description =>
                            setTypeDescriptions(prev => ({
                              ...prev,
                              [typeId]: description
                            }))
                          }
                        />

                        {requiresSubmission &&
                          !isPeerReview &&
                          !isStructuredForm && (
                          <SubmissionFileSettingsEditor
                            courseId={course.id}
                            assignmentTypeId={typeId}
                            initialSettings={
                              typeSettings[typeId] ||
                              {}
                            }
                            onSaved={patch =>
                              setTypeSettings(prev => ({
                                ...prev,
                                [typeId]: {
                                  ...(prev[typeId] || {}),
                                  ...patch
                                }
                              }))
                            }
                          />
                        )}
                        </>
                      )}
                      {activeTab === 'peer_review' && isPeerReview && (
                        <PeerReviewManager
                          course={course}
                          peerReviewAssignments={assignments}
                          allCourseAssignments={courseAssignments}
                          assignmentTypes={assignmentTypes}
                          enrolments={enrolments}
                          orderedTypeIds={
                            orderedAssignmentTypeEntries.map(
                              ([orderedTypeId]) => orderedTypeId
                            )
                          }
                        />
                      )}

                      {activeTab === 'dates' &&
                        isStructuredForm && (
                          <div style={{ display: 'grid', gap: '16px' }}>
                            {assignments.map(assignment => {
                              const label =
                              getAssignmentLabel(
                                assignment
                              );

                              return (
                                <StructuredFormScheduleEditor
                                  key={assignment.id}
                                  assignment={assignment}
                                  label={label}
                                  scheduleEnabled={
                                    scheduleEnabled
                                  }
                                  scheduleChoiceEnabled={
                                    !!typeSettings[typeId]
                                      ?.schedule_choice_enabled
                                  }
                                  scheduleChoiceOpenDate={
                                    typeSettings[typeId]
                                      ?.schedule_choice_open_date ||
                                    ''
                                  }
                                  scheduleChoiceCloseDate={
                                    typeSettings[typeId]
                                      ?.schedule_choice_close_date ||
                                    ''
                                  }
                                  course={course}
                                  calendarWeeks={weeks}
                                  calendarAssignments={
                                    courseAssignments
                                  }
                                  calendarTypeSettings={
                                    typeSettings
                                  }
                                  calendarScheduleItems={
                                    assignmentScheduleItems
                                  }
                                  calendarHolidays={
                                    holidays
                                  }
                                  scheduleReadings={
                                    scheduleReadings
                                  }
                                  scheduleEvents={
                                    scheduleEvents
                                  }
                                  onScheduleTypeSettingsChanged={
                                    patch =>
                                      setTypeSettings(prev => ({
                                        ...prev,
                                        [typeId]: {
                                          ...prev[typeId],
                                          ...patch
                                        }
                                      }))
                                  }
                                  groupDeadlinesEnabled={
                                    !!typeSettings[typeId]
                                      ?.group_deadlines_enabled
                                  }
                                  groupNameFor={groupNumber =>
                                    assignmentGroupName(
                                      assignment.id,
                                      groupNumber
                                    )
                                  }
                                  onAssignmentChanged={updated =>
                                    setCourseAssignments(prev =>
                                      prev.map(row =>
                                        row.id === assignment.id
                                          ? {
                                              ...row,
                                              ...updated,
                                              assignment_types:
                                                row.assignment_types
                                            }
                                          : row
                                      )
                                    )
                                  }
                                />
                              );
                            })}
                          </div>
                        )}

                      {activeTab === 'dates' &&
                        !scheduleEnabled &&
                        !isStructuredForm && (
                        <div style={styles.instanceTable}>
                          {assignments.map(assignment => {
                            const label =
                              getAssignmentLabel(
                                assignment
                              );
                            return (
                              <div
                                key={assignment.id}
                                style={{
                                  ...styles.instanceRow,
                                  gridTemplateColumns: requiresSubmission
                                    ? 'minmax(120px, 0.8fr) repeat(3, minmax(140px, 1fr)) auto'
                                    : 'minmax(120px, 0.8fr) minmax(180px, 1fr) auto'
                                }}
                              >
                                <div style={styles.instanceName}>
                                  <div>{label}</div>


                                </div>

                                {requiresSubmission ? (
                                  (
                                    typeSettings[typeId]
                                      ?.group_deadlines_enabled &&
                                    assignment.groups_enabled &&
                                    Number(assignment.group_count) > 0
                                  ) ? (
                                    <>
                                      <div
                                        style={{
                                          gridColumn: '2 / -2',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          gap: '10px'
                                        }}
                                      >
                                        {Array.from(
                                          {
                                            length:
                                              Number(
                                                assignment.group_count
                                              ) || 0
                                          },
                                          (_, index) => index + 1
                                        ).map(groupNumber => {
                                          const deadline =
                                            groupDeadlines[
                                              assignment.id
                                            ]?.[groupNumber] || {};

                                          return (
                                            <div
                                              key={groupNumber}
                                              style={{
                                                display: 'grid',
                                                gridTemplateColumns:
                                                  '90px minmax(140px, 1fr) minmax(140px, 1fr)',
                                                gap: '8px',
                                                alignItems: 'end'
                                              }}
                                            >
                                              <div
                                                style={{
                                                  fontSize: '13px',
                                                  fontWeight: '700',
                                                  color: '#1f2933',
                                                  paddingBottom: '9px'
                                                }}
                                              >
                                                {assignmentGroupName(assignment.id, groupNumber)}
                                              </div>

                                              <div
                                                style={
                                                  styles.instanceField
                                                }
                                              >
                                                <label
                                                  style={
                                                    styles.compactLabel
                                                  }
                                                >
                                                  Open date
                                                </label>

                                                                                                <CourseDatePicker
                                                  value={
                                                    deadline.open_date ||
                                                    ''
                                                  }
                                                  initialMonthDate={
                                                    getPreviousGroupClosingDate(
                                                      assignment,
                                                      groupNumber
                                                    ) ||
                                                    assignment.open_date ||
                                                    ''
                                                  }
                                                  emptyLabel={
                                                    assignment.open_date
                                                      ? `Use assignment date (${assignment.open_date})`
                                                      : 'Select date'
                                                  }
                                                  onChange={
                                                    value =>
                                                      handleGroupDeadlineDateChange(
                                                        assignment.id,
                                                        groupNumber,
                                                        'open_date',
                                                        value
                                                      )
                                                  }
                                                  course={course}
                                                  weeks={weeks}
                                                  assignments={
                                                    courseAssignments
                                                  }
                                                  typeSettings={
                                                    typeSettings
                                                  }
                                                  scheduleItems={
                                                    assignmentScheduleItems
                                                  }
                                                  scheduleReadings={
                                                    scheduleReadings
                                                  }
                                                  scheduleEvents={
                                                    scheduleEvents
                                                  }
                                                  holidays={
                                                    holidays
                                                  }
                                                />
                                              </div>

                                              <div
                                                style={
                                                  styles.instanceField
                                                }
                                              >
                                                <label
                                                  style={
                                                    styles.compactLabel
                                                  }
                                                >
                                                  Due date
                                                </label>

                                                                                                <CourseDatePicker
                                                  value={
                                                    deadline.due_date ||
                                                    ''
                                                  }
                                                  initialMonthDate={
                                                    deadline.open_date ||
                                                    assignment.open_date ||
                                                    ''
                                                  }
                                                  emptyLabel={
                                                    assignment.due_date
                                                      ? `Use assignment date (${assignment.due_date})`
                                                      : 'Select date'
                                                  }
                                                  onChange={
                                                    value =>
                                                      handleGroupDeadlineDateChange(
                                                        assignment.id,
                                                        groupNumber,
                                                        'due_date',
                                                        value
                                                      )
                                                  }
                                                  course={course}
                                                  weeks={weeks}
                                                  assignments={
                                                    courseAssignments
                                                  }
                                                  typeSettings={
                                                    typeSettings
                                                  }
                                                  scheduleItems={
                                                    assignmentScheduleItems
                                                  }
                                                  scheduleReadings={
                                                    scheduleReadings
                                                  }
                                                  scheduleEvents={
                                                    scheduleEvents
                                                  }
                                                  holidays={
                                                    holidays
                                                  }
                                                />
                                              </div>
                                            </div>
                                          );
                                        })}

                                        <div
                                          style={{
                                            maxWidth: '220px'
                                          }}
                                        >
                                          <label
                                            style={
                                              styles.compactLabel
                                            }
                                          >
                                            Word limit
                                          </label>

                                          <input
                                            style={styles.input}
                                            type="number"
                                            min="1"
                                            defaultValue={
                                              assignment.word_limit ||
                                              ''
                                            }
                                            onBlur={event =>
                                              handleUpdateAssignment(
                                                assignment.id,
                                                {
                                                  word_limit:
                                                    event.target.value
                                                      ? Number(
                                                          event.target
                                                            .value
                                                        )
                                                      : null
                                                }
                                              )
                                            }
                                            placeholder="No limit"
                                          />
                                        </div>
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <div
                                        style={
                                          styles.instanceField
                                        }
                                      >
                                        <label
                                          style={
                                            styles.compactLabel
                                          }
                                        >
                                          Open date
                                        </label>

                                        <CourseDatePicker
                                          value={assignment.open_date || ''}
                                          initialMonthDate={
                                            getPreviousAssignmentClosingDate(
                                              assignment
                                            )
                                          }
                                          onChange={value =>
                                            handleUpdateAssignment(
                                              assignment.id,
                                              {
                                                open_date:
                                                  value || null
                                              }
                                            )
                                          }
                                          course={course}
                                          weeks={weeks}
                                          assignments={courseAssignments}
                                          typeSettings={typeSettings}
                                          scheduleItems={assignmentScheduleItems}
                                          scheduleReadings={scheduleReadings}
                                          scheduleEvents={scheduleEvents}
                                          holidays={holidays}
                                        />
                                      </div>

                                      <div
                                        style={
                                          styles.instanceField
                                        }
                                      >
                                        <label
                                          style={
                                            styles.compactLabel
                                          }
                                        >
                                          Due date
                                        </label>

                                        <CourseDatePicker
                                          value={assignment.due_date || ''}
                                          initialMonthDate={
                                            assignment.open_date ||
                                            ''
                                          }
                                          onChange={value =>
                                            handleUpdateAssignment(
                                              assignment.id,
                                              {
                                                due_date:
                                                  value || null
                                              }
                                            )
                                          }
                                          course={course}
                                          weeks={weeks}
                                          assignments={courseAssignments}
                                          typeSettings={typeSettings}
                                          scheduleItems={assignmentScheduleItems}
                                          scheduleReadings={scheduleReadings}
                                          scheduleEvents={scheduleEvents}
                                          holidays={holidays}
                                        />
                                      </div>

                                      <div
                                        style={
                                          styles.instanceField
                                        }
                                      >
                                        <label
                                          style={
                                            styles.compactLabel
                                          }
                                        >
                                          Word limit
                                        </label>

                                        <input
                                          style={styles.input}
                                          type="number"
                                          min="1"
                                          defaultValue={
                                            assignment.word_limit ||
                                            ''
                                          }
                                          onBlur={event =>
                                            handleUpdateAssignment(
                                              assignment.id,
                                              {
                                                word_limit:
                                                  event.target.value
                                                    ? Number(
                                                        event.target
                                                          .value
                                                      )
                                                    : null
                                              }
                                            )
                                          }
                                          placeholder="No limit"
                                        />
                                      </div>
                                    </>
                                  )
                                ) : (
                                  <div style={styles.instanceField}>
                                    <label style={styles.compactLabel}>
                                      Occurs on
                                    </label>

                                    <CourseDatePicker
                                      value={assignment.occurs_on || ''}
                                      onChange={value =>
                                        handleUpdateAssignment(
                                          assignment.id,
                                          {
                                            occurs_on:
                                              value || null
                                          }
                                        )
                                      }
                                      course={course}
                                      weeks={weeks}
                                      assignments={courseAssignments}
                                      typeSettings={typeSettings}
                                      scheduleItems={assignmentScheduleItems}
                                      scheduleReadings={scheduleReadings}
                                      scheduleEvents={scheduleEvents}
                                      holidays={holidays}
                                    />
                                  </div>
                                )}

                                <div style={styles.instanceRemove}>
                                  <button
                                    type="button"
                                    style={styles.dangerBtn}
                                    onClick={() =>
                                      handleDeleteAssignment(
                                        assignment.id
                                      )
                                    }
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {activeTab === 'schedule' &&
                        requiresSubmission &&
                        scheduleEnabled && (
                        <div style={styles.scheduleInstances}>
                          {scheduleChoiceCapacityWarning && (
                            <div
                              style={{
                                color: '#b42318',
                                fontSize: '13px',
                                lineHeight: '1.45',
                                margin: '0 0 10px'
                              }}
                            >
                              Warning: there are more{' '}
                              {scheduleChoiceCapacityWarning.noun}{' '}
                              choices to allocate across these assignment
                              instances than there are available option
                              places ({scheduleChoiceCapacityWarning.requiredPlaces}{' '}
                              required, {scheduleChoiceCapacityWarning.availablePlaces}{' '}
                              available). Please increase either the number
                              of options, the capacity, or both.
                            </div>
                          )}

                          <div style={styles.scheduleChoicePanel}>
                            <label style={styles.scheduleChoiceToggle}>
                              <input
                                type="checkbox"
                                checked={
                                  choiceEnabled &&
                                  assignments.length > 1 &&
                                  sharedScheduleEnabled
                                }
                                disabled={
                                  scheduleSharingSavingTypeId === typeId ||
                                  !choiceEnabled ||
                                  assignments.length <= 1 ||
                                  (choiceEnabled &&
                                    assignments.length > 1 &&
                                    sharedScheduleEnabled)
                                }
                                onChange={event =>
                                  handleToggleScheduleSharing(
                                    typeId,
                                    event.target.checked
                                  )
                                }
                              />
                              <span>
                                Use one Schedule for all instances
                              </span>
                            </label>

                            <div style={styles.scheduleChoiceHint}>
                              {choiceEnabled && assignments.length > 1
                                ? sharedScheduleEnabled
                                  ? 'All instances of this assignment type draw from the same pool of linked readings/events.'
                                  : 'Choice across multiple instances requires one shared pool. Turn this on to finish the setup.'
                                : choiceEnabled
                                  ? 'Students/groups choose one linked reading/event for this assignment.'
                                  : 'Each assignment instance has one linked reading/event of its own.'}
                            </div>
                          </div>

                          {(() => {
                            const timingDraft =
                              scheduleTimingDrafts[typeId] ||
                              scheduleTimingDraftFromSettings(
                                typeSettings[typeId]
                              );

                            return (
                              <div style={styles.scheduleTimingPanel}>
                                <div style={styles.scheduleTimingHeader}>
                                  Assignment-wide relative dates
                                </div>
                                <div style={styles.scheduleTimingHint}>
                                  These offsets apply to every linked reading or event below. Moving the source item changes the derived dates automatically.
                                </div>

                                <div style={styles.scheduleTimingRows}>
                                  <div style={styles.scheduleTimingRow}>
                                    <strong>Opens</strong>
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      style={styles.scheduleDaysInput}
                                      value={timingDraft.openDays}
                                      placeholder="—"
                                      onChange={event =>
                                        setScheduleTimingDrafts(prev => ({
                                          ...prev,
                                          [typeId]: {
                                            ...timingDraft,
                                            openDays: event.target.value
                                          }
                                        }))
                                      }
                                    />
                                    <span>days</span>
                                    <select
                                      style={styles.scheduleDirectionSelect}
                                      value={timingDraft.openDirection}
                                      disabled={timingDraft.openDays === ''}
                                      onChange={event =>
                                        setScheduleTimingDrafts(prev => ({
                                          ...prev,
                                          [typeId]: {
                                            ...timingDraft,
                                            openDirection: event.target.value
                                          }
                                        }))
                                      }
                                    >
                                      <option value="before">before</option>
                                      <option value="after">after</option>
                                    </select>
                                    <span>the linked item</span>
                                  </div>

                                  <div style={styles.scheduleTimingRow}>
                                    <strong>Due</strong>
                                    <input
                                      type="number"
                                      min="0"
                                      step="1"
                                      style={styles.scheduleDaysInput}
                                      value={timingDraft.dueDays}
                                      placeholder="—"
                                      onChange={event =>
                                        setScheduleTimingDrafts(prev => ({
                                          ...prev,
                                          [typeId]: {
                                            ...timingDraft,
                                            dueDays: event.target.value
                                          }
                                        }))
                                      }
                                    />
                                    <span>days</span>
                                    <select
                                      style={styles.scheduleDirectionSelect}
                                      value={timingDraft.dueDirection}
                                      disabled={timingDraft.dueDays === ''}
                                      onChange={event =>
                                        setScheduleTimingDrafts(prev => ({
                                          ...prev,
                                          [typeId]: {
                                            ...timingDraft,
                                            dueDirection: event.target.value
                                          }
                                        }))
                                      }
                                    >
                                      <option value="before">before</option>
                                      <option value="after">after</option>
                                    </select>
                                    <span>the linked item</span>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  style={styles.btn}
                                  disabled={scheduleTimingSavingTypeId === typeId}
                                  onClick={() => handleSaveScheduleTiming(typeId)}
                                >
                                  {scheduleTimingSavingTypeId === typeId
                                    ? 'Saving...'
                                    : 'Save Relative Dates'}
                                </button>
                              </div>
                            );
                          })()}

                          <div style={styles.scheduleChoicePanel}>
                            <label style={styles.scheduleChoiceToggle}>
                              <input
                                type="checkbox"
                                checked={choiceEnabled}
                                disabled={scheduleChoiceSavingTypeId === typeId}
                                onChange={event =>
                                  handleToggleScheduleChoiceMode(
                                    typeId,
                                    event.target.checked
                                  )
                                }
                              />
                              <span>
                                Allow students/groups to choose among linked items
                              </span>
                            </label>

                            {!choiceEnabled ? (
                              <div style={styles.scheduleChoiceHint}>
                                Each assignment instance can have one linked reading/event. Your remembered Choice pool is retained in the background.
                              </div>
                            ) : (() => {
                              const choiceDraft =
                                scheduleChoiceDrafts[typeId] ||
                                scheduleChoiceDraftFromSettings(
                                  typeSettings[typeId]
                                );

                              return (
                                <>
                                  <div style={styles.scheduleChoiceHint}>
                                    {sharedScheduleEnabled
                                      ? 'Each student/group chooses one option for each assignment instance. Capacity is shared globally across the pool.'
                                      : 'Each student/group chooses one of the linked items.'}
                                  </div>

                                  <div style={styles.scheduleChoiceSettingsRow}>
                                    <div style={styles.instanceField}>
                                      <label style={styles.compactLabel}>
                                        Choice opens
                                      </label>
                                                                            <CourseDatePicker
                                        value={
                                          choiceDraft.openDate
                                        }
                                        onChange={
                                          value =>
                                            setScheduleChoiceDrafts(
                                              prev => ({
                                                ...prev,

                                                [typeId]: {
                                                  ...choiceDraft,
                                                  openDate:
                                                    value
                                                }
                                              })
                                            )
                                        }
                                        course={course}
                                        weeks={weeks}
                                        assignments={
                                          courseAssignments
                                        }
                                        typeSettings={
                                          typeSettings
                                        }
                                        scheduleItems={
                                          assignmentScheduleItems
                                        }
                                        scheduleReadings={
                                          scheduleReadings
                                        }
                                        scheduleEvents={
                                          scheduleEvents
                                        }
                                        holidays={
                                          holidays
                                        }
                                      />
                                    </div>

                                    <div style={styles.instanceField}>
                                      <label style={styles.compactLabel}>
                                        Choice closes
                                      </label>
                                                                            <CourseDatePicker
                                        value={
                                          choiceDraft.closeDate
                                        }
                                        initialMonthDate={
                                          choiceDraft.openDate ||
                                          ''
                                        }
                                        onChange={
                                          value =>
                                            setScheduleChoiceDrafts(
                                              prev => ({
                                                ...prev,

                                                [typeId]: {
                                                  ...choiceDraft,
                                                  closeDate:
                                                    value
                                                }
                                              })
                                            )
                                        }
                                        course={course}
                                        weeks={weeks}
                                        assignments={
                                          courseAssignments
                                        }
                                        typeSettings={
                                          typeSettings
                                        }
                                        scheduleItems={
                                          assignmentScheduleItems
                                        }
                                        scheduleReadings={
                                          scheduleReadings
                                        }
                                        scheduleEvents={
                                          scheduleEvents
                                        }
                                        holidays={
                                          holidays
                                        }
                                      />
                                    </div>
<div style={styles.scheduleChoiceSave}>
                                      <button
                                        type="button"
                                        style={styles.btn}
                                        disabled={scheduleChoiceSavingTypeId === typeId}
                                        onClick={() =>
                                          handleSaveScheduleChoiceSettings(typeId)
                                        }
                                      >
                                        {scheduleChoiceSavingTypeId === typeId
                                          ? 'Saving...'
                                          : 'Save Choice Settings'}
                                      </button>
                                    </div>
                                  </div>
                                </>
                              );
                            })()}
                          </div>

                          {displayedScheduleAssignments.map(assignment => {
                            const label =
                              sharedScheduleEnabled
                                ? 'Shared Schedule'
                                : getAssignmentLabel(
                                    assignment
                                  );

                            const activeConfigurationMode =
                              choiceEnabled ? 'choice' : 'single';

                            const linkedItems =
                              getAssignmentScheduleItems(
                                assignment.id,
                                activeConfigurationMode
                              );

                            const manualChoiceAssignment =
                              sharedScheduleEnabled
                                ? assignments.find(
                                    candidate =>
                                      String(candidate.id) ===
                                      String(
                                        choiceTargetAssignmentByType[typeId] || ''
                                      )
                                  ) || assignments[0] || assignment
                                : assignment;

                            const manualLinkedItems =
                              getAssignmentScheduleItems(
                                manualChoiceAssignment.id,
                                'choice'
                              );

                            const choiceUnitStateKey =
                              sharedScheduleEnabled
                                ? `${typeId}:${manualChoiceAssignment.id}`
                                : assignment.id;

                            const linkedReadingIds =
                              new Set(
                                linkedItems
                                  .filter(
                                    item =>
                                      item.item_type ===
                                      'reading'
                                  )
                                  .map(
                                    item =>
                                      item.week_reading_assignment_id
                                  )
                              );

                            const linkedEventIds =
                              new Set(
                                linkedItems
                                  .filter(
                                    item =>
                                      item.item_type ===
                                      'event'
                                  )
                                  .map(
                                    item =>
                                      item.course_event_id
                                  )
                              );

                            const availableReadings =
                              scheduleReadings.filter(
                                row =>
                                  !linkedReadingIds.has(
                                    row.id
                                  )
                              );

                            const availableEvents =
                              scheduleEvents.filter(
                                row =>
                                  !linkedEventIds.has(
                                    row.id
                                  )
                              );

                            const isScheduleSaving =
                              scheduleSavingId ===
                              assignment.id;
                            const choiceUnits =
                              choiceEnabled
                                ? getChoiceUnits(manualChoiceAssignment)
                                : [];
                            const requiredChoiceCount =
                              Math.max(
                                1,
                                Number(
                                  typeSettings[typeId]
                                    ?.schedule_choice_required_count
                                ) || 1
                              );
                            const needsChoices =
                              choiceEnabled
                                ? choiceUnits.filter(
                                    unit =>
                                      getChoiceCountForUnit(
                                        manualChoiceAssignment,
                                        unit
                                      ) < requiredChoiceCount
                                  )
                                : [];
                            const selectedUnitKey =
                              choiceUnitByAssignment[
                                choiceUnitStateKey
                              ] || '';
                            const selectedChoiceUnit =
                              choiceUnits.find(
                                unit =>
                                  unit.key === selectedUnitKey
                              ) || null;
                            const isChoiceSaving =
                              choiceSavingAssignmentId ===
                              manualChoiceAssignment.id;
                            const duplicateLink =
                              getAssignmentScheduleDuplicate(assignment.id);
                            const duplicateMode =
                              !!duplicateLink ||
                              !!duplicateModeDrafts[assignment.id];
                            const duplicateCandidates =
                              getDuplicateCandidates(assignment);
                            const duplicateSaving =
                              duplicateSavingAssignmentId === assignment.id;
                            const duplicateSourceAssignment = duplicateLink
                              ? courseAssignments.find(
                                  row =>
                                    String(row.id) ===
                                    String(duplicateLink.source_course_assignment_id)
                                ) || null
                              : null;
                            const duplicateChoiceEnabled = duplicateLink
                              ? getEffectiveDuplicateChoiceEnabled(assignment.id)
                              : false;

                            return (
                              <div
                                key={assignment.id}
                                style={styles.scheduleInstanceCard}
                              >
                                <div
                                  style={styles.scheduleInstanceHeader}
                                >
                                  <span>{label}</span>
                                  <button
                                    type="button"
                                    style={{
                                      ...styles.duplicateBtn,
                                      ...(duplicateMode
                                        ? styles.duplicateBtnActive
                                        : {})
                                    }}
                                    disabled={
                                      duplicateSaving ||
                                      !typeSettings[typeId]?.sequence_id
                                    }
                                    onClick={() =>
                                      handleToggleDuplicate(assignment)
                                    }
                                    title={
                                      typeSettings[typeId]?.sequence_id
                                        ? sharedScheduleEnabled
                                          ? 'Live-sync this shared Schedule with another assignment in the same sequence.'
                                          : 'Live-sync this instance with another assignment in the same sequence.'
                                        : 'Add this assignment type to a sequence before using Duplicate.'
                                    }
                                  >
                                    {duplicateSaving
                                      ? 'Saving...'
                                      : duplicateLink
                                        ? 'Duplicate ✓'
                                        : 'Duplicate'}
                                  </button>
                                </div>

                                {duplicateMode ? (
                                  <div style={styles.duplicatePanel}>
                                    <label style={styles.compactLabel}>
                                      Duplicate schedule from
                                    </label>
                                    <select
                                      style={styles.scheduleSelect}
                                      value={
                                        duplicateLink?.source_course_assignment_id || ''
                                      }
                                      disabled={duplicateSaving}
                                      onChange={event =>
                                        handleSelectDuplicateSource(
                                          assignment,
                                          event.target.value
                                        )
                                      }
                                    >
                                      <option value="">
                                        Select an assignment instance...
                                      </option>
                                      {duplicateCandidates.map(candidate => (
                                        <option
                                          key={candidate.id}
                                          value={candidate.id}
                                        >
                                          {assignmentInstanceLabel(candidate)}
                                        </option>
                                      ))}
                                    </select>

                                    {duplicateCandidates.length === 0 &&
                                      !duplicateLink && (
                                      <div style={styles.duplicateHint}>
                                        No other Schedule-enabled assignment instances are available in this sequence.
                                      </div>
                                    )}

                                    {duplicateLink && (
                                      <div style={styles.duplicateHint}>
                                        Live sync from <strong>
                                          {assignmentInstanceLabel(
                                            duplicateSourceAssignment
                                          )}
                                        </strong>. Linked items and individual/group choices follow that assignment automatically. Relative dates, submissions and grading remain separate here.
                                      </div>
                                    )}
                                  </div>
                                ) : (!choiceEnabled && linkedItems.length >= 1) ? (
                                <div style={styles.scheduleEmpty}>
                                  Choice is off, so this instance can have only one linked reading/event. Remove it before linking a different one.
                                </div>
                              ) : (
                                <div style={styles.scheduleAddRow}>
                                  <select
                                    style={styles.scheduleSelect}
                                    value={
                                      scheduleSelections[
                                        assignment.id
                                      ] || ''
                                    }
                                    disabled={isScheduleSaving}
                                    onChange={event =>
                                      setScheduleSelections(
                                        prev => ({
                                          ...prev,
                                          [assignment.id]:
                                            event.target.value
                                        })
                                      )
                                    }
                                  >
                                    <option value="">
                                      Select a reading or event...
                                    </option>

                                    {availableReadings.length > 0 && (
                                      <optgroup label="Readings">
                                        {availableReadings.map(
                                          occurrence => (
                                            <option
                                              key={occurrence.id}
                                              value={`reading:${occurrence.id}`}
                                            >
                                              {`Week ${
                                                occurrence
                                                  .course_weeks
                                                  ?.week_number ||
                                                '—'
                                              } — ${
                                                occurrence
                                                  .course_readings
                                                  ?.title ||
                                                'Reading'
                                              }${
                                                occurrence
                                                  .course_readings
                                                  ?.author
                                                  ? ` (${occurrence.course_readings.author})`
                                                  : ''
                                              }`}
                                            </option>
                                          )
                                        )}
                                      </optgroup>
                                    )}

                                    {availableEvents.length > 0 && (
                                      <optgroup label="Events">
                                        {availableEvents.map(
                                          courseEvent => (
                                            <option
                                              key={courseEvent.id}
                                              value={`event:${courseEvent.id}`}
                                            >
                                              {`Week ${
                                                courseEvent
                                                  .course_weeks
                                                  ?.week_number ||
                                                '—'
                                              } — ${
                                                courseEvent.title
                                              }`}
                                            </option>
                                          )
                                        )}
                                      </optgroup>
                                    )}
                                  </select>

                                  <button
                                    type="button"
                                    style={styles.btn}
                                    disabled={
                                      isScheduleSaving ||
                                      !scheduleSelections[
                                        assignment.id
                                      ]
                                    }
                                    onClick={() =>
                                      handleAddScheduleItem(
                                        assignment.id,
                                        activeConfigurationMode
                                      )
                                    }
                                  >
                                    {isScheduleSaving
                                      ? 'Saving...'
                                      : 'Add'}
                                  </button>
                                </div>
                                )}

                                {linkedItems.length === 0 ? (
                                  <div style={styles.scheduleEmpty}>
                                    {duplicateMode
                                      ? 'The source assignment currently has no linked readings or events.'
                                      : 'No readings or events linked.'}
                                  </div>
                                ) : (
                                  <div style={styles.linkedScheduleList}>
                                    {linkedItems.map(
                                      (item, index) => {
                                        const source =
                                          getScheduleSourceInfo(
                                            item
                                          );

                                        return (
                                          <div
                                            key={item.id}
                                            style={styles.linkedScheduleItem}
                                          >
                                            <div
                                              style={styles.linkedScheduleMain}
                                            >
                                              <span
                                                style={
                                                  styles.linkedTypePill
                                                }
                                              >
                                                {source.typeLabel}
                                              </span>

                                              <div>
                                                <div
                                                  style={
                                                    styles.linkedScheduleTitle
                                                  }
                                                >
                                                  {source.title}
                                                </div>

                                                {source.meta && (
                                                  <div
                                                    style={
                                                      styles.linkedScheduleMeta
                                                    }
                                                  >
                                                    {source.meta}
                                                  </div>
                                                )}


                                                {source.anchorDate && (
                                                  <div style={styles.linkedScheduleDates}>
                                                    <span>
                                                      Anchor {formatScheduleDate(source.anchorDate)}
                                                    </span>
                                                    {typeSettings[typeId]?.schedule_open_offset_days !== null &&
                                                      typeSettings[typeId]?.schedule_open_offset_days !== undefined && (
                                                      <span>
                                                        Opens {formatScheduleDate(
                                                          addDaysToScheduleDate(
                                                            source.anchorDate,
                                                            typeSettings[typeId].schedule_open_offset_days
                                                          )
                                                        )}
                                                      </span>
                                                    )}
                                                    {typeSettings[typeId]?.schedule_due_offset_days !== null &&
                                                      typeSettings[typeId]?.schedule_due_offset_days !== undefined && (
                                                      <span>
                                                        Due {formatScheduleDate(
                                                          addDaysToScheduleDate(
                                                            source.anchorDate,
                                                            typeSettings[typeId].schedule_due_offset_days
                                                          )
                                                        )}
                                                      </span>
                                                    )}
                                                  </div>
                                                )}

                                                {choiceEnabled && !duplicateMode && (() => {
                                                  const itemChoices = getScheduleItemChoices(item);

                                                  return (
                                                    <div style={styles.linkedChoiceMeta}>
                                                      <label style={styles.capacityLabel}>
                                                        Capacity
                                                        <input
                                                          type="number"
                                                          min="1"
                                                          step="1"
                                                          style={styles.capacityInput}
                                                          defaultValue={
                                                            item.capacity ?? ''
                                                          }
                                                          placeholder="Unlimited"
                                                          disabled={
                                                            isScheduleSaving ||
                                                            isChoiceSaving
                                                          }
                                                          onBlur={event =>
                                                            handleSaveScheduleItemCapacity(
                                                              item,
                                                              event.target.value
                                                            )
                                                          }
                                                        />
                                                      </label>

                                                      <span>
                                                        {itemChoices.length}
                                                        {item.capacity
                                                          ? ` / ${item.capacity}`
                                                          : ''}{' '}
                                                        selected
                                                      </span>

                                                      {itemChoices.length > 0 && (
                                                        <span style={styles.choiceOccupants}>
                                                          {itemChoices
                                                            .map(choice =>
                                                              getChoiceOccupantLabel(
                                                                choice
                                                              )
                                                            )
                                                            .join(', ')}
                                                        </span>
                                                      )}
                                                    </div>
                                                  );
                                                })()}

                                                {duplicateChoiceEnabled && duplicateMode && (() => {
                                                  const itemChoices = getScheduleItemChoices(item);

                                                  return (
                                                    <div style={styles.linkedChoiceMeta}>
                                                      <span>
                                                        Capacity: {item.capacity || 'Unlimited'}
                                                      </span>
                                                      <span>
                                                        {itemChoices.length}
                                                        {item.capacity
                                                          ? ` / ${item.capacity}`
                                                          : ''}{' '}
                                                        selected
                                                      </span>
                                                    </div>
                                                  );
                                                })()}
                                              </div>
                                            </div>

                                            {!duplicateMode && (
                                            <div
                                              style={
                                                styles.linkedScheduleActions
                                              }
                                            >
                                              <button
                                                type="button"
                                                style={
                                                  styles.scheduleOrderBtn
                                                }
                                                disabled={
                                                  isScheduleSaving ||
                                                  index === 0
                                                }
                                                onClick={() =>
                                                  handleMoveScheduleItem(
                                                    assignment.id,
                                                    item.id,
                                                    -1
                                                  )
                                                }
                                                title="Move up"
                                              >
                                                ↑
                                              </button>

                                              <button
                                                type="button"
                                                style={
                                                  styles.scheduleOrderBtn
                                                }
                                                disabled={
                                                  isScheduleSaving ||
                                                  index ===
                                                    linkedItems.length -
                                                      1
                                                }
                                                onClick={() =>
                                                  handleMoveScheduleItem(
                                                    assignment.id,
                                                    item.id,
                                                    1
                                                  )
                                                }
                                                title="Move down"
                                              >
                                                ↓
                                              </button>

                                              <button
                                                type="button"
                                                style={styles.dangerBtn}
                                                disabled={
                                                  isScheduleSaving
                                                }
                                                onClick={() =>
                                                  handleRemoveScheduleItem(
                                                    item
                                                  )
                                                }
                                              >
                                                Remove
                                              </button>
                                            </div>
                                            )}
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                )}

                                {duplicateChoiceEnabled && duplicateMode && (
                                  <div style={styles.duplicateChoiceNote}>
                                    Choice status is live-synced from {assignmentInstanceLabel(duplicateSourceAssignment)}. Make any individual/group choice changes on the source assignment.
                                  </div>
                                )}

                                {choiceEnabled && !duplicateMode && (
                                  <div style={styles.choiceManager}>
                                    <div style={styles.choiceManagerHeader}>
                                      <div>
                                        <div style={styles.choiceManagerTitle}>
                                          Choice status
                                        </div>
                                        <div style={styles.choiceManagerHint}>
                                          {manualChoiceAssignment.groups_enabled && Number(manualChoiceAssignment.group_count) > 0
                                            ? 'Choices are made once per assignment group for the selected assignment instance.'
                                            : 'Choices are made individually for the selected assignment instance.'}
                                        </div>
                                      </div>
                                      <div style={styles.choiceStatusSummary}>
                                        {needsChoices.length === 0
                                          ? 'All complete'
                                          : `${needsChoices.length} need choice${needsChoices.length === 1 ? '' : 's'}`}
                                      </div>
                                    </div>

                                    {sharedScheduleEnabled && (
                                      <div style={styles.manualChoiceRow}>
                                        <select
                                          style={styles.scheduleSelect}
                                          value={manualChoiceAssignment.id}
                                          disabled={isChoiceSaving}
                                          onChange={event =>
                                            setChoiceTargetAssignmentByType(prev => ({
                                              ...prev,
                                              [typeId]: event.target.value
                                            }))
                                          }
                                        >
                                          {assignments.map(candidate => (
                                            <option
                                              key={candidate.id}
                                              value={candidate.id}
                                            >
                                              {assignmentInstanceLabel(candidate)}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    )}

                                    {choiceUnits.length > 0 && (
                                      <div style={styles.needsChoiceList}>
                                        {choiceUnits.map(unit => (
                                          <span
                                            key={unit.key}
                                            style={styles.needsChoicePill}
                                            title={unit.detail || ''}
                                          >
                                            {unit.label}{' '}
                                            {getChoiceCountForUnit(
                                        manualChoiceAssignment,
                                        unit
                                      )}/{requiredChoiceCount}
                                          </span>
                                        ))}
                                      </div>
                                    )}

                                    <div style={styles.manualChoiceRow}>
                                      <select
                                        style={styles.scheduleSelect}
                                        value={selectedUnitKey}
                                        disabled={isChoiceSaving}
                                        onChange={event =>
                                          setChoiceUnitByAssignment(prev => ({
                                            ...prev,
                                            [choiceUnitStateKey]:
                                              event.target.value
                                          }))
                                        }
                                      >
                                        <option value="">
                                          Select {assignment.groups_enabled && Number(assignment.group_count) > 0
                                            ? 'a group'
                                            : 'a student'}...
                                        </option>
                                        {choiceUnits.map(unit => (
                                          <option
                                            key={unit.key}
                                            value={unit.key}
                                          >
                                            {unit.label}
                                            {unit.detail
                                              ? ` — ${unit.detail}`
                                              : ''}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    {selectedChoiceUnit && (
                                      <div
                                        style={{
                                          display: 'grid',
                                          gridTemplateColumns:
                                            'minmax(220px, 1fr) auto',
                                          gap: '8px',
                                          alignItems: 'end'
                                        }}
                                      >
                                        <label style={styles.instanceField}>
                                          <span style={styles.compactLabel}>
                                            Reading/event
                                          </span>
                                          <select
                                            style={styles.scheduleSelect}
                                            value={
                                              choiceItemByAssignment[
                                                choiceUnitStateKey
                                              ] || ''
                                            }
                                            disabled={isChoiceSaving}
                                            onChange={event =>
                                              setChoiceItemByAssignment(prev => ({
                                                ...prev,
                                                [choiceUnitStateKey]:
                                                  event.target.value
                                              }))
                                            }
                                          >
                                            <option value="">
                                              Select a reading or event...
                                            </option>
                                            {manualLinkedItems.map(item => {
                                              const source =
                                                getScheduleSourceInfo(item);
                                              const existingChoice =
                                                assignmentScheduleSelections.find(
                                                  choice =>
                                                    choice.course_assignment_id ===
                                                      manualChoiceAssignment.id &&
                                                    choice.course_assignment_schedule_item_id ===
                                                      item.id &&
                                                    choiceMatchesUnit(
                                                      choice,
                                                      selectedChoiceUnit
                                                    )
                                                );
                                              const occupancy =
                                                getScheduleItemChoices(item).length;
                                              const capacity =
                                                item.capacity === null ||
                                                item.capacity === undefined
                                                  ? null
                                                  : Number(item.capacity);
                                              const full =
                                                !existingChoice &&
                                                capacity !== null &&
                                                occupancy >= capacity;
                                              const capacityLabel =
                                                capacity === null
                                                  ? 'Unlimited'
                                                  : `${occupancy}/${capacity}`;

                                              return (
                                                <option
                                                  key={item.id}
                                                  value={item.id}
                                                  disabled={full}
                                                >
                                                  {source.title} — {full
                                                    ? 'Full'
                                                    : capacityLabel}
                                                </option>
                                              );
                                            })}
                                          </select>
                                        </label>

                                        <button
                                          type="button"
                                          style={styles.btn}
                                          disabled={
                                            isChoiceSaving ||
                                            !choiceItemByAssignment[
                                              choiceUnitStateKey
                                            ]
                                          }
                                          onClick={() => {
                                            const item =
                                              manualLinkedItems.find(
                                                candidate =>
                                                  String(candidate.id) ===
                                                  String(
                                                    choiceItemByAssignment[
                                                      choiceUnitStateKey
                                                    ] || ''
                                                  )
                                              );

                                            if (item) {
                                              handleAssignManualScheduleChoice(
                                                manualChoiceAssignment,
                                                selectedChoiceUnit,
                                                item
                                              );
                                            }
                                          }}
                                        >
                                          {isChoiceSaving
                                            ? 'Assigning...'
                                            : 'Assign'}
                                        </button>
                                      </div>
                                    )}

                                    {selectedChoiceUnit && (() => {
                                      const currentChoices =
                                        getChoicesForAssignment(
                                          manualChoiceAssignment.id
                                        ).filter(choice =>
                                          choiceMatchesUnit(
                                            choice,
                                            selectedChoiceUnit
                                          )
                                        );

                                      if (currentChoices.length === 0) {
                                        return (
                                          <div style={styles.scheduleChoiceHint}>
                                            No reading/event assigned yet for this instance.
                                          </div>
                                        );
                                      }

                                      return (
                                        <div style={styles.linkedChoiceMeta}>
                                          <span>
                                            Assigned:{' '}
                                            <strong>
                                              {currentChoices
                                                .map(choice => {
                                                  const item =
                                                    manualLinkedItems.find(
                                                      candidate =>
                                                        candidate.id ===
                                                        choice.course_assignment_schedule_item_id
                                                    );
                                                  return item
                                                    ? getScheduleSourceInfo(item).title
                                                    : 'Saved option';
                                                })
                                                .join(', ')}
                                            </strong>
                                          </span>
                                          <button
                                            type="button"
                                            style={styles.dangerBtn}
                                            disabled={isChoiceSaving}
                                            onClick={() =>
                                              handleRemoveManualScheduleChoice(
                                                manualChoiceAssignment,
                                                selectedChoiceUnit
                                              )
                                            }
                                          >
                                            Remove
                                          </button>
                                        </div>
                                      );
                                    })()}

                                    {false && selectedChoiceUnit && (
                                      <div style={styles.manualChoiceButtons}>
                                        {manualLinkedItems.map(item => {
                                          const source =
                                            getScheduleSourceInfo(item);
                                          const existingChoice =
                                            assignmentScheduleSelections.find(
                                              choice =>
                                                choice.course_assignment_schedule_item_id === item.id &&
                                                choiceMatchesUnit(
                                                  choice,
                                                  selectedChoiceUnit
                                                )
                                            );
                                          const occupancy = getScheduleItemChoices(item).length;
                                          const capacity =
                                            item.capacity === null ||
                                            item.capacity === undefined
                                              ? null
                                              : Number(item.capacity);
                                          const unitCount =
                                            getChoiceCountForUnit(
                                              manualChoiceAssignment,
                                              selectedChoiceUnit
                                            );
                                          const atCapacity =
                                            !existingChoice &&
                                            capacity !== null &&
                                            occupancy >= capacity;
                                          const unitComplete =
                                            !existingChoice &&
                                            unitCount >= requiredChoiceCount;

                                          return (
                                            <button
                                              key={item.id}
                                              type="button"
                                              style={{
                                                ...styles.choiceOptionBtn,
                                                ...(existingChoice
                                                  ? styles.choiceOptionBtnSelected
                                                  : {})
                                              }}
                                              disabled={
                                                isChoiceSaving ||
                                                atCapacity ||
                                                unitComplete
                                              }
                                              title={
                                                atCapacity
                                                  ? 'At capacity'
                                                  : unitComplete
                                                    ? 'Required choices already complete'
                                                    : ''
                                              }
                                              onClick={() =>
                                                handleToggleManualScheduleChoice(
                                                  manualChoiceAssignment,
                                                  selectedChoiceUnit,
                                                  item
                                                )
                                              }
                                            >
                                              <span>
                                                {existingChoice ? '✓ ' : ''}
                                                {source.title}
                                              </span>
                                              <span style={styles.choiceOptionCount}>
                                                {occupancy}
                                                {capacity
                                                  ? `/${capacity}`
                                                  : ''}
                                              </span>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {activeTab === 'groups' && allowGroups && (
                        <div style={styles.groupInstances}>
                          {assignments.map(assignment => {
                            const label =
                              getAssignmentLabel(
                                assignment
                              );
                            const duplicateLink =
                              getAssignmentScheduleDuplicate(assignment.id);
                            const duplicateSourceAssignment = duplicateLink
                              ? courseAssignments.find(
                                  candidate =>
                                    String(candidate.id) ===
                                    String(duplicateLink.source_course_assignment_id)
                                ) || null
                              : null;
                            const groupsInherited = !!duplicateLink;
                            const groupsSharedAcrossInstances =
                              duplicateLink?.sync_kind === 'shared_instance';

                            return (
                              <div
                                key={assignment.id}
                                style={styles.groupInstanceCard}
                              >
                                <div style={styles.groupInstanceHeader}>
                                  <div style={styles.groupInstanceTitle}>
                                    {label}
                                  </div>

                                  {assignments.length > 1 &&
                                    assignment.groups_enabled &&
                                    !groupsInherited && (
                                      <button
                                        type="button"
                                        style={styles.applyGroupsBtn}
                                        disabled={
                                          applyingGroupSetupId ===
                                          assignment.id
                                        }
                                        onClick={() =>
                                          applyGroupSetupToAllInstances(
                                            assignment
                                          )
                                        }
                                      >
                                        {applyingGroupSetupId ===
                                        assignment.id
                                          ? 'Applying...'
                                          : 'Apply Group Setup to All Instances'}
                                      </button>
                                    )}
                                </div>

                                {groupsInherited && (
                                  <div style={styles.fieldHint}>
                                    {groupsSharedAcrossInstances ? (
                                      <>
                                        Groups are shared across assignment instances and follow{' '}
                                        <strong>
                                          {assignmentInstanceLabel(
                                            duplicateSourceAssignment
                                          )}
                                        </strong>. Change group membership on the shared source assignment.
                                      </>
                                    ) : (
                                      <>
                                        Groups are inherited from{' '}
                                        <strong>
                                          {assignmentInstanceLabel(
                                            duplicateSourceAssignment
                                          )}
                                        </strong>{' '}
                                        while Duplicate Schedule is active. Change
                                        group membership on the source assignment.
                                      </>
                                    )}
                                  </div>
                                )}

                                <label style={styles.groupToggleRow}>
                                  <input
                                    type="checkbox"
                                    checked={
                                      !!assignment.groups_enabled
                                    }
                                    disabled={groupsInherited}
                                    onChange={event =>
                                      handleToggleGroups(
                                        assignment,
                                        event.target.checked
                                      )
                                    }
                                  />
                                  <span>
                                    Use groups for this assignment
                                  </span>
                                </label>

                                {assignment.groups_enabled && (
                                  <div style={styles.groupSettingsRow}>
                                    <div
                                      style={{
                                        ...styles.field,
                                        maxWidth: '180px'
                                      }}
                                    >
                                      <label style={styles.label}>
                                        Number of groups
                                      </label>

                                      <select
                                        style={styles.input}
                                        value={
                                          assignment.group_count || 2
                                        }
                                        disabled={groupsInherited}
                                        onChange={event =>
                                          handleChangeGroupCount(
                                            assignment,
                                            Number(
                                              event.target.value
                                            )
                                          )
                                        }
                                      >
                                        {Array.from(
                                          { length: 20 },
                                          (_, index) => index + 1
                                        ).map(number => (
                                          <option
                                            key={number}
                                            value={number}
                                          >
                                            {number}
                                          </option>
                                        ))}
                                      </select>
                                    </div>

                                    <div style={styles.manageGroupsBlock}>
                                      <button
                                        type="button"
                                        style={styles.btn}
                                        disabled={groupsInherited}
                                        onClick={() =>
                                          openGroupManager(
                                            assignment.id
                                          )
                                        }
                                      >
                                        {groupsInherited
                                          ? 'Groups Inherited'
                                          : 'Manage Groups'}
                                      </button>

                                      <div style={styles.fieldHint}>
                                        {groupsInherited
                                          ? `Group membership is live-synced from ${assignmentInstanceLabel(duplicateSourceAssignment)}.`
                                          : 'Students can belong to only one group for this assignment.'}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {!assignment.groups_enabled && (
                                  <div style={styles.fieldHint}>
                                    Grouping is available for this
                                    assignment, but is currently off.
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          }
        )}

        <form
          onSubmit={handleAddAssignment}
          style={styles.addForm}
        >
          <h4 style={styles.addHeading}>
            Add Assignment
          </h4>

          <div style={styles.addRow}>
            <div style={styles.field}>
              <label style={styles.label}>
                Assignment type
              </label>

              <select
                style={styles.input}
                value={addingTypeId}
                onChange={event =>
                  setAddingTypeId(event.target.value)
                }
              >
                <option value="">
                  Select a type
                </option>

                {nonCoreTypes.map(type => (
                  <option
                    key={type.id}
                    value={type.id}
                  >
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{
                ...styles.field,
                maxWidth: '120px'
              }}
            >
              <label style={styles.label}>
                Initial instances
              </label>

              <input
                style={styles.input}
                type="number"
                min="1"
                max="10"
                value={addingCount}
                onChange={event =>
                  setAddingCount(
                    Number(event.target.value)
                  )
                }
              />
            </div>

            <div style={styles.fieldBtn}>
              <label style={styles.label}>
                &nbsp;
              </label>

              <button
                style={styles.btn}
                disabled={saving}
              >
                {saving ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </form>
      </div>

      <GroupManagerModal
        assignment={modalAssignment}
        label={modalAssignmentLabel}
        enrolments={enrolments}
        memberships={groupMemberships}
        loading={groupLoading}
        saving={groupSaving}
        selectedIds={selectedGroupStudents}
        setSelectedIds={setSelectedGroupStudents}
        onAdd={addSelectedStudentsToGroup}
        onRemove={removeStudentFromGroup}
        copySources={modalCopySources}
        onCopySource={copySourceIntoAssignmentGroup}
        groupNameFor={groupNumber =>
          assignmentGroupName(
            modalAssignment.id,
            groupNumber
          )
        }
        groupDescriptionFor={groupNumber =>
          assignmentGroupDescription(
            modalAssignment.id,
            groupNumber
          )
        }
        onRenameGroup={groupNumber =>
          handleRenameAssignmentGroup(
            modalAssignment.id,
            groupNumber
          )
        }
                onEditDescription={groupNumber =>
          handleEditAssignmentGroupDescription(
            modalAssignment.id,
            groupNumber
          )
        }
        onClose={() => {
          setGroupModalAssignmentId(null);
          setGroupMemberships([]);
          setSelectedGroupStudents([]);
        }}
      />
    </div>
  );
}

const styles = {
  subheading: {
    margin: '0 0 20px',
    fontSize: '18px',
    color: '#1f2933'
  },

  muted: {
    color: '#66788a',
    fontSize: '14px',
    margin: '8px 0'
  },

  error: {
    background: '#faeaea',
    border: '1px solid #e8bcbc',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#a12a2a',
    fontSize: '14px',
    marginBottom: '16px'
  },

  success: {
    background: '#e7f5ed',
    border: '1px solid #b9dfc9',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#216e45',
    fontSize: '14px',
    marginBottom: '16px'
  },

  section: {
    marginBottom: '32px',
    paddingBottom: '32px',
    borderBottom: '1px solid #d7dee5'
  },

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '14px'
  },

  sectionHeading: {
    margin: '0 0 12px',
    fontSize: '16px',
    color: '#1f2933'
  },

  countPill: {
    background: '#e7f1f7',
    color: '#215b7d',
    borderRadius: '999px',
    padding: '3px 10px',
    fontSize: '12px',
    fontWeight: '700'
  },

  weekGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },

  weekBtn: {
    border: '1px solid',
    borderRadius: '8px',
    padding: '8px 10px',
    cursor: 'pointer',
    textAlign: 'center',
    minWidth: '54px'
  },

  weekBtnOn: {
    background: '#215b7d',
    borderColor: '#215b7d',
    color: 'white'
  },

  weekBtnOff: {
    background: 'white',
    borderColor: '#d7dee5',
    color: '#66788a'
  },

  weekNum: {
    fontSize: '12px',
    fontWeight: '700'
  },

  weekDate: {
    fontSize: '11px',
    marginTop: '2px'
  },

  aiDisclosureHeaderToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    border: '1px solid #c7d3dc',
    borderRadius: '999px',
    padding: '4px 8px',
    background: '#f8fafb',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#1f2933',
    flexShrink: 0
  },

  aiDisclosureHeaderLabel: {
    fontWeight: '700'
  },

  aiDisclosureHeaderState: {
    minWidth: '20px',
    fontWeight: '700',
    color: '#66788a'
  },

  aiDisclosureBox: {
    marginTop: '14px',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '12px 14px',
    background: '#f8fafb'
  },

  aiDisclosureToggleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#1f2933'
  },

  aiDisclosureHint: {
    display: 'block',
    marginTop: '3px',
    color: '#66788a',
    fontSize: '12px',
    lineHeight: '1.45',
    fontWeight: '400'
  },

  sequencePanel: {
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '14px',
    background: '#f8fafb',
    marginBottom: '16px'
  },

  sequencePanelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '10px'
  },

  sequencePanelTitle: {
    fontSize: '13px',
    fontWeight: '800',
    color: '#1f2933',
    marginBottom: '3px'
  },

  sequenceCreateRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(180px, 1fr) auto',
    gap: '8px',
    alignItems: 'center'
  },

  sequenceList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginTop: '10px'
  },

  sequenceItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: '1px solid #e2e8ed',
    borderRadius: '7px',
    padding: '7px 9px',
    background: 'white'
  },

  sequenceName: {
    fontSize: '12px',
    fontWeight: '750',
    color: '#1f2933',
    flex: 1
  },

  sequenceCount: {
    fontSize: '11px',
    color: '#66788a'
  },

  sequenceDeleteBtn: {
    border: 'none',
    background: 'transparent',
    color: '#a12a2a',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer',
    padding: '2px 4px'
  },

  sequenceHeaderField: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    marginLeft: '8px',
    fontSize: '13px',
    cursor: 'default'
  },

  sequenceHeaderLabel: {
    color: '#52606d',
    fontSize: '12px',
    fontWeight: '700'
  },

  sequenceSelect: {
    minWidth: '150px',
    border: '1px solid #d7dee5',
    borderRadius: '6px',
    padding: '5px 7px',
    background: 'white',
    color: '#33434f',
    fontSize: '11px',
    fontWeight: '500'
  },

  typeGroup: {
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '14px',
    marginBottom: '16px',
    background: 'white'
  },

  typeGroupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },

  typeGroupName: {
    fontWeight: '700',
    fontSize: '16px',
    color: '#1f2933',
    marginRight: '2px'
  },

  pillBlue: {
    background: '#e7f1f7',
    color: '#215b7d',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '700'
  },

  pillGrey: {
    background: '#f0f4f7',
    color: '#66788a',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '700'
  },

  pillPurple: {
    background: '#eee8f8',
    color: '#5a4383',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '700'
  },

  typeGroupHeaderClickable: {
    cursor: 'pointer',
    marginBottom: 0
  },

  typeGroupTitleBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    minWidth: 0,
    flex: 1
  },

  orderControls: {
    display: 'inline-flex',
    gap: '4px',
    marginLeft: 'auto',
    flexShrink: 0
  },

  orderBtn: {
    width: '30px',
    height: '30px',
    border: '1px solid #c7d3dc',
    borderRadius: '6px',
    background: '#f8fafb',
    color: '#33434f',
    fontSize: '16px',
    fontWeight: '800',
    lineHeight: 1,
    cursor: 'pointer'
  },

  typeExpanded: {
    borderTop: '1px solid #e5eaee',
    marginTop: '12px',
    background: 'white'
  },

  instanceTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    border: '1px solid #e5eaee',
    borderRadius: '8px',
    overflow: 'hidden'
  },

  instanceRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(120px, 0.8fr) repeat(3, minmax(140px, 1fr)) auto',
    gap: '12px',
    alignItems: 'end',
    padding: '11px 12px',
    borderBottom: '1px solid #e8edf1',
    background: 'white'
  },

  instanceName: {
    fontSize: '13px',
    fontWeight: '800',
    color: '#1f2933',
    alignSelf: 'center'
  },

  instanceField: {
    minWidth: 0
  },

  compactLabel: {
    display: 'block',
    fontSize: '10px',
    fontWeight: '700',
    color: '#7a8894',
    marginBottom: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.03em'
  },

  instanceRemove: {
    alignSelf: 'end',
    paddingBottom: '1px'
  },

  groupInstances: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },

  groupInstanceCard: {
    border: '1px solid #e5eaee',
    borderRadius: '8px',
    padding: '12px',
    background: '#f9fbfc'
  },

  groupInstanceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '9px',
    flexWrap: 'wrap'
  },

  groupInstanceTitle: {
    fontSize: '13px',
    fontWeight: '800',
    color: '#1f2933'
  },

  applyGroupsBtn: {
    background: 'transparent',
    color: '#5a4383',
    border: '1px solid #cbbde3',
    borderRadius: '6px',
    padding: '5px 9px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer'
  },

  assignmentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '7px'
  },

  assignmentCard: {
    border: '1px solid #e5eaee',
    borderRadius: '8px',
    background: '#f9fbfc',
    overflow: 'hidden'
  },

  assignmentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 12px',
    cursor: 'pointer',
    gap: '12px'
  },

  assignmentTitleBlock: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    gap: '7px'
  },

  chevron: {
    color: '#66788a',
    fontSize: '13px',
    width: '12px',
    flexShrink: 0
  },

  assignmentTitle: {
    fontWeight: '650',
    fontSize: '14px',
    color: '#1f2933'
  },

  groupActivePill: {
    background: '#eee8f8',
    color: '#5a4383',
    borderRadius: '999px',
    padding: '2px 7px',
    fontSize: '10px',
    fontWeight: '700',
    whiteSpace: 'nowrap'
  },

  assignmentExpanded: {
    borderTop: '1px solid #e5eaee',
    background: 'white'
  },

  innerTabs: {
    display: 'flex',
    gap: '2px',
    padding: '8px 10px 0',
    borderBottom: '1px solid #e5eaee',
    overflowX: 'auto'
  },

  innerTab: {
    borderTop: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    borderBottom: '2px solid transparent',
    background: 'transparent',
    color: '#66788a',
    padding: '8px 11px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },

  tabContent: {
    padding: '14px'
  },

  duplicateBtn: {
    border: '1px solid #9fb7c7',
    background: 'white',
    color: '#215b7d',
    borderRadius: '7px',
    padding: '6px 10px',
    fontSize: '12px',
    fontWeight: '800',
    cursor: 'pointer'
  },
  duplicateBtnActive: {
    background: '#e7f1f7',
    borderColor: '#7fa6bb'
  },
  duplicatePanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    marginBottom: '12px',
    padding: '10px 11px',
    border: '1px solid #cbdce6',
    borderRadius: '8px',
    background: '#f7fafc'
  },
  duplicateHint: {
    color: '#627d98',
    fontSize: '12px',
    lineHeight: 1.45
  },
  duplicateChoiceNote: {
    marginTop: '10px',
    padding: '9px 10px',
    borderRadius: '7px',
    background: '#f1f5f8',
    color: '#486581',
    fontSize: '12px',
    lineHeight: 1.45
  },

  scheduleInstances: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },

  scheduleInstanceCard: {
    border: '1px solid #d7dee5',
    borderRadius: '9px',
    padding: '12px',
    background: '#f9fbfc'
  },

  scheduleInstanceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    fontWeight: '800',
    fontSize: '13px',
    color: '#1f2933',
    marginBottom: '9px'
  },

  scheduleAddRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px'
  },

  scheduleSelect: {
    flex: 1,
    minWidth: 0,
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '13px',
    background: 'white',
    color: '#1f2933'
  },

  scheduleEmpty: {
    fontSize: '12px',
    color: '#7a8894',
    padding: '6px 2px 2px'
  },

  scheduleTimingPanel: {
    border: '1px solid #d7dee5', borderRadius: '8px', padding: '14px',
    background: '#f8fafb'
  },
  scheduleTimingHeader: {
    fontSize: '14px', fontWeight: '800', color: '#1f2933', marginBottom: '4px'
  },
  scheduleTimingHint: {
    fontSize: '12px', color: '#66788a', lineHeight: '1.45', marginBottom: '12px'
  },
  scheduleTimingRows: {
    display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px'
  },
  scheduleTimingRow: {
    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
    fontSize: '13px', color: '#1f2933'
  },
  scheduleDaysInput: {
    width: '72px', border: '1px solid #bcccdc', borderRadius: '6px',
    padding: '7px 8px', fontSize: '13px', background: 'white'
  },
  scheduleDirectionSelect: {
    border: '1px solid #bcccdc', borderRadius: '6px', padding: '7px 8px',
    fontSize: '13px', background: 'white'
  },
  linkedScheduleDates: {
    display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '4px',
    fontSize: '11px', color: '#52697a'
  },
  linkedScheduleList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },

  linkedScheduleItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 10px',
    border: '1px solid #e1e7eb',
    borderRadius: '7px',
    background: 'white'
  },

  linkedScheduleMain: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    minWidth: 0
  },

  linkedTypePill: {
    flexShrink: 0,
    borderRadius: '999px',
    background: '#eef2f5',
    color: '#52616d',
    padding: '2px 7px',
    fontSize: '10px',
    fontWeight: '800'
  },

  linkedScheduleTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#1f2933'
  },

  linkedScheduleMeta: {
    marginTop: '2px',
    fontSize: '11px',
    color: '#7a8894'
  },

  linkedScheduleActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    flexShrink: 0
  },

  scheduleOrderBtn: {
    border: '1px solid #d7dee5',
    borderRadius: '6px',
    background: 'white',
    color: '#52616d',
    width: '28px',
    height: '28px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '800'
  },

  scheduleChoicePanel: {
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '14px',
    background: 'white'
  },
  scheduleChoiceToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    fontWeight: '800',
    color: '#1f2933',
    cursor: 'pointer'
  },
  scheduleChoiceHint: {
    marginTop: '6px',
    fontSize: '12px',
    color: '#66788a',
    lineHeight: '1.45'
  },
  scheduleChoiceSettingsRow: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(150px, 1fr) minmax(150px, 1fr) minmax(130px, 0.7fr) auto',
    gap: '10px',
    alignItems: 'end',
    marginTop: '12px'
  },
  scheduleChoiceSave: {
    paddingBottom: '8px'
  },
  linkedChoiceMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    marginTop: '6px',
    fontSize: '11px',
    color: '#52697a'
  },
  capacityLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontWeight: '700'
  },
  capacityInput: {
    width: '86px',
    border: '1px solid #bcccdc',
    borderRadius: '6px',
    padding: '4px 6px',
    fontSize: '11px',
    background: 'white'
  },
  choiceOccupants: {
    color: '#66788a'
  },
  choiceManager: {
    marginTop: '12px',
    borderTop: '1px solid #e5eaee',
    paddingTop: '12px'
  },
  choiceManagerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    alignItems: 'flex-start'
  },
  choiceManagerTitle: {
    fontSize: '13px',
    fontWeight: '800',
    color: '#1f2933'
  },
  choiceManagerHint: {
    marginTop: '2px',
    fontSize: '11px',
    color: '#7a8894'
  },
  choiceStatusSummary: {
    fontSize: '11px',
    fontWeight: '800',
    color: '#52616d'
  },
  needsChoiceList: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginTop: '9px'
  },
  needsChoicePill: {
    borderRadius: '999px',
    background: '#fff4d7',
    color: '#8a5b00',
    padding: '3px 8px',
    fontSize: '11px',
    fontWeight: '700'
  },
  manualChoiceRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '10px'
  },
  manualChoiceButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '7px',
    marginTop: '8px'
  },
  choiceOptionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: '1px solid #cbd5dc',
    borderRadius: '7px',
    background: 'white',
    color: '#334e68',
    padding: '7px 9px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  choiceOptionBtnSelected: {
    border: '1px solid #2f6f9f',
    background: '#eaf3f8',
    color: '#215b7d'
  },
  choiceOptionCount: {
    fontSize: '10px',
    color: '#7a8894',
    fontWeight: '800'
  },

  descriptionWrap: {
    marginBottom: '2px'
  },

  dateRow: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap'
  },

  field: {
    flex: 1,
    minWidth: '140px',
    marginBottom: '8px'
  },

  fieldBtn: {
    flexShrink: 0
  },

  label: {
    display: 'block',
    fontWeight: '700',
    fontSize: '13px',
    marginBottom: '6px',
    color: '#1f2933'
  },

  fieldHint: {
    fontSize: '11px',
    color: '#7a8894',
    lineHeight: '1.45',
    marginBottom: '9px'
  },

  input: {
    width: '100%',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '9px 11px',
    fontSize: '14px',
    boxSizing: 'border-box',
    background: 'white'
  },

  groupToggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '650',
    color: '#1f2933',
    marginBottom: '12px',
    cursor: 'pointer'
  },

  groupSettingsRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '18px',
    flexWrap: 'wrap',
    paddingTop: '4px'
  },

  manageGroupsBlock: {
    paddingBottom: '8px'
  },

  addForm: {
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '16px',
    background: '#f7f9fa',
    marginTop: '16px'
  },

  addHeading: {
    margin: '0 0 14px',
    fontSize: '14px',
    color: '#1f2933',
    fontWeight: '700'
  },

  addRow: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'flex-end'
  },

  btn: {
    background: '#215b7d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },

  secondaryBtn: {
    marginTop: '8px',
    background: 'transparent',
    color: '#215b7d',
    border: '1px solid #b0cfe0',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '13px',
    cursor: 'pointer'
  },

  smallBtn: {
    background: '#215b7d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '6px 9px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },

  disabledBtn: {
    opacity: 0.45,
    cursor: 'default'
  },

  dangerBtn: {
    background: 'transparent',
    color: '#a12a2a',
    border: '1px solid #e2b8b8',
    borderRadius: '6px',
    padding: '5px 10px',
    fontSize: '13px',
    cursor: 'pointer',
    flexShrink: 0
  },

  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(22, 32, 40, 0.48)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '24px'
  },

  modal: {
    width: 'min(1180px, 96vw)',
    maxHeight: '88vh',
    background: 'white',
    borderRadius: '12px',
    boxShadow: '0 18px 55px rgba(0,0,0,0.26)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },

  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '18px',
    padding: '18px 20px',
    borderBottom: '1px solid #d7dee5'
  },

  modalTitle: {
    margin: 0,
    color: '#1f2933',
    fontSize: '18px'
  },

  modalHint: {
    color: '#66788a',
    fontSize: '12px',
    marginTop: '5px'
  },

  closeBtn: {
    border: 'none',
    background: 'transparent',
    color: '#66788a',
    fontSize: '25px',
    lineHeight: 1,
    cursor: 'pointer',
    padding: '0 3px'
  },

  modalLoading: {
    padding: '28px',
    color: '#66788a'
  },

  groupManagerBody: {
    display: 'grid',
    gridTemplateColumns: '270px minmax(0, 1fr)',
    gap: '16px',
    padding: '16px',
    minHeight: 0,
    overflow: 'hidden'
  },

  unassignedPanel: {
    border: '1px solid #d7dee5',
    borderRadius: '9px',
    padding: '12px',
    minHeight: 0,
    overflowY: 'auto'
  },

  groupPanelTitle: {
    fontWeight: '800',
    fontSize: '13px',
    color: '#1f2933',
    marginBottom: '9px'
  },

  studentPicker: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },

  studentPickerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '6px 4px',
    fontSize: '12px',
    color: '#33434f',
    cursor: 'pointer',
    borderBottom: '1px solid #f0f4f7'
  },

  groupsScroll: {
    minWidth: 0,
    overflowX: 'auto',
    overflowY: 'auto'
  },

  groupsGrid: {
    display: 'grid',
    gap: '10px',
    alignItems: 'start',
    minWidth: 'max-content'
  },

  groupColumn: {
    border: '1px solid #d7dee5',
    borderRadius: '9px',
    background: '#f9fbfc',
    minHeight: '220px',
    overflow: 'hidden'
  },

  groupColumnHeader: {
    padding: '10px',
    borderBottom: '1px solid #d7dee5',
    background: '#f0f4f7'
  },

  groupColumnActions: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap'
  },

  copyGroupBtn: {
    background: 'white',
    color: '#5a4383',
    border: '1px solid #cbbde3',
    borderRadius: '6px',
    padding: '6px 9px',
    fontSize: '10px',
    fontWeight: '700',
    cursor: 'pointer'
  },

  copySourcePanel: {
    padding: '10px',
    borderBottom: '1px solid #d7dee5',
    background: '#fff'
  },

  copySourceHeading: {
    fontSize: '11px',
    fontWeight: '800',
    color: '#1f2933',
    marginBottom: '8px'
  },

  copySourceCategory: {
    marginBottom: '10px'
  },

  copySourceCategoryTitle: {
    fontSize: '10px',
    fontWeight: '800',
    color: '#66788a',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '5px'
  },

  copySourceButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },

  copySourceBtn: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    border: '1px solid #d7dee5',
    borderRadius: '6px',
    background: '#f9fbfc',
    color: '#33434f',
    padding: '6px 8px',
    fontSize: '10px',
    cursor: 'pointer',
    textAlign: 'left'
  },

  copySourceCount: {
    minWidth: '22px',
    textAlign: 'center',
    borderRadius: '999px',
    background: '#eee8f8',
    color: '#5a4383',
    fontWeight: '800',
    padding: '1px 6px'
  },

  copySourceCancel: {
    border: 'none',
    background: 'transparent',
    color: '#66788a',
    fontSize: '10px',
    cursor: 'pointer',
    padding: '2px 0'
  },

  groupColumnTitle: {
    fontWeight: '800',
    fontSize: '13px',
    color: '#1f2933',
    marginBottom: '7px'
  },

  groupMemberList: {
    padding: '7px'
  },

  groupMember: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '7px 4px',
    borderBottom: '1px solid #e8edf1',
    fontSize: '12px',
    color: '#33434f'
  },

  removeMemberBtn: {
    border: 'none',
    background: 'transparent',
    color: '#a12a2a',
    fontSize: '10px',
    fontWeight: '700',
    cursor: 'pointer',
    padding: 0,
    flexShrink: 0
  },

  emptyGroupText: {
    color: '#8a98a5',
    fontSize: '12px',
    padding: '7px 3px'
  },

  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '12px 18px',
    borderTop: '1px solid #d7dee5',
    background: '#f9fbfc'
  },
  weightSummary: {
    margin: '8px 0 14px',
    padding: '10px 12px',
    background: '#f7f9fa',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#1f2933'
  },
  weightControl: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0
  },
  weightLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#66788a'
  },
  weightInput: {
    width: '72px',
    border: '1px solid #d7dee5',
    borderRadius: '6px',
    padding: '5px 7px',
    fontSize: '13px',
    background: 'white'
  },
  weightPercent: {
    fontSize: '13px',
    color: '#66788a'
  },
    weekControl: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '5px'
  },

  weekInstructorChecks: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    minHeight: '20px'
  },

  assignmentInstructorBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    marginLeft: '4px'
  },

  assignmentInstructorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },

  assignmentInstructorLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#66788a'
  },

  instructorChecks: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px'
  },

  instructorCheck: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    fontSize: '12px',
    fontWeight: '700',
    color: '#1f2933',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
};

export { GroupManagerModal };